import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Upload, Import, Sparkles, Download, FileJson, Play, Pause, SkipBack, SkipForward, Trash2, FolderPlus, FolderOpen, X, Grid3x3, List, Edit2, FileText, ChevronDown, ChevronUp, ChevronRight, HelpCircle, Palette, Calendar, MoreVertical, PanelLeft, PanelLeftClose, Plus, Columns2, LayoutList, Youtube, Loader2, Check, ExternalLink, Eye, Clock, Layers, Music, Image, Video, Type, Archive, AlertTriangle, Library, Copy, GripVertical, Coins, CheckCircle2 } from 'lucide-react';
import { useCredits, CREDIT_OPERATIONS, CreditOperation } from '@/hooks/useCredits';
import { useSubscription } from '@/hooks/useSubscription';
import { CreditConfirmDialog } from '@/components/credits/CreditConfirmDialog';
import { CreditPurchaseModal } from '@/components/credits/CreditPurchaseModal';
import { UpgradeModal } from '@/components/subscription';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useMovConversion } from '@/hooks/useMovConversion';
import { CastingVideoImporter } from './CastingVideoImporter';
import { LoginModal } from './LoginModal';
import { ProjectBin } from './video-editor/ProjectBin';
import { TimelineSequence, ProjectClip } from './video-editor/TimelineSequence';
import { AIAssetGenerator } from './video-editor/AIAssetGenerator';
import { ScriptDropZone } from './video-editor/ScriptDropZone';
import { ScriptAnalysisChat } from './video-editor/ScriptAnalysisChat';
import { PlanModeChat } from './video-editor/PlanModeChat';
import { SchedulePostModal } from './social/SchedulePostModal';
import { TemplatesTab, VideoTemplate, TemplateSettings } from './templates';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { resolveVideoUrl, isPresignedUrl, resolveOutputConfig } from '@/lib/utils';
import { useYouTubePublish } from '@/hooks/useYouTubePublish';
import { useBackgroundRemoval } from '@/hooks/useBackgroundRemoval';
import { useAudioEnhancement } from '@/hooks/useAudioEnhancement';
import { useBackgroundReplacement } from '@/hooks/useBackgroundReplacement';
import { useScriptAnalysis } from '@/hooks/useScriptAnalysis';
import { usePlanMode } from '@/hooks/usePlanMode';
import { RenderControlsPanel, DEFAULT_COLOR_ADJUSTMENTS, hasColorAdjustments, type ColorAdjustments } from './video-editor/RenderControlsPanel';
import { AIEditPreview } from './video-editor/AIEditPreview';
import { QuickPreviewPanel } from './video-editor/QuickPreviewPanel';
import { RenderHistory } from './video-editor/RenderHistory';
import { BackgroundReplacementWizard } from './video-editor/BackgroundReplacementWizard';
import { ProjectHeader } from './video-editor/ProjectHeader';
import type { EditPlan, PlanMessage, PlanQuestion, RenderedVideo as RenderedVideoType, ScriptAnalysis } from '@/types/video-enhancer';

interface ProjectFile {
  id: string;
  name: string;
  fileType?: 'video' | 'audio' | 'image' | 'srt';
  thumbnailUrl?: string;
  duration: number;
  timestamp?: string;
  projectName?: string;
  sessionName?: string;
  groupName?: string;
  takeName?: string;
  talent?: string[];
  s3Url?: string;
  s3Key?: string;
  imported?: boolean;
  liked?: boolean;
  comments?: any[];
  enhanced?: boolean;
  enhancementPrompt?: string;
  analysisData?: {
    transcript?: any;
    background?: string;
  };
  processingStatus?: 'starting' | 'processing' | 'succeeded' | 'failed';
  processingJobId?: string;
  analysisStatus?: 'starting' | 'processing' | 'completed' | 'failed';
  analysisJobId?: string;
  alphaMaskFor?: string; // ID of the original video this is an alpha mask for
  outputType?: 'green-screen' | 'alpha-mask' | 'foreground-mask'; // For Shotstack LLM workflow
  layerType?: 'main' | 'background' | 'audio'; // Track which layer this file belongs to
  linkedAudioId?: string; // ID of the linked audio file (for green screen videos)
  // Green screen processing (in-place updates)
  greenScreenUrl?: string; // URL of the green screen processed version
  greenScreenKey?: string; // S3 key of the green screen version
  hasGreenScreen?: boolean; // Whether this import has been processed with green screen removal
  greenScreenType?: 'green-screen' | 'foreground-mask' | 'alpha-mask'; // Type of green screen processing
  linkedVideoId?: string; // ID of the linked video file (for audio tracks from green screen)
  linkedPairId?: string; // ID to group linked video/audio pairs together
  // AI Generation fields
  isAIGenerated?: boolean;
  aiGenerationPrompt?: string;
  aiAssetType?: 'image' | 'video';
}

interface RenderedVideo {
  id: string;
  name: string;
  thumbnailUrl?: string;
  videoUrl?: string;
  downloadUrl?: string;
  url?: string; // Shotstack returns just 'url'
  timestamp: string;
  duration: number;
  status: 'pending' | 'processing' | 'rendering' | 'rendered' | 'failed';
  renderId?: string;
  renderStatus?: string;
  progress?: number;
  error?: string;
  errorDetails?: {
    error?: string;
    message?: string;
    details?: any;
  };
  shotstackJson?: any;
  configuration: {
    clips: ProjectClip[];
    masterPrompt: string;
  };
  // Saved library fields
  savedAt?: string;        // ISO timestamp when saved to library
  savedS3Url?: string;     // Permanent S3 URL after save
  savedS3Key?: string;     // S3 key for the saved file
}

export const VideoEnhancerNLE = () => {
  const { toast } = useToast();
  const { checkCredits, fetchBalance, isAuthenticated: isCreditUser } = useCredits();
  const { isSubscriber, fetchSubscription } = useSubscription();
  const { needsConversion, convert: convertMov } = useMovConversion();

  // Credit confirmation dialog state
  const [creditConfirmOpen, setCreditConfirmOpen] = useState(false);
  const [creditConfirmOperation, setCreditConfirmOperation] = useState<CreditOperation>(CREDIT_OPERATIONS.SHOTSTACK_RENDER);
  const [creditConfirmOperationName, setCreditConfirmOperationName] = useState('');
  const [creditConfirmCallback, setCreditConfirmCallback] = useState<(() => void) | null>(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalTrigger, setUpgradeModalTrigger] = useState<'ai_feature' | 'reader' | 'general'>('ai_feature');

  // Helper to check credits before AI operations (requires subscription + credits)
  const requireCreditConfirmation = async (
    operationType: CreditOperation,
    operationName: string,
    onConfirm: () => void
  ) => {
    // Skip credit check if not authenticated
    if (!isCreditUser()) {
      onConfirm();
      return;
    }

    const creditCheck = await checkCredits(operationType);

    // If free operation, proceed directly (no subscription required for free ops)
    if (creditCheck?.isFree) {
      onConfirm();
      return;
    }

    // For paid operations, check subscription first
    if (!isSubscriber) {
      setUpgradeModalTrigger('ai_feature');
      setShowUpgradeModal(true);
      return;
    }

    // User is subscriber, check credits
    if (creditCheck?.hasCredits) {
      setCreditConfirmOperation(operationType);
      setCreditConfirmOperationName(operationName);
      setCreditConfirmCallback(() => onConfirm);
      setCreditConfirmOpen(true);
    } else {
      // Not enough credits - show purchase modal (for subscribers to buy more)
      toast({
        title: 'Not enough video tokens',
        description: `You need ${creditCheck?.cost || 1} video token(s) for ${operationName}. Please purchase more video tokens.`,
        variant: 'destructive',
      });
      setShowCreditPurchaseModal(true);
    }
  };

  // API Helper Functions for MongoDB
  const apiBase = '/api/projects';
  
  const fetchProjects = async () => {
    try {
      const response = await fetch(apiBase, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      return data.projects || [];
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    }
  };
  
  const fetchProject = async (projectId: string) => {
    try {
      const response = await fetch(`${apiBase}/${projectId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch project');
      const data = await response.json();
      return data.project;
    } catch (error) {
      console.error('Error fetching project:', error);
      return null;
    }
  };
  
  const saveProject = async (projectId: string, updates: any) => {
    try {
      const response = await fetch(`${apiBase}/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to save project');
      return true;
    } catch (error) {
      console.error('Error saving project:', error);
      return false;
    }
  };
  
  const createProject = async (name: string) => {
    try {
      const response = await fetch(apiBase, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error('Failed to create project');
      const data = await response.json();
      return data.project;
    } catch (error) {
      console.error('Error creating project:', error);
      return null;
    }
  };
  
  const deleteProjectApi = async (projectId: string) => {
    try {
      const response = await fetch(`${apiBase}/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete project');
      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      return false;
    }
  };
  
  // Track if we're using database or localStorage
  const [useDatabase, setUseDatabase] = useState(false);
  const [dbProjects, setDbProjects] = useState<any[]>([]);
  const [currentDbProject, setCurrentDbProject] = useState<any>(null);
  
  
  // Projects list
  const [projects, setProjects] = useState<string[]>(() => {
    // Only load from localStorage if authenticated
    const hasToken = document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');
    if (!hasToken) return ['default'];

    try {
      const saved = localStorage.getItem('projects_NLE');
      const projectList = saved ? JSON.parse(saved) : ['default'];
      // Ensure default project exists
      if (!projectList.includes('default')) {
        projectList.unshift('default');
      }
      return projectList;
    } catch {
      return ['default'];
    }
  });

  // Project state with localStorage persistence
  const [currentProject, setCurrentProject] = useState<string>(() => {
    // Only load from localStorage if authenticated
    const hasToken = document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');
    if (!hasToken) return 'default';
    return localStorage.getItem('currentProject_NLE') || 'default';
  });

  const [files, setFiles] = useState<ProjectFile[]>(() => {
    // Only load from localStorage if authenticated
    const hasToken = document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');
    if (!hasToken) return [];

    try {
      const saved = localStorage.getItem(`project_${localStorage.getItem('currentProject_NLE') || 'default'}_files`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [clips, setClips] = useState<ProjectClip[]>(() => {
    // Only load from localStorage if authenticated
    const hasToken = document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');
    if (!hasToken) return [];

    try {
      const saved = localStorage.getItem(`project_${localStorage.getItem('currentProject_NLE') || 'default'}_clips`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showImporter, setShowImporter] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [draggedFile, setDraggedFile] = useState<ProjectFile | null>(null);
  
  const [renderedVideos, setRenderedVideos] = useState<RenderedVideo[]>(() => {
    // Only load from localStorage if authenticated
    const hasToken = document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');
    if (!hasToken) return [];

    try {
      const saved = localStorage.getItem(`project_${localStorage.getItem('currentProject_NLE') || 'default'}_rendered`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Saved library - permanent renders that have been transferred to S3
  const [savedRenders, setSavedRenders] = useState<RenderedVideo[]>(() => {
    // Only load from localStorage if authenticated
    const hasToken = document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');
    if (!hasToken) return [];

    try {
      const saved = localStorage.getItem(`project_${localStorage.getItem('currentProject_NLE') || 'default'}_savedRenders`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [selectedJsonView, setSelectedJsonView] = useState<RenderedVideo | null>(null);
  const [selectedVideoModal, setSelectedVideoModal] = useState<RenderedVideo | null>(null);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [renderMode, setRenderMode] = useState<'direct' | 'lambda'>(() => {
    // Restore render mode from localStorage
    const saved = localStorage.getItem('renderMode');
    return (saved === 'lambda' || saved === 'direct') ? saved : 'direct';
  });
  const [creativeBrief, setCreativeBrief] = useState(''); // Creative brief for AI Creative mode
  const [showAICreativeWarning, setShowAICreativeWarning] = useState(false); // Warning popup for AI Creative mode
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false); // Welcome dialog for choosing render mode
  // Export-level video effects (Shotstack filters)
  const [exportFilter, setExportFilter] = useState<string>('none');
  const [colorAdjustments, setColorAdjustments] = useState<ColorAdjustments>({ ...DEFAULT_COLOR_ADJUSTMENTS });
  const [showMasterPrompt, setShowMasterPrompt] = useState(false); // Collapsed by default
  const [editableMasterPrompt, setEditableMasterPrompt] = useState<string>(''); // Editable master prompt
  const [isEditingMasterPrompt, setIsEditingMasterPrompt] = useState(false); // Toggle edit mode
  const [previewClip, setPreviewClip] = useState<{
    clip: ProjectClip;
    file: ProjectFile;
  } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Check if user has valid auth token on mount - look for 'token=' in cookies
    const hasToken = document.cookie.includes('token=');
    return hasToken;
  });
  
  // Background removal state - extracted to useBackgroundRemoval hook
  
  // Import choice dialog state
  const [showImportChoice, setShowImportChoice] = useState(false);

  // AI Asset Generator modal state
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiGeneratingJob, setAiGeneratingJob] = useState<{
    jobId: string;
    type: 'image' | 'video';
    status: string;
    progress: number;
    assetUrl?: string;
  } | null>(null);

  // Schedule Post modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleVideoData, setScheduleVideoData] = useState<{ videoUrl: string; thumbnailUrl?: string; renderId?: string } | null>(null);

  // YouTube publish state - extracted to useYouTubePublish hook

  // Templates panel state
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<VideoTemplate | null>(null);

  // AI Edit Preview state
  const [aiPreview, setAiPreview] = useState<{
    steps: Array<{
      type: string;
      instruction: string;
      timing: { start: number; length: number; end: number };
      timing_note: string;
      effects: string[];
      position: string;
      layer: number;
      asset: { type: string; src: string | null; text: string | null };
    }>;
    summary: {
      totalClips: number;
      totalDuration: string;
      videoClips: number;
      imageClips: number;
      textOverlays: number;
      audioTracks: number;
      hasTransitions: boolean;
      hasFilters: boolean;
    };
    totalDuration: number;
    shotstackJson?: any;
  } | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);

  // Quick Preview state (for direct/Quick Render mode - playback state is internal to QuickPreviewPanel)
  const [quickPreview, setQuickPreview] = useState<{
    clips: Array<{
      id: string;
      type: 'video' | 'image' | 'audio';
      name: string;
      url: string;
      inTime: number;
      outTime: number;
      duration: number;
      thumbnailUrl?: string;
      effects: string[];
    }>;
    summary: {
      totalClips: number;
      totalDuration: string;
      videoClips: number;
      imageClips: number;
      audioTracks: number;
    };
    totalDuration: number;
  } | null>(null);
  const [showQuickPreviewPanel, setShowQuickPreviewPanel] = useState(false);

  // Script Analysis state - extracted to useScriptAnalysis hook (initialized below)

  // Plan Mode state - extracted to usePlanMode hook (initialized below after pollRenderStatus is defined)

  // Sidebar state for desktop NLE layout
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('videoEnhancerSidebarCollapsed');
    // Default to collapsed (1-column mode) if no preference saved
    return saved === null ? true : saved === 'true';
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('videoEnhancerSidebarCollapsed', String(newValue));
      return newValue;
    });
  }, []);

  // Walkthrough tour state
  const [runTour, setRunTour] = useState(false); // Don't start automatically - wait for welcome dialog

  const tourSteps: Step[] = [
    {
      target: '[data-tour="import-button"]',
      content: 'Start by importing videos from your Hey Joe project, or upload files directly using the dropzones.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="media-layers"]',
      content: 'Upload different types of media here: main video/images, background layers, or audio tracks.',
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="project-bin"]',
      content: 'Your files appear here. Select files and click "Add to Timeline", or drag them directly.',
      disableBeacon: true,
      placement: 'top',
    },
    {
      target: '[data-tour="timeline"]',
      content: 'Arrange your clips on the timeline. Click a clip to edit its properties like trim points, titles, and transitions.',
      disableBeacon: true,
      placement: 'top',
    },
    {
      target: '[data-tour="render-mode"]',
      content: 'Choose Quick Render for manual control, or AI Creative to let AI make editing decisions based on your brief.',
      disableBeacon: true,
      placement: 'top',
    },
    {
      target: '[data-tour="render-button"]',
      content: 'When you\'re ready, click here to process your timeline into a final video!',
      disableBeacon: true,
      placement: 'top',
    },
  ];

  const handleTourCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      localStorage.setItem('videoEnhancerTourCompleted', 'true');
    }
  };
  const [pendingImportData, setPendingImportData] = useState<{videos: any[], projectName: string} | null>(null);
  
  // Analysis service state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activePollJobs, setActivePollJobs] = useState<Set<string>>(new Set());
  const [completedJobs, setCompletedJobs] = useState<Set<string>>(new Set());
  const [analysisResults, setAnalysisResults] = useState<{[key: string]: any}>(() => {
    // Load analysis results from localStorage on component mount
    try {
      const saved = localStorage.getItem(`project_${localStorage.getItem('currentProject_NLE') || 'default'}_analysis`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Forward refs for functions defined later in the component
  const pollRenderStatusRef = useRef<(videoId: string, renderId: string) => void>(() => {});
  const applyAIEditToTimelineRef = useRef<(json: any, title: string) => void>(() => {});

  // Extracted hooks
  const youtube = useYouTubePublish({ toast });

  const bgRemoval = useBackgroundRemoval({
    files, setFiles, toast, requireCreditConfirmation, CREDIT_OPERATIONS,
  });

  const audioEnhancement = useAudioEnhancement({
    renderedVideos,
    setRenderedVideos,
    toast,
    requireCreditConfirmation,
    CREDIT_OPERATIONS,
  });

  const bgReplacement = useBackgroundReplacement({
    renderedVideos,
    setRenderedVideos,
    toast,
    requireCreditConfirmation,
    CREDIT_OPERATIONS,
  });

  const scriptHook = useScriptAnalysis({
    files,
    analysisResults,
    currentProject,
    aspectRatio,
    toast,
    onCreativeBriefChange: (brief: string) => setCreativeBrief(brief),
    applyAIEditToTimeline: (...args: [any, string]) => applyAIEditToTimelineRef.current(...args),
  });

  const planMode = usePlanMode({
    files,
    selectedFiles, // Pass selected files for AI Creative mode
    analysisResults,
    scriptContent: scriptHook.scriptContent,
    currentProject,
    aspectRatio,
    toast,
    onRenderCreated: (video: RenderedVideo) => setRenderedVideos(prev => [...prev, video]),
    onRenderUpdated: (videoId: string, updates: Partial<RenderedVideo>) =>
      setRenderedVideos(prev => prev.map(v => v.id === videoId ? { ...v, ...updates } : v)),
    pollRenderStatus: (...args: [string, string]) => pollRenderStatusRef.current(...args),
  });

  // Destructure commonly used hook values for backwards compatibility
  const {
    scriptContent, setScriptContent, scriptFileName, setScriptFileName,
    scriptAnalysis, setScriptAnalysis, isAnalyzingScript,
    scriptAnswers, showScriptPanel, setShowScriptPanel, isGeneratingScriptPlan,
    scriptConversation, isRefiningScript,
    handleScriptLoaded, handleAnalyzeScript, handleScriptAnswerChange,
    handleScriptRefinement, handleGenerateScriptPlan,
    loadProjectScriptData, resetScriptState,
  } = scriptHook;

  const {
    planModeActive, planConversation, isPlanProcessing,
    editPlan, isPlanApproved, isPlanRendering, planQuestions,
    finishingTouches, setFinishingTouches,
    handleStartPlanMode, handlePlanMessage, handlePlanQuestionSelect,
    handleApprovePlan, handleClosePlanMode, handleRefinePlan,
    loadPlanFromRender, resetPlanState,
  } = planMode;

  const {
    youtubeConnected, youtubeChannel,
    showYoutubePublishModal, setShowYoutubePublishModal,
    youtubePublishData, youtubePublishForm, setYoutubePublishForm,
    youtubeUploading, youtubeUploadResult,
    connectYoutube, disconnectYoutube, uploadToYoutube, openYoutubePublishModal,
  } = youtube;

  const {
    showRemoveBgModal, setShowRemoveBgModal,
    pendingRemoveBgFiles, selectedOutputType, setSelectedOutputType,
    showBackgroundSelector, setShowBackgroundSelector,
    selectedOriginalFile, selectedAlphaMask,
    selectedBackground, setSelectedBackground,
    handleRemoveBackground, processRemoveBackground,
    openBackgroundSelector, handleCompositeBackground,
  } = bgRemoval;

  // Check database availability and fetch projects on mount
  useEffect(() => {
    const initializeProjects = async () => {
      console.log('🔐 Checking authentication and MongoDB...');
      
      // First, try to migrate any orphaned projects
      try {
        const migrateResponse = await fetch('/api/projects/migrate', {
          method: 'POST',
          credentials: 'include',
        });
        if (migrateResponse.ok) {
          const migrateResult = await migrateResponse.json();
          if (migrateResult.migratedCount > 0) {
            console.log(`🔄 Migrated ${migrateResult.migratedCount} orphaned projects`);
          }
        }
      } catch (error) {
        console.log('🔄 Migration check failed:', error);
      }
      
      const fetchedProjects = await fetchProjects();
      console.log('📦 Fetched projects:', fetchedProjects);
      
      if (fetchedProjects && fetchedProjects.length > 0) {
        setUseDatabase(true);
        // Deduplicate projects by name (keep first occurrence)
        const seenNames = new Set<string>();
        const uniqueProjects = fetchedProjects.filter(p => {
          if (seenNames.has(p.name)) return false;
          seenNames.add(p.name);
          return true;
        });
        setDbProjects(uniqueProjects);

        // Load the current project from DB
        const projectNames = uniqueProjects.map(p => p.name);
        setProjects(projectNames);
        
        // Load the first project or the saved current one
        const savedCurrent = localStorage.getItem('currentProject_NLE');
        const projectToLoad = fetchedProjects.find(p => p.name === savedCurrent) || fetchedProjects[0];
        if (projectToLoad) {
          setCurrentProject(projectToLoad.name);
          setCurrentDbProject(projectToLoad);
          setFiles(projectToLoad.files || []);
          setClips(projectToLoad.clips || []);
          // Filter out expired renders (older than 24 hours) when loading
          const now = Date.now();
          const twentyFourHours = 24 * 60 * 60 * 1000;
          const validRenders = (projectToLoad.renders || []).filter((video: any) => {
            if (video.status === 'pending' || video.status === 'processing' || video.status === 'rendering') {
              return true;
            }
            const renderTime = new Date(video.timestamp).getTime();
            return (now - renderTime) < twentyFourHours;
          });
          setRenderedVideos(validRenders);
          setSavedRenders(projectToLoad.savedRenders || []);
          setAnalysisResults(projectToLoad.analysisResults || {});
          console.log(`📦 Loaded project: ${projectToLoad.name} (ID: ${projectToLoad._id})`);
        }
        
        console.log('✅ Using MongoDB for project storage');
        toast({
          title: 'Connected to cloud storage',
          description: 'Your projects are now synced across devices',
        });
      } else {
        // Try to create a default project if authenticated
        if (isAuthenticated) {
          console.log('📝 No projects found, creating default project...');
          const defaultProject = await createProject('Default Project');
          if (defaultProject) {
            setUseDatabase(true);
            setDbProjects([defaultProject]);
            setCurrentProject(defaultProject.name);
            setCurrentDbProject(defaultProject);
            console.log('✅ Created default project in MongoDB');
          } else {
            console.log('⚠️ Could not create project, falling back to localStorage');
          }
        } else {
          console.log('❌ Not authenticated, using localStorage for project storage');
        }
      }
    };
    
    if (isAuthenticated) {
      initializeProjects();
    } else {
      console.log('🔒 User not authenticated');
    }
  }, [isAuthenticated]);

  // Track previous authentication state to detect logout events
  const prevAuthRef = useRef<boolean | null>(null);

  // Persist render mode to localStorage
  useEffect(() => {
    localStorage.setItem('renderMode', renderMode);
  }, [renderMode]);

  // Clear all project data when user logs out (but not on initial mount)
  useEffect(() => {
    // Only clear data if authentication changed from true to false (actual logout)
    // Don't clear on initial mount when prevAuthRef is null
    if (prevAuthRef.current === true && !isAuthenticated) {
      console.log('🔒 User logged out - clearing all project data');
      setProjects(['default']);
      setCurrentProject('default');
      setFiles([]);
      setClips([]);
      setRenderedVideos([]);
      setSavedRenders([]);
      setAnalysisResults(null);
      setUseDatabase(false);
      setDbProjects([]);
      setCurrentDbProject(null);
    }

    // Update the previous auth state
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Show welcome dialog on first visit
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcomeDialog');
    const tourCompleted = localStorage.getItem('videoEnhancerTourCompleted');

    if (!hasSeenWelcome) {
      setShowWelcomeDialog(true);
    } else if (!tourCompleted) {
      // If user has seen welcome but hasn't completed tour, start tour after a delay
      setTimeout(() => setRunTour(true), 1000);
    }
  }, []);

  // YouTube handlers extracted to useYouTubePublish hook

  // Save project data (to database or localStorage)
  useEffect(() => {
    // Debounce saves to avoid too many API calls
    const saveTimer = setTimeout(() => {
      const saveProjectData = async () => {
        if (useDatabase && currentDbProject) {
          // Save to database
          console.log('💾 Auto-saving to MongoDB...', {
            projectId: currentDbProject._id,
            filesCount: files.length,
            clipsCount: clips.length
          });
          
          try {
            // Strip analysisData from files and clips to prevent 413 Content Too Large
            // analysisData contains full transcripts which bloat the payload
            const filesWithoutAnalysis = files.map(({ analysisData, ...file }) => file);
            const clipsWithoutAnalysis = clips.map(({ analysisData, ...clip }) => clip);

            await saveProject(currentDbProject._id, {
              files: filesWithoutAnalysis,
              clips: clipsWithoutAnalysis,
              renders: renderedVideos,
              analysisResults,
              savedRenders,
            });
            console.log('✅ Auto-save complete');
          } catch (error) {
            console.error('❌ Auto-save failed:', error);
          }
        } else {
          // Save to localStorage (existing code)
          try {
            localStorage.setItem(`project_${currentProject}_files`, JSON.stringify(files));
            console.log('💾 Saved to localStorage');
          } catch (error) {
            console.error('Failed to save files to localStorage:', error);
          }
        }
      };
      
      saveProjectData();
    }, 1000); // Wait 1 second before saving to debounce
    
    return () => clearTimeout(saveTimer);
  }, [files, clips, renderedVideos, analysisResults, savedRenders, currentProject, useDatabase, currentDbProject]);
  
  // Save clips
  useEffect(() => {
    const saveClipsData = async () => {
      if (useDatabase && currentDbProject) {
        // Handled by main save effect
      } else {
        // Save to localStorage
        try {
          localStorage.setItem(`project_${currentProject}_clips`, JSON.stringify(clips));
        } catch (error) {
          console.error('Failed to save clips to localStorage:', error);
        }
      }
    };
    
    saveClipsData();
  }, [clips, currentProject, useDatabase, currentDbProject]);
  
  // Save rendered videos
  useEffect(() => {
    const saveRenderedData = async () => {
      if (useDatabase && currentDbProject) {
        // Handled by main save effect
      } else {
        // Save to localStorage
        try {
          localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(renderedVideos));
        } catch (error) {
          console.error('Failed to save rendered videos to localStorage:', error);
        }
      }
    };
    
    saveRenderedData();
  }, [renderedVideos, currentProject, useDatabase, currentDbProject]);

  // Save saved renders (library) to localStorage
  useEffect(() => {
    const saveSavedRendersData = async () => {
      if (useDatabase && currentDbProject) {
        // Handled by main save effect
      } else {
        // Save to localStorage
        try {
          localStorage.setItem(`project_${currentProject}_savedRenders`, JSON.stringify(savedRenders));
        } catch (error) {
          console.error('Failed to save saved renders to localStorage:', error);
        }
      }
    };

    saveSavedRendersData();
  }, [savedRenders, currentProject, useDatabase, currentDbProject]);

  // Auto-delete expired renders (older than 24 hours)
  useEffect(() => {
    const cleanupExpiredRenders = () => {
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;

      setRenderedVideos(prev => {
        const validRenders = prev.filter(video => {
          // Keep renders that are still processing
          if (video.status === 'pending' || video.status === 'processing' || video.status === 'rendering') {
            return true;
          }
          const renderTime = new Date(video.timestamp).getTime();
          return (now - renderTime) < twentyFourHours;
        });

        if (validRenders.length !== prev.length) {
          console.log(`Cleaned up ${prev.length - validRenders.length} expired render(s)`);
          return validRenders;
        }
        return prev;
      });
    };

    // Run immediately on mount
    cleanupExpiredRenders();

    // Run every 5 minutes to catch renders as they expire
    const interval = setInterval(cleanupExpiredRenders, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Save analysis results
  useEffect(() => {
    const saveAnalysisData = async () => {
      if (useDatabase && currentDbProject) {
        // Handled by main save effect
      } else {
        // Save to localStorage
        try {
          localStorage.setItem(`project_${currentProject}_analysis`, JSON.stringify(analysisResults));
        } catch (error) {
          console.error('Failed to save analysis results to localStorage:', error);
        }
      }
    };
    
    saveAnalysisData();
  }, [analysisResults, currentProject, useDatabase, currentDbProject]);
  
  // Note: Commenting out the original localStorage saves since they're now handled above
  /*
  // Save project data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(`project_${currentProject}_files`, JSON.stringify(files));
    } catch (error) {
      console.error('Failed to save files to localStorage:', error);
    }
  }, [files, currentProject]);
  
  useEffect(() => {
    try {
      localStorage.setItem(`project_${currentProject}_clips`, JSON.stringify(clips));
    } catch (error) {
      console.error('Failed to save clips to localStorage:', error);
    }
  }, [clips, currentProject]);
  
  useEffect(() => {
    try {
      localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(renderedVideos));
    } catch (error) {
      console.error('Failed to save rendered videos to localStorage:', error);
    }
  }, [renderedVideos, currentProject]);
  
  // Save analysis results to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(`project_${currentProject}_analysis`, JSON.stringify(analysisResults));
    } catch (error) {
      console.error('Failed to save analysis results to localStorage:', error);
    }
  }, [analysisResults, currentProject]);
  */

  // Save projects list
  useEffect(() => {
    try {
      localStorage.setItem('projects_NLE', JSON.stringify(projects));
    } catch (error) {
      console.error('Failed to save projects list:', error);
    }
  }, [projects]);

  // Project management functions
  const handleProjectChange = async (projectName: string) => {
    if (useDatabase) {
      // Load from database
      const project = dbProjects.find(p => p.name === projectName);
      if (project) {
        setCurrentProject(projectName);
        setCurrentDbProject(project);
        setFiles(project.files || []);
        setClips(project.clips || []);
        // Filter out expired renders (older than 24 hours) when loading
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        const validRenders = (project.renders || []).filter((video: any) => {
          if (video.status === 'pending' || video.status === 'processing' || video.status === 'rendering') {
            return true;
          }
          const renderTime = new Date(video.timestamp).getTime();
          return (now - renderTime) < twentyFourHours;
        });
        setRenderedVideos(validRenders);
        setSavedRenders(project.savedRenders || []);
        setAnalysisResults(project.analysisResults || {});
        localStorage.setItem('currentProject_NLE', projectName);

        // Reset Plan Mode state when switching projects
        resetPlanState();
      }
    } else {
      // Load from localStorage (existing code)
      localStorage.setItem('currentProject_NLE', projectName);
      setCurrentProject(projectName);
      
      // Load the new project's data
      try {
        const savedFiles = localStorage.getItem(`project_${projectName}_files`);
        setFiles(savedFiles ? JSON.parse(savedFiles) : []);
        
        const savedClips = localStorage.getItem(`project_${projectName}_clips`);
        setClips(savedClips ? JSON.parse(savedClips) : []);
        
        const savedRendered = localStorage.getItem(`project_${projectName}_rendered`);
        setRenderedVideos(savedRendered ? JSON.parse(savedRendered) : []);
        
        const savedAnalysis = localStorage.getItem(`project_${projectName}_analysis`);
        setAnalysisResults(savedAnalysis ? JSON.parse(savedAnalysis) : {});

        // Load script data
        loadProjectScriptData(projectName);
        // Also update creative brief if script was loaded
        const savedScriptContent = localStorage.getItem(`project_${projectName}_scriptContent`);
        if (savedScriptContent) {
          setCreativeBrief(savedScriptContent);
        } else {
          setCreativeBrief('');
        }
      } catch (error) {
        console.error('Failed to load project data:', error);
        setFiles([]);
        setClips([]);
        setRenderedVideos([]);
        setAnalysisResults({});
        resetScriptState();
        setCreativeBrief('');
      }

      // Reset Plan Mode state when switching projects (localStorage branch)
      resetPlanState();
    }

    setSelectedFiles(new Set());

    toast({
      title: "Project switched",
      description: `Switched to project: ${projectName}`,
    });
  };

  const handleDeleteProject = async (projectName: string) => {
    if (projectName === 'default' && !useDatabase) {
      toast({
        title: "Cannot delete default project",
        description: "The default project cannot be deleted",
        variant: "destructive"
      });
      return;
    }
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the project "${projectName}"? This will remove all files, clips, and rendered videos.`)) {
      return;
    }
    
    if (useDatabase) {
      // Delete from database
      const project = dbProjects.find(p => p.name === projectName);
      if (project) {
        const success = await deleteProjectApi(project._id);
        if (success) {
          // Remove from local state
          const updatedDbProjects = dbProjects.filter(p => p.name !== projectName);
          setDbProjects(updatedDbProjects);
          setProjects(updatedDbProjects.map(p => p.name));
          
          // If deleting current project, switch to another
          if (currentProject === projectName) {
            const nextProject = updatedDbProjects[0];
            if (nextProject) {
              handleProjectChange(nextProject.name);
            }
          }
          
          toast({
            title: "Project deleted",
            description: `Project "${projectName}" has been deleted from cloud`,
          });
        } else {
          toast({
            title: "Delete failed",
            description: "Failed to delete project from cloud",
            variant: "destructive"
          });
        }
      }
    } else {
      // Remove from localStorage (existing code)
      localStorage.removeItem(`project_${projectName}_files`);
      localStorage.removeItem(`project_${projectName}_clips`);
      localStorage.removeItem(`project_${projectName}_rendered`);
      localStorage.removeItem(`project_${projectName}_analysis`);
      
      // Remove from projects list
      const updatedProjects = projects.filter(p => p !== projectName);
      setProjects(updatedProjects);
      
      // If deleting current project, switch to default
      if (currentProject === projectName) {
        handleProjectChange('default');
      }
      
      toast({
        title: "Project deleted",
        description: `Project "${projectName}" has been deleted`,
      });
    }
  };

  const handleDeleteRenderedVideo = (videoId: string) => {
    if (!window.confirm('Are you sure you want to remove this video from the history?')) {
      return;
    }

    const updatedVideos = renderedVideos.filter(v => v.id !== videoId);
    setRenderedVideos(updatedVideos);
    localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updatedVideos));

    toast({
      title: "Video removed",
      description: "The video has been removed from the history"
    });
  };

  // Save rendered video to library (permanent S3 storage)
  const handleSaveToLibrary = async (video: RenderedVideo) => {
    if (!video.url) {
      toast({
        title: "Cannot save",
        description: "Video URL is not available",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Saving to library...",
      description: "Transferring video to permanent storage",
    });

    try {
      // Transfer the video from Shotstack's cache to our S3 bucket
      const response = await fetch('/api/transfer-to-s3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUrl: video.url,
          fileName: video.name
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Transfer failed');
      }

      const result = await response.json();

      // Create saved render with S3 info
      const savedRender: RenderedVideo = {
        ...video,
        savedAt: new Date().toISOString(),
        savedS3Url: result.s3Url,
        savedS3Key: result.s3Key,
      };

      // Add to saved renders (library) - update state AND localStorage immediately
      const updatedSavedRenders = [...savedRenders, savedRender];
      setSavedRenders(updatedSavedRenders);
      localStorage.setItem(`project_${currentProject}_savedRenders`, JSON.stringify(updatedSavedRenders));

      // Remove from render history (move, not copy)
      const updatedRenderedVideos = renderedVideos.filter(v => v.id !== video.id);
      setRenderedVideos(updatedRenderedVideos);
      localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updatedRenderedVideos));

      toast({
        title: "Saved to library",
        description: `"${video.name}" has been permanently saved`,
      });
    } catch (error) {
      console.error('Error saving to library:', error);
      toast({
        title: "Save Failed",
        description: error instanceof Error ? error.message : "Failed to save video to library",
        variant: "destructive"
      });
    }
  };

  // Delete video from saved library
  const handleDeleteFromLibrary = (videoId: string) => {
    if (!window.confirm('Are you sure you want to remove this video from your library? The file will remain in S3.')) {
      return;
    }

    const updatedSaved = savedRenders.filter(v => v.id !== videoId);
    setSavedRenders(updatedSaved);
    localStorage.setItem(`project_${currentProject}_savedRenders`, JSON.stringify(updatedSaved));

    toast({
      title: "Removed from library",
      description: "The video has been removed from your library"
    });
  };

  // Re-import rendered video to files bin
  const handleReimportRenderedVideo = async (video: RenderedVideo) => {
    if (!video.url) {
      toast({
        title: "Error",
        description: "Video URL not available",
        variant: "destructive"
      });
      return;
    }

    // Show loading toast
    toast({
      title: "Transferring...",
      description: "Uploading video to permanent storage",
    });

    try {
      // Transfer the video from Shotstack's cache to our S3 bucket
      const response = await fetch('/api/transfer-to-s3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUrl: video.url,
          fileName: video.name
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Transfer failed');
      }

      const result = await response.json();

      // Create a new file entry with the permanent S3 URL
      const newFile: ProjectFile = {
        id: Math.random().toString(36).substr(2, 9),
        name: `${video.name} (Rendered)`,
        fileType: 'video',
        thumbnailUrl: video.thumbnailUrl || '',
        duration: video.duration || 0,
        timestamp: new Date().toISOString(),
        s3Url: result.s3Url,
        s3Key: result.s3Key,
        imported: true,
        enhanced: true,
        enhancementPrompt: video.configuration.masterPrompt || 'Re-imported from render',
      };

      setFiles(prev => [...prev, newFile]);

      toast({
        title: "Re-imported",
        description: `"${video.name}" added to files bin with permanent storage`,
      });
    } catch (error) {
      console.error('Error transferring video:', error);
      toast({
        title: "Transfer Failed",
        description: error instanceof Error ? error.message : "Failed to transfer video",
        variant: "destructive"
      });
    }
  };

  const handleRenameProject = (oldName: string) => {
    if (oldName === 'default') {
      toast({
        title: "Cannot rename default project",
        description: "The default project cannot be renamed",
        variant: "destructive"
      });
      return;
    }

    const newName = window.prompt(`Enter new name for project "${oldName}":`, oldName);
    if (!newName || newName.trim() === '' || newName === oldName) {
      return;
    }

    const trimmedName = newName.trim();
    if (projects.includes(trimmedName)) {
      toast({
        title: "Project name already exists",
        description: `A project named "${trimmedName}" already exists`,
        variant: "destructive"
      });
      return;
    }

    // Get all data from old project
    const oldFiles = localStorage.getItem(`project_${oldName}_files`);
    const oldClips = localStorage.getItem(`project_${oldName}_clips`);
    const oldRendered = localStorage.getItem(`project_${oldName}_rendered`);
    const oldAnalysis = localStorage.getItem(`project_${oldName}_analysis`);

    // Save to new project keys
    if (oldFiles) localStorage.setItem(`project_${trimmedName}_files`, oldFiles);
    if (oldClips) localStorage.setItem(`project_${trimmedName}_clips`, oldClips);
    if (oldRendered) localStorage.setItem(`project_${trimmedName}_rendered`, oldRendered);
    if (oldAnalysis) localStorage.setItem(`project_${trimmedName}_analysis`, oldAnalysis);

    // Remove old project data
    localStorage.removeItem(`project_${oldName}_files`);
    localStorage.removeItem(`project_${oldName}_clips`);
    localStorage.removeItem(`project_${oldName}_rendered`);
    localStorage.removeItem(`project_${oldName}_analysis`);

    // Update projects list
    const updatedProjects = projects.map(p => p === oldName ? trimmedName : p);
    setProjects(updatedProjects);
    localStorage.setItem('projects_NLE', JSON.stringify(updatedProjects));

    // Switch to renamed project if it was current
    if (currentProject === oldName) {
      setCurrentProject(trimmedName);
      localStorage.setItem('currentProject_NLE', trimmedName);
      
      // Reload project data
      const newFiles = localStorage.getItem(`project_${trimmedName}_files`);
      const newClips = localStorage.getItem(`project_${trimmedName}_clips`);
      const newRendered = localStorage.getItem(`project_${trimmedName}_rendered`);

      if (newFiles) setFiles(JSON.parse(newFiles));
      if (newClips) setClips(JSON.parse(newClips));
      if (newRendered) setRenderedVideos(JSON.parse(newRendered));
    }

    toast({
      title: "Project renamed",
      description: `Project renamed from "${oldName}" to "${trimmedName}"`,
    });
  };

  const handleCreateProject = async () => {
    const name = window.prompt('Enter new project name:');
    if (name && name.trim()) {
      const trimmedName = name.trim();

      // Check if project with this name already exists
      if (projects.includes(trimmedName)) {
        toast({
          title: "Project exists",
          description: `A project named "${trimmedName}" already exists. Please choose a different name.`,
          variant: "destructive"
        });
        return;
      }

      if (useDatabase) {
        const newProject = await createProject(trimmedName);
        if (newProject) {
          const updatedProjects = [...dbProjects, newProject];
          setDbProjects(updatedProjects);
          setProjects(updatedProjects.map(p => p.name));
          // Directly switch to the new project instead of calling handleProjectChange
          // since the state update is async and dbProjects won't have it yet
          setCurrentProject(newProject.name);
          setCurrentDbProject(newProject);
          setFiles(newProject.files || []);
          setClips(newProject.clips || []);
          setRenderedVideos(newProject.renders || []);
          setSavedRenders(newProject.savedRenders || []);
          setAnalysisResults(newProject.analysisResults || {});
          setSelectedFiles(new Set());
          // Clear script state for new project
          resetScriptState();
          setCreativeBrief('');
          localStorage.setItem('currentProject_NLE', newProject.name);
          toast({
            title: "Project created",
            description: `Created and opened: ${newProject.name}`,
          });
          // Show welcome dialog to choose render mode for new project
          setShowWelcomeDialog(true);
        }
      } else {
        const updatedProjects = [...projects, trimmedName];
        setProjects(updatedProjects);
        localStorage.setItem('projects_NLE', JSON.stringify(updatedProjects));
        // Directly switch to the new empty project
        setCurrentProject(trimmedName);
        setFiles([]);
        setClips([]);
        setRenderedVideos([]);
        setAnalysisResults({});
        setSelectedFiles(new Set());
        // Clear script state for new project
        resetScriptState();
        setCreativeBrief('');
        localStorage.setItem('currentProject_NLE', trimmedName);
        toast({
          title: "Project created",
          description: `Created and opened: ${trimmedName}`,
        });
        // Show welcome dialog to choose render mode for new project
        setShowWelcomeDialog(true);
      }
    }
  };

  // Helper function to wait for iCloud file download
  const waitForFileDownload = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      // Check if file seems to be an iCloud placeholder (very small size for video)
      const isLikelyICloudPlaceholder = file.size < 1000 && /\.(mov|mp4|m4v)$/i.test(file.name);
      
      if (!isLikelyICloudPlaceholder) {
        resolve(file);
        return;
      }
      
      console.log(`📱 iOS Debug - Detected potential iCloud file: ${file.name}, size: ${file.size}`);
      
      // For iCloud files, we need to read the file to trigger download
      const reader = new FileReader();
      reader.onload = () => {
        console.log(`📱 iOS Debug - iCloud file downloaded: ${file.name}`);
        resolve(file);
      };
      reader.onerror = () => {
        console.error(`📱 iOS Debug - Failed to download iCloud file: ${file.name}`);
        reject(new Error('Failed to download file from iCloud'));
      };
      
      // Start reading the file - this triggers iCloud download
      reader.readAsArrayBuffer(file);
      
      // Timeout after 30 seconds
      setTimeout(() => {
        reject(new Error('iCloud download timeout'));
      }, 30000);
    });
  };

  // Handle file uploads for main timeline (video/image)
  const onDropMain = useCallback(async (acceptedFiles: File[], layerType: 'main' | 'background' | 'audio' = 'main') => {
    console.log('📱 iOS Debug - Files received for layer:', layerType, acceptedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
    
    // iOS often doesn't set MIME types correctly, so we'll be more flexible with case-insensitive extensions
    const videoFiles = acceptedFiles.filter(file => 
      file.type.startsWith('video/') || 
      /\.(mp4|mov|avi|webm|m4v)$/i.test(file.name)
    );
    const audioFiles = acceptedFiles.filter(file => 
      file.type.startsWith('audio/') || 
      /\.(mp3|wav|m4a|aac)$/i.test(file.name)
    );
    // Filter images - exclude HEIC/HEIF as Shotstack doesn't support them
    const imageFiles = acceptedFiles.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      const isHeic = ext === 'heic' || ext === 'heif' || file.type === 'image/heic' || file.type === 'image/heif';
      if (isHeic) {
        return false; // Reject HEIC files
      }
      return file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
    });

    // Warn about rejected HEIC files
    const heicFiles = acceptedFiles.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ext === 'heic' || ext === 'heif' || file.type === 'image/heic' || file.type === 'image/heif';
    });
    if (heicFiles.length > 0) {
      toast({
        title: "HEIC format not supported",
        description: `${heicFiles.length} HEIC file(s) skipped. Please convert to JPG or PNG first.`,
        variant: "destructive"
      });
    }
    const srtFiles = acceptedFiles.filter(file => file.name.toLowerCase().endsWith('.srt'));
    
    const allSupportedFiles = [...videoFiles, ...audioFiles, ...imageFiles, ...srtFiles];
    
    console.log('📱 iOS Debug - Supported files:', allSupportedFiles.map(f => ({ name: f.name, type: f.type })));
    
    if (allSupportedFiles.length === 0) {
      toast({
        title: "Invalid files",
        description: "Please select video, audio, image, or SRT files",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Downloading files from iCloud",
      description: `Processing ${allSupportedFiles.length} file${allSupportedFiles.length > 1 ? 's' : ''}...`,
    });

    // Process files and upload to S3
    const newFiles: ProjectFile[] = await Promise.all(
      allSupportedFiles.map(async (file) => {
        try {
          console.log(`📱 iOS Debug - Starting processing for ${file.name}, type: ${file.type}`);
          
          // Wait for iCloud download if needed
          const downloadedFile = await waitForFileDownload(file);
          console.log(`📱 iOS Debug - File ready: ${downloadedFile.name}, size: ${downloadedFile.size}`);
          
          // Determine file type more reliably for iOS
          let detectedType = downloadedFile.type;
          if (!detectedType || detectedType === 'application/octet-stream') {
            // iOS often reports wrong MIME types, detect from extension (case-insensitive)
            const ext = downloadedFile.name.toLowerCase().split('.').pop();
            switch (ext) {
              case 'mp4':
              case 'm4v':
                detectedType = 'video/mp4';
                break;
              case 'mov':
                detectedType = 'video/quicktime';
                break;
              case 'avi':
                detectedType = 'video/x-msvideo';
                break;
              case 'webm':
                detectedType = 'video/webm';
                break;
              case 'mp3':
                detectedType = 'audio/mpeg';
                break;
              case 'wav':
                detectedType = 'audio/wav';
                break;
              case 'm4a':
              case 'aac':
                detectedType = 'audio/mp4';
                break;
              case 'jpg':
              case 'jpeg':
                detectedType = 'image/jpeg';
                break;
              case 'png':
                detectedType = 'image/png';
                break;
              case 'gif':
                detectedType = 'image/gif';
                break;
              case 'webp':
                detectedType = 'image/webp';
                break;
              case 'srt':
                detectedType = 'text/plain';
                break;
              default:
                detectedType = downloadedFile.type || 'application/octet-stream';
            }
          }
          
          console.log(`📱 iOS Debug - Detected type: ${detectedType} for ${downloadedFile.name}`);
          
          // Get upload URL from backend
          const response = await fetch('/get-upload-url', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fileName: downloadedFile.name,
              fileType: detectedType
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`📱 iOS Debug - Upload URL request failed:`, errorText);
            throw new Error('Failed to get upload URL: ' + errorText);
          }

          const { uploadUrl, downloadUrl: origDownloadUrl, s3Key: origS3Key } = await response.json();
          let downloadUrl = origDownloadUrl;
          let s3Key = origS3Key;
          console.log(`📱 iOS Debug - Got upload URL for ${downloadedFile.name}`);

          toast({
            title: "Uploading to cloud",
            description: `Uploading ${downloadedFile.name}...`,
          });

          // Upload file to S3
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: downloadedFile,
            headers: {
              'Content-Type': detectedType
            }
          });

          if (!uploadResponse.ok) {
            console.error(`📱 iOS Debug - S3 upload failed for ${downloadedFile.name}:`, uploadResponse.status, uploadResponse.statusText);
            throw new Error(`Failed to upload file to S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }
          
          console.log(`📱 iOS Debug - Successfully uploaded ${downloadedFile.name} to S3`);

          // Convert non-MP4 video files (e.g. iPhone MOV) via MediaConvert
          if (detectedType.startsWith('video/') && needsConversion(downloadedFile.name)) {
            toast({ title: 'Converting MOV to MP4...', description: 'This may take a minute' });
            const convResult = await convertMov(s3Key, 'hey-joe-videos');
            s3Key = convResult.outputKey;
            downloadUrl = convResult.downloadUrl;
            console.log(`✅ Converted ${downloadedFile.name} to MP4: ${s3Key}`);
          }

        const fileType = detectedType.startsWith('video/') ? 'video' :
                        detectedType.startsWith('audio/') ? 'audio' :
                        detectedType.startsWith('image/') ? 'image' : 'srt';

        let thumbnailUrl = '';
        let duration = 0;
        let aspectRatio: string = '';
        let videoWidth = 0;
        let videoHeight = 0;

        // Generate thumbnail for video files
        if (fileType === 'video') {
          // For mobile devices, skip complex video thumbnail generation to avoid timeouts
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          
          if (isMobile) {
            console.log(`📱 iOS Debug - Mobile device detected, using simple video processing for ${downloadedFile.name}`);
            thumbnailUrl = 'VIDEO_ICON';
            
            // Try to get duration quickly without thumbnail generation
            try {
              const video = document.createElement('video');
              video.muted = true;
              video.playsInline = true;
              const videoUrl = URL.createObjectURL(downloadedFile);
              
              duration = await new Promise<number>((resolve) => {
                const timeout = setTimeout(() => {
                  URL.revokeObjectURL(videoUrl);
                  resolve(0);
                }, 3000); // Short timeout for mobile
                
                video.onloadedmetadata = () => {
                  clearTimeout(timeout);
                  URL.revokeObjectURL(videoUrl);
                  resolve(video.duration || 0);
                };
                
                video.onerror = () => {
                  clearTimeout(timeout);
                  URL.revokeObjectURL(videoUrl);
                  resolve(0);
                };
                
                video.src = videoUrl;
              });
              
              console.log(`📱 iOS Debug - Got video duration: ${duration} for ${downloadedFile.name}`);
            } catch (error) {
              console.log(`📱 iOS Debug - Failed to get video duration, using 0 for ${downloadedFile.name}`);
              duration = 0;
            }
          } else {
            // Desktop: use full thumbnail generation
            console.log(`📱 iOS Debug - Desktop device, using full video processing for ${downloadedFile.name}`);
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const videoUrl = URL.createObjectURL(downloadedFile);

            thumbnailUrl = await new Promise<string>((resolve) => {
              let resolved = false;

              const cleanup = () => {
                if (!resolved) {
                  resolved = true;
                  URL.revokeObjectURL(videoUrl);
                }
              };

              const timeout = setTimeout(() => {
                cleanup();
                resolve('VIDEO_ICON');
              }, 10000);

              video.onloadedmetadata = () => {
                duration = video.duration;
                videoWidth = video.videoWidth;
                videoHeight = video.videoHeight;
                console.log(`📐 Video dimensions: ${videoWidth}x${videoHeight} for ${downloadedFile.name}`);
                const seekTime = Math.min(1, video.duration * 0.1);
                video.currentTime = seekTime;
              };

              video.onseeked = () => {
                // Set canvas size based on video aspect ratio
                const isPortrait = videoHeight > videoWidth;
                if (isPortrait) {
                  // Portrait video (9:16) - use portrait thumbnail
                  canvas.width = 180;
                  canvas.height = 320;
                } else {
                  // Landscape video (16:9) - use landscape thumbnail
                  canvas.width = 320;
                  canvas.height = 180;
                }
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  try {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
                    clearTimeout(timeout);
                    cleanup();
                    resolve(thumbnail);
                  } catch (error) {
                    clearTimeout(timeout);
                    cleanup();
                    resolve('VIDEO_ICON');
                  }
                } else {
                  clearTimeout(timeout);
                  cleanup();
                  resolve('VIDEO_ICON');
                }
              };

              video.onerror = () => {
                clearTimeout(timeout);
                cleanup();
                resolve('VIDEO_ICON');
              };

              video.src = videoUrl;
            });

            // Calculate aspect ratio and store on file
            if (videoWidth > 0 && videoHeight > 0) {
              const ratio = videoWidth / videoHeight;
              // Match to standard ratios if close, otherwise leave for "match source"
              const TOLERANCE = 0.05;
              if (Math.abs(ratio - 16/9) < TOLERANCE) aspectRatio = '16:9';
              else if (Math.abs(ratio - 9/16) < TOLERANCE) aspectRatio = '9:16';
              else if (Math.abs(ratio - 1) < TOLERANCE) aspectRatio = '1:1';
              else if (Math.abs(ratio - 4/5) < TOLERANCE) aspectRatio = '4:5';
              else aspectRatio = 'source'; // Non-standard ratio — use match source
              console.log(`📐 Detected aspect ratio: ${aspectRatio} (${ratio.toFixed(3)}, ${videoWidth}x${videoHeight}) for ${downloadedFile.name}`);
            }
          }
        } else if (fileType === 'image') {
          // Use S3 URL for images instead of blob URL to prevent expiration
          thumbnailUrl = downloadUrl;
        } else if (fileType === 'audio') {
          thumbnailUrl = 'AUDIO_ICON';
          // Get audio duration
          const audio = document.createElement('audio');
          const audioUrl = URL.createObjectURL(downloadedFile);
          duration = await new Promise<number>((resolve) => {
            audio.onloadedmetadata = () => {
              URL.revokeObjectURL(audioUrl);
              resolve(audio.duration);
            };
            audio.onerror = () => {
              URL.revokeObjectURL(audioUrl);
              resolve(0);
            };
            audio.src = audioUrl;
          });
        } else if (fileType === 'srt') {
          thumbnailUrl = 'SRT_ICON';
        }

          const fileObject = {
            id: Math.random().toString(36).substr(2, 9),
            name: downloadedFile.name,
            fileType,
            thumbnailUrl,
            duration,
            width: videoWidth || undefined,
            height: videoHeight || undefined,
            aspectRatio: aspectRatio || undefined, // Video aspect ratio (9:16 or 16:9)
            timestamp: new Date().toISOString(),
            imported: false,
            s3Url: downloadUrl, // Add the S3 URL from the upload
            s3Key: s3Key, // Add the S3 key
            layerType: layerType, // Add layer type to track which dropzone was used
          };
          
          console.log(`📱 iOS Debug - Successfully created file object for ${downloadedFile.name}:`, {
            name: fileObject.name,
            fileType: fileObject.fileType,
            hasThumbnail: !!fileObject.thumbnailUrl,
            thumbnailType: typeof fileObject.thumbnailUrl,
            duration: fileObject.duration,
            hasS3Url: !!fileObject.s3Url
          });
          
          return fileObject;
        } catch (error) {
          console.error(`📱 iOS Debug - Failed to upload ${file.name}:`, error);
          toast({
            title: "Upload failed",
            description: `Failed to upload ${file.name}. ${error instanceof Error ? error.message : 'Unknown error'}`,
            variant: "destructive"
          });
          return null;
        }
      })
    );

    // Filter out failed uploads
    const successfulFiles = newFiles.filter(f => f !== null) as ProjectFile[];
    
    console.log(`📱 iOS Debug - Upload results:`, {
      totalAttempted: newFiles.length,
      successful: successfulFiles.length,
      failed: newFiles.length - successfulFiles.length,
      successfulFiles: successfulFiles.map(f => f?.name)
    });

    if (successfulFiles.length > 0) {
      console.log(`📱 iOS Debug - Adding ${successfulFiles.length} files to project bin`);
      setFiles(prev => {
        const updated = [...prev, ...successfulFiles];
        console.log(`📱 iOS Debug - Project bin now has ${updated.length} total files`);
        return updated;
      });

      toast({
        title: "Files uploaded",
        description: `${successfulFiles.length} file${successfulFiles.length > 1 ? 's' : ''} uploaded successfully`,
      });

      // Auto-analyze uploaded video files (get transcripts)
      const videoFiles = successfulFiles.filter(f => f.fileType === 'video' && f.s3Url);

      if (videoFiles.length > 0) {
        console.log(`🎬 Auto-analyzing ${videoFiles.length} uploaded videos...`);
        // Pass file objects directly to avoid stale state issues
        handleAnalyze(videoFiles);

        // Auto-detect render aspect ratio from uploaded videos
        const videoAspectRatios = videoFiles
          .filter((f: any) => f.aspectRatio)
          .map((f: any) => f.aspectRatio);

        if (videoAspectRatios.length > 0) {
          // Use the aspect ratio of the first video with a detected ratio
          const detectedRatio = videoAspectRatios[0];
          console.log(`📐 Auto-setting render aspect ratio to ${detectedRatio} based on uploaded video`);
          setAspectRatio(detectedRatio);
        }
      }
    } else if (newFiles.length > 0) {
      console.log(`📱 iOS Debug - All uploads failed!`);
      toast({
        title: "Upload failed",
        description: "All files failed to upload. Please try again.",
        variant: "destructive"
      });
    }
  }, [toast]);

  // Wrapper for backward compatibility
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    return onDropMain(acceptedFiles, 'main');
  }, [onDropMain]);

  // Create unified dropzone that auto-detects file types
  const { getRootProps: getMainRootProps, getInputProps: getMainInputProps, isDragActive: isMainDragActive } = useDropzone({
    onDrop: (files) => {
      // Auto-detect audio files and route them to audio layer
      const audioFiles = files.filter(file =>
        file.type.startsWith('audio/') ||
        /\.(mp3|wav|m4a|aac)$/i.test(file.name)
      );
      const otherFiles = files.filter(file =>
        !file.type.startsWith('audio/') &&
        !/\.(mp3|wav|m4a|aac)$/i.test(file.name)
      );

      // Process audio files as audio layer
      if (audioFiles.length > 0) {
        onDropMain(audioFiles, 'audio');
      }
      // Process video/image/srt as main layer
      if (otherFiles.length > 0) {
        onDropMain(otherFiles, 'main');
      }
    },
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.webm', '.m4v'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'text/plain': ['.srt'],
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac'],
      // iOS fallback
      'application/octet-stream': ['.mp4', '.mov', '.m4v'],
    },
    multiple: true,
    preventDropOnDocument: true,
  });

  // Background dropzone (video/image)
  const onDropBackground = useCallback(async (acceptedFiles: File[]) => {
    console.log('🎨 Background files received:', acceptedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
    
    // Process background files with background layer type
    await onDropMain(acceptedFiles, 'background');
  }, [onDropMain]);

  const { getRootProps: getBackgroundRootProps, getInputProps: getBackgroundInputProps, isDragActive: isBackgroundDragActive } = useDropzone({
    onDrop: onDropBackground,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.webm', '.m4v'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      // iOS fallback
      'application/octet-stream': ['.mp4', '.mov', '.m4v'],
    },
    multiple: false, // Typically only one background at a time
    preventDropOnDocument: true,
  });

  // Audio dropzone
  const onDropAudio = useCallback(async (acceptedFiles: File[]) => {
    console.log('🎵 Audio files received:', acceptedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
    
    const audioFiles = acceptedFiles.filter(file => 
      file.type.startsWith('audio/') || 
      /\.(mp3|wav|m4a|aac)$/i.test(file.name)
    );
    
    if (audioFiles.length === 0) {
      toast({
        title: "Invalid files",
        description: "Please select audio files only",
        variant: "destructive"
      });
      return;
    }
    
    // Process audio files with audio layer type
    await onDropMain(audioFiles, 'audio');
  }, [onDropMain, toast]);

  const { getRootProps: getAudioRootProps, getInputProps: getAudioInputProps, isDragActive: isAudioDragActive } = useDropzone({
    onDrop: onDropAudio,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac'],
    },
    multiple: true,
    preventDropOnDocument: true,
  });

  // Keep original names for backward compatibility
  const getRootProps = getMainRootProps;
  const getInputProps = getMainInputProps;
  const isDragActive = isMainDragActive;

  // Handle login success
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setIsAuthenticated(true); // Update auth state
    setShowImporter(true);
  };

  // Handle Hey Joe video imports
  const handleVideosImported = async (importedVideos: any[]) => {
    // Extract project name from first video - check castingData first
    const projectName = importedVideos[0]?.castingData?.projectName || importedVideos[0]?.projectName || 'Untitled Project';
    const sessionName = importedVideos[0]?.castingData?.sessionName || importedVideos[0]?.sessionName || 'Session';
    const fullProjectName = `${projectName} - ${sessionName}`;
    
    console.log('📥 Import received for:', fullProjectName);
    
    // Store the import data and show choice dialog
    setPendingImportData({ videos: importedVideos, projectName: fullProjectName });
    setShowImportChoice(true);
    setShowImporter(false);
  };
  
  // Process import based on user choice
  const processImport = async (createNewProject: boolean) => {
    if (!pendingImportData) return;
    
    const { videos: importedVideos, projectName: fullProjectName } = pendingImportData;
    
    // If user wants to create a new project
    if (createNewProject) {
      console.log('🆕 Creating new project for import:', fullProjectName);
      
      if (useDatabase) {
        // Create new project in MongoDB
        const newProject = await createProject(fullProjectName);

        if (newProject) {
          setDbProjects(prev => [...prev, newProject]);
          setProjects(prev => {
            if (!prev.includes(fullProjectName)) {
              return [...prev, fullProjectName];
            }
            return prev;
          });

          // Switch to the new project
          setCurrentProject(fullProjectName);
          setCurrentDbProject(newProject);
          localStorage.setItem('currentProject_NLE', fullProjectName);

          // Clear existing data for new project
          setFiles([]);
          setClips([]);
          setRenderedVideos([]);
          setAnalysisResults({});
          // Clear script state for new project
          resetScriptState();
          setCreativeBrief('');
        } else {
          // Project creation failed - abort import and ask user to create project first
          console.error('❌ Failed to create project:', fullProjectName);
          toast({
            title: "Project creation failed",
            description: `Could not create "${fullProjectName}". Please create a new project first, then try importing again.`,
            variant: "destructive"
          });
          // Clear pending import data and close modal
          setPendingImportData(null);
          setShowImportChoice(false);
          return; // Abort the import
        }
      } else {
        // LocalStorage path
        if (!projects.includes(fullProjectName)) {
          setProjects(prev => [...prev, fullProjectName]);
        }
        
        // Switch to new project
        setCurrentProject(fullProjectName);
        localStorage.setItem('currentProject_NLE', fullProjectName);
        
        // Clear existing data
        setFiles([]);
        setClips([]);
        setRenderedVideos([]);
        setAnalysisResults({});
        // Clear script state for new project
        resetScriptState();
        setCreativeBrief('');
      }
    } else {
      console.log('📁 Adding to current project:', currentProject);
    }
    
    const newFiles: ProjectFile[] = importedVideos.map(video => ({
      id: video.id || Math.random().toString(36).substr(2, 9),
      name: video.name || video.fileName || 'Imported Video',
      fileType: 'video' as const,
      thumbnailUrl: video.thumbnailUrl || video.castingData?.thumbnailUrl,
      duration: video.duration || 0,
      timestamp: new Date().toISOString(),
      projectName: video.castingData?.projectName || video.projectName,
      sessionName: video.castingData?.sessionName || video.sessionName,
      groupName: video.castingData?.groupName || video.groupName,
      takeName: video.castingData?.takeName || video.takeName,
      talent: video.castingData?.talent || video.talent,
      s3Url: video.s3Url,
      s3Key: video.s3Key,
      imported: true,
      liked: video.castingData?.liked || video.liked,
      comments: video.castingData?.comments || video.comments,
    }));

    // Add files to the project
    setFiles(prev => {
      // Don't add duplicates
      const existingKeys = new Set(prev.map(f => f.s3Key));
      const uniqueNewFiles = newFiles.filter(f => !existingKeys.has(f.s3Key));
      const updatedFiles = [...prev, ...uniqueNewFiles];
      
      // Save to database immediately if using MongoDB
      if (useDatabase && currentDbProject) {
        // Strip analysisData to prevent 413 Content Too Large
        const filesWithoutAnalysis = updatedFiles.map(({ analysisData, ...file }) => file);
        const clipsWithoutAnalysis = clips.map(({ analysisData, ...clip }) => clip);
        saveProject(currentDbProject._id, {
          files: filesWithoutAnalysis,
          clips: clipsWithoutAnalysis,
          renders: renderedVideos,
          analysisResults,
        }).then(() => {
          console.log('💾 Saved imported files to MongoDB');
        });
      }
      
      return updatedFiles;
    });
    
    // Clear pending import data
    setPendingImportData(null);
    setShowImportChoice(false);

    const projectDesc = createNewProject ? fullProjectName : currentProject;
    toast({
      title: "Videos imported",
      description: `${newFiles.length} video${newFiles.length > 1 ? 's' : ''} imported to ${projectDesc}`,
    });

    // Auto-analyze imported videos (get transcripts)
    const videoFiles = newFiles.filter(f => f.fileType === 'video' && f.s3Url);

    if (videoFiles.length > 0) {
      console.log(`🎬 Auto-analyzing ${videoFiles.length} imported videos...`);
      // Pass file objects directly to avoid stale state issues
      handleAnalyze(videoFiles);
    }
  };

  // Handle file selection
  const handleFileSelect = (fileId: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    setSelectedFiles(new Set(files.map(f => f.id)));
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedFiles(new Set());
  };

  // Handle AI asset generation (actual implementation)
  const executeAIGenerate = async (
    type: 'image' | 'video' | 'music',
    prompt: string,
    options: { aspectRatio?: string; loop?: boolean; duration?: number; intensity?: string; format?: string }
  ) => {
    try {
      let endpoint: string;
      let body: Record<string, unknown>;

      if (type === 'image') {
        endpoint = '/api/generate-image';
        body = { prompt, aspectRatio: options.aspectRatio };
      } else if (type === 'video') {
        endpoint = '/api/generate-video';
        body = { prompt, aspectRatio: options.aspectRatio, loop: options.loop };
      } else {
        endpoint = '/api/generate-music';
        body = {
          prompt,
          duration: options.duration || 30,
          intensity: options.intensity || 'medium',
          format: options.format || 'mp3',
        };
      }

      // Get auth token for credit-protected endpoints
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start generation');
      }

      const data = await response.json();

      setAiGeneratingJob({
        jobId: data.jobId,
        type,
        status: 'processing',
        progress: 0,
      });

      const typeLabel = type === 'image' ? 'Image' : type === 'video' ? 'Video' : 'Music';
      const timeEstimate = type === 'video'
        ? 'This may take 1-3 minutes...'
        : type === 'music'
        ? 'This typically takes 30-60 seconds...'
        : 'This typically takes 10-20 seconds...';

      toast({
        title: `${typeLabel} generation started`,
        description: timeEstimate,
      });

      // Start polling for completion
      pollAIGenerationStatus(data.jobId, type, prompt, options.duration);

    } catch (error: any) {
      console.error('AI generation error:', error);
      toast({
        title: 'Generation failed',
        description: error.message || 'Failed to start AI generation',
        variant: 'destructive',
      });
      setAiGeneratingJob(null);
    }
  };

  // Handle AI asset generation - wrapper with credit confirmation
  const handleAIGenerate = (
    type: 'image' | 'video' | 'music',
    prompt: string,
    options: { aspectRatio?: string; loop?: boolean; duration?: number; intensity?: string; format?: string }
  ) => {
    // Determine operation type and name based on generation type
    const operationMap: Record<string, { operation: CreditOperation; name: string }> = {
      image: { operation: CREDIT_OPERATIONS.IMAGE_GENERATION, name: 'Image Generation' },
      video: { operation: CREDIT_OPERATIONS.VIDEO_GENERATION, name: 'Video Generation' },
      music: { operation: CREDIT_OPERATIONS.MUSIC_GENERATION, name: 'Music Generation' },
    };

    const { operation, name } = operationMap[type];

    // Check credits and confirm before proceeding
    requireCreditConfirmation(operation, name, () => {
      executeAIGenerate(type, prompt, options);
    });
  };

  // Poll AI generation job status
  const pollAIGenerationStatus = async (jobId: string, type: 'image' | 'video' | 'music', prompt: string, duration?: number) => {
    const maxAttempts = type === 'video' ? 180 : type === 'music' ? 90 : 60; // 15min for video, 7.5min for music, 5min for image
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/api/generate-asset/status/${jobId}`);
        const data = await response.json();

        if (data.status === 'completed' && data.assetUrl) {
          // Success! Add to project files
          const typeLabel = type === 'image' ? 'Image' : type === 'video' ? 'Video' : 'Music';
          const fileType = type === 'video' ? 'video' : type === 'music' ? 'audio' : 'image';

          const newFile: ProjectFile = {
            id: `ai-${jobId}`,
            name: `AI ${typeLabel} - ${prompt.substring(0, 30)}...`,
            fileType: fileType as 'video' | 'image' | 'audio',
            s3Url: data.assetUrl,
            s3Key: data.s3Key,
            duration: type === 'video' ? 5 : type === 'music' ? (duration || data.duration || 30) : 0,
            thumbnailUrl: type === 'image' ? data.assetUrl : undefined,
            isAIGenerated: true,
            aiGenerationPrompt: prompt,
            aiAssetType: type,
            // AI images need width/height for Lambda validation
            width: type === 'image' ? (data.width || 1024) : undefined,
            height: type === 'image' ? (data.height || 1024) : undefined,
          };

          setFiles(prev => [...prev, newFile]);
          setAiGeneratingJob({ jobId, type, status: 'completed', progress: 100, assetUrl: data.assetUrl });

          toast({
            title: `${typeLabel} generated!`,
            description: type === 'music'
              ? 'Your AI music has been added to the project bin. Drag it to the audio track!'
              : 'Your AI asset has been added to the project bin.',
          });

          // Clear the job after a delay so user sees completion message
          setTimeout(() => setAiGeneratingJob(null), 3000);
          return;
        }

        if (data.status === 'failed') {
          setAiGeneratingJob({ jobId, type, status: 'failed', progress: 0 });
          toast({
            title: 'Generation failed',
            description: data.error || 'AI generation failed. Please try again.',
            variant: 'destructive',
          });
          return;
        }

        // Still processing - update progress and continue polling
        setAiGeneratingJob({
          jobId,
          type,
          status: data.status,
          progress: data.progress || 0,
        });

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          setAiGeneratingJob({ jobId, type, status: 'failed', progress: 0 });
          toast({
            title: 'Generation timeout',
            description: 'The generation is taking too long. Please try again.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Polling error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        }
      }
    };

    // Start polling
    setTimeout(poll, 2000);
  };
  // Background removal handlers extracted to useBackgroundRemoval hook


  // Handle analyze - accepts either file IDs (string[]) or file objects directly
  const handleAnalyze = async (fileIdsOrFiles: string[] | any[]) => {
    console.log('🔍 ANALYZE called with:', fileIdsOrFiles);

    if (fileIdsOrFiles.length === 0) {
      toast({
        title: 'No Files Selected',
        description: 'Please select videos to analyze',
        variant: 'destructive',
      });
      return;
    }

    // Determine if we received IDs or file objects
    const isFileObjects = typeof fileIdsOrFiles[0] === 'object';

    let analyzableFiles: any[];
    if (isFileObjects) {
      // Direct file objects passed (from auto-analyze)
      analyzableFiles = (fileIdsOrFiles as any[]).filter(f =>
        f.fileType === 'video' ||
        (f.fileType === 'audio' && f.linkedVideoId)
      );
    } else {
      // File IDs passed - look them up in current state
      const selectedFiles = files.filter(f => (fileIdsOrFiles as string[]).includes(f.id));
      analyzableFiles = selectedFiles.filter(f =>
        f.fileType === 'video' ||
        (f.fileType === 'audio' && f.linkedVideoId)
      );
    }

    if (analyzableFiles.length === 0) {
      toast({
        title: 'No Analyzable Files Selected',
        description: 'Please select video files or linked audio files for analysis',
        variant: 'destructive',
      });
      return;
    }

    setIsAnalyzing(true);
    const analysisJobs: string[] = [];

    try {
      // Start analysis for each selected video or linked audio file
      for (const file of analyzableFiles) {
        console.log('🔍 Processing file:', file.id, file.name, 'has s3Url:', !!file.s3Url);
        if (file.s3Url) {
          console.log(`🎬 Starting analysis for: ${file.name}`);
          
          const response = await fetch('/api/analyze', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              video_url: file.s3Url,
              video_id: file.id,
              modules: ['transcribe', 'background', 'visual']
            })
          });

          console.log('🔍 API Response status:', response.status, response.ok);
          if (response.ok) {
            const result = await response.json();
            console.log('🔍 API Response result:', result);
            analysisJobs.push(result.analysis_id);
            console.log(`✅ Analysis started: ${result.analysis_id}`);
            
            // Mark file as analyzing
            console.log('🔍 Setting analysis status for file:', file.id, 'to starting with jobId:', result.analysis_id);
            setFiles(prev => {
              const updated = prev.map(f => 
                f.id === file.id 
                  ? { ...f, analysisStatus: 'starting', analysisJobId: result.analysis_id }
                  : f
              );
              console.log('🔍 Updated files state:', updated.find(f => f.id === file.id));
              return updated;
            });
          } else {
            console.error(`❌ Failed to start analysis for ${file.name}`, response);
            // Mark file as failed
            setFiles(prev => prev.map(f => 
              f.id === file.id 
                ? { ...f, analysisStatus: 'failed' }
                : f
            ));
          }
        }
      }

      if (analysisJobs.length > 0) {
        toast({
          title: 'Analysis Started',
          description: `Analyzing ${analysisJobs.length} video${analysisJobs.length > 1 ? 's' : ''}...`,
        });

        // Poll for results
        pollAnalysisResults(analysisJobs);
      } else {
        toast({
          title: 'No Videos to Analyze',
          description: 'Please select video files for analysis',
          variant: 'destructive',
        });
      }

    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: 'Analysis Failed',
        description: 'Failed to start video analysis',
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const pollAnalysisResults = async (analysisIds: string[]) => {
    console.log('🔄 Starting analysis polling for:', analysisIds);
    
    const pollInterval = setInterval(async () => {
      let allCompleted = true;

      for (const analysisId of analysisIds) {
        // Check current status
        const currentStatus = analysisResults[analysisId]?.status;
        console.log(`📊 Checking analysis ${analysisId}, current status:`, currentStatus);
        
        if (currentStatus === 'completed') {
          console.log(`✅ Already completed: ${analysisId}`);
          continue;
        }

        try {
          const response = await fetch(`/api/analyze/status/${analysisId}`);
          if (response.ok) {
            const result = await response.json();
            console.log(`📥 Analysis API response for ${analysisId}:`, result);
            
            setAnalysisResults(prev => {
              const updated = {
                ...prev,
                [analysisId]: result
              };
              console.log('💾 Updated analysisResults:', updated);
              return updated;
            });

            // Update file analysis status
            if (result.status === 'completed') {
              console.log(`✅ Analysis complete: ${analysisId}`, result);
              console.log('🔍 Updating file analysis status to completed');
              
              setFiles(prev => {
                const updated = prev.map(f => {
                  if (f.analysisJobId === analysisId) {
                    console.log(`📝 Updating file ${f.name} analysis to completed`);
                    return { ...f, analysisStatus: 'completed', analysisJobId: undefined, analysisData: result.results };
                  }
                  return f;
                });
                return updated;
              });
              
              toast({
                title: 'Analysis Complete',
                description: `Video analysis finished`,
              });
            } else if (result.status === 'failed') {
              setFiles(prev => prev.map(f => 
                f.analysisJobId === analysisId 
                  ? { ...f, analysisStatus: 'failed', analysisJobId: undefined }
                  : f
              ));
            } else if (result.status === 'processing') {
              setFiles(prev => prev.map(f => 
                f.analysisJobId === analysisId 
                  ? { ...f, analysisStatus: 'processing' }
                  : f
              ));
              allCompleted = false;
            } else {
              allCompleted = false;
            }
          }
        } catch (error) {
          console.error(`Polling error for ${analysisId}:`, error);
          setFiles(prev => prev.map(f => 
            f.analysisJobId === analysisId 
              ? { ...f, analysisStatus: 'failed', analysisJobId: undefined }
              : f
          ));
        }
      }

      if (allCompleted) {
        console.log('🎉 All analysis jobs completed, stopping polling');
        clearInterval(pollInterval);
      } else {
        console.log('⏳ Still waiting for analysis to complete');
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 30 minutes (analysis can take a long time)
    setTimeout(() => {
      clearInterval(pollInterval);
      // Mark any remaining jobs as failed due to timeout
      for (const analysisId of analysisIds) {
        if (analysisResults[analysisId]?.status !== 'completed') {
          setFiles(prev => prev.map(f => 
            f.analysisJobId === analysisId 
              ? { ...f, analysisStatus: 'failed', analysisJobId: undefined }
              : f
          ));
        }
      }
    }, 1800000); // 30 minutes
  };
  // Script Analysis handlers extracted to useScriptAnalysis hook

  // Plan Mode handlers extracted to usePlanMode hook


  // Handle drag start from project bin
  const handleDragStart = (file: ProjectFile) => {
    setDraggedFile(file);
  };

  // Handle clip drop to timeline
  const handleClipDrop = (fileId: string) => {
    const file = files.find(f => f.id === fileId);
    if (!file) return;

    const newClips: ProjectClip[] = [];
    
    // Generate appropriate prompt based on file type and enhancement
    let clipPrompt = '';
    
    if (file.enhanced) {
      // Re-imported/enhanced files should start with a clean prompt
      // The enhancementPrompt contains the previous master prompt - don't re-use it
      clipPrompt = '';
    } else if (file.layerType === 'background') {
      // Files specifically uploaded as backgrounds
      clipPrompt = 'background';
    } else if (file.layerType === 'audio') {
      clipPrompt = file.linkedVideoId 
        ? 'Original audio track from green screen video'
        : 'Use this audio track';
    } else if (file.fileType === 'video') {
      // Check if this is a green screen processed video
      if (file.hasGreenScreen) {
        clipPrompt = 'chromaKey #97eca9';
      } else {
        clipPrompt = '';
      }
    } else if (file.fileType === 'audio') {
      clipPrompt = 'Use this audio track';
    } else if (file.fileType === 'image') {
      clipPrompt = 'Use this image as background or overlay';
    } else if (file.fileType === 'srt') {
      clipPrompt = 'Use this subtitle track';
    }

    // Determine layerType - images default to 'main' if not explicitly set as background
    let clipLayerType = file.layerType;
    if (file.fileType === 'image' && !clipLayerType) {
      clipLayerType = 'main'; // Images default to main track (can be fullframe or overlay)
    }

    const newClip: ProjectClip = {
      id: Math.random().toString(36).substr(2, 9),
      sourceFileId: file.id,
      position: clips.length,
      prompt: clipPrompt,
      duration: file.duration,
      name: file.hasGreenScreen ? `${file.name} (Green Screen)` : file.name,
      thumbnailUrl: file.thumbnailUrl,
      fileType: file.fileType,
      analysisData: file.analysisData,
      layerType: clipLayerType,
      linkedPairId: file.linkedPairId,
      linkedMaskId: file.linkedMaskId,
      isLinked: !!(file.linkedAudioId || file.linkedVideoId),
      inTime: 0, // Default to start of file
      outTime: file.duration || undefined, // Default to end of file if duration is available
      // Store URL directly on clip for render
      s3Url: file.greenScreenUrl || file.s3Url,
      s3Key: file.s3Key || file.greenScreenKey,
      // Image defaults - fullframe mode, 5 second duration
      imageMode: file.fileType === 'image' ? 'fullframe' : undefined,
      imageDuration: file.fileType === 'image' ? 5 : undefined,
    };
    
    newClips.push(newClip);

    // Check if this file has a linked mask (for foreground mask pairs)
    if (file.linkedMaskId) {
      const maskFile = files.find(f => f.id === file.linkedMaskId);
      if (maskFile) {
        // Add the mask clip right after the main clip
        const maskClip: ProjectClip = {
          id: Math.random().toString(36).substr(2, 9),
          sourceFileId: maskFile.id,
          position: clips.length + 1,
          prompt: 'luma matte for compositing',
          duration: maskFile.duration,
          name: maskFile.displayName || maskFile.name,
          thumbnailUrl: maskFile.thumbnailUrl,
          fileType: maskFile.fileType,
          layerType: 'mask',
          linkedPairId: file.linkedPairId,
          isLinked: true,
        };
        newClips.push(maskClip);
      }
    }
    
    // Check if this is a mask file being added - also add its linked original
    if (file.layerType === 'mask' && file.linkedVideoId) {
      const originalFile = files.find(f => f.id === file.linkedVideoId);
      if (originalFile && !clips.some(c => c.sourceFileId === originalFile.id)) {
        // Add the original video clip if not already in timeline
        const originalClip: ProjectClip = {
          id: Math.random().toString(36).substr(2, 9),
          sourceFileId: originalFile.id,
          position: clips.length,
          prompt: '',
          duration: originalFile.duration,
          name: originalFile.displayName || originalFile.name,
          thumbnailUrl: originalFile.thumbnailUrl,
          fileType: originalFile.fileType,
          layerType: 'main',
          linkedPairId: file.linkedPairId,
          isLinked: true,
        };
        newClips.unshift(originalClip); // Add original before mask
      }
    }

    setClips(prev => [...prev, ...newClips]);

    const message = newClips.length > 1
      ? `${file.name} and linked audio added to timeline`
      : `${file.name} added to timeline`;

    toast({
      title: "Clips added",
      description: message,
    });
  };

  // Handle clip drop with options (for luma matte files)
  const handleClipDropWithOptions = (fileId: string, options: { includeMask?: boolean; videoOnly?: boolean }) => {
    const file = files.find(f => f.id === fileId);
    if (!file) return;

    const newClips: ProjectClip[] = [];

    // Generate appropriate prompt based on file type
    let clipPrompt = '';

    if (file.fileType === 'video') {
      clipPrompt = '';
    }

    const newClip: ProjectClip = {
      id: Math.random().toString(36).substr(2, 9),
      sourceFileId: file.id,
      position: clips.length,
      prompt: clipPrompt,
      duration: file.duration,
      name: file.displayName || file.name,
      thumbnailUrl: file.thumbnailUrl,
      fileType: file.fileType,
      analysisData: file.analysisData,
      layerType: 'main',
      linkedPairId: file.linkedPairId,
      linkedMaskId: file.linkedMaskId,
      isLinked: false,
      inTime: 0,
      outTime: file.duration || undefined,
      // Store URL directly on clip for render
      s3Url: file.greenScreenUrl || file.s3Url,
      s3Key: file.s3Key || file.greenScreenKey,
    };

    newClips.push(newClip);

    // Only add the linked mask if includeMask is true
    if (options.includeMask && file.linkedMaskId) {
      const maskFile = files.find(f => f.id === file.linkedMaskId);
      if (maskFile) {
        const maskClip: ProjectClip = {
          id: Math.random().toString(36).substr(2, 9),
          sourceFileId: maskFile.id,
          position: clips.length + 1,
          prompt: 'luma matte for compositing',
          duration: maskFile.duration,
          name: maskFile.displayName || maskFile.name,
          thumbnailUrl: maskFile.thumbnailUrl,
          fileType: maskFile.fileType,
          layerType: 'mask',
          linkedPairId: file.linkedPairId,
          isLinked: true,
        };
        newClips.push(maskClip);
      }
    }

    setClips(prev => [...prev, ...newClips]);

    const message = options.includeMask && newClips.length > 1
      ? `${file.name} with luma matte added to timeline`
      : `${file.name} added to timeline (video only)`;

    toast({
      title: "Clips added",
      description: message,
    });
  };

  // Handle clip removal
  const handleClipRemove = (clipId: string) => {
    setClips(prev => prev.filter(c => c.id !== clipId));
  };

  // Handle clip preview
  const handlePreviewClip = (clipId: string) => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip) return;
    
    const file = files.find(f => f.id === clip.sourceFileId);
    if (!file) return;
    
    setPreviewClip({ clip, file });
  };

  // Handle file removal from project bin
  const handleFileRemove = (fileId: string) => {
    if (!window.confirm('Are you sure you want to remove this file from the project bin?')) {
      return;
    }

    // Remove file from files list
    const updatedFiles = files.filter(f => f.id !== fileId);
    setFiles(updatedFiles);
    
    // Remove from selected files if selected
    const updatedSelectedFiles = new Set(selectedFiles);
    updatedSelectedFiles.delete(fileId);
    setSelectedFiles(updatedSelectedFiles);

    // Remove any clips using this file
    const updatedClips = clips.filter(c => c.fileId !== fileId);
    setClips(updatedClips);

    // Save to localStorage
    localStorage.setItem(`project_${currentProject}_files`, JSON.stringify(updatedFiles));
    localStorage.setItem(`project_${currentProject}_clips`, JSON.stringify(updatedClips));

    toast({
      title: "File removed",
      description: "File has been removed from the project bin",
    });
  };

  // Handle clip reordering
  const handleClipReorder = (clipId: string, newPosition: number) => {
    setClips(prev => {
      const clipIndex = prev.findIndex(c => c.id === clipId);
      if (clipIndex === -1) return prev;

      const newClips = [...prev];
      const [removed] = newClips.splice(clipIndex, 1);
      newClips.splice(newPosition, 0, removed);

      // Update positions
      return newClips.map((clip, index) => ({
        ...clip,
        position: index,
      }));
    });
  };

  // Handle prompt change
  const handlePromptChange = (clipId: string, prompt: string) => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, prompt } : clip
      )
    );
  };
  
  // Handle time change
  const handleTimeChange = (clipId: string, inTime: number | undefined, outTime: number | undefined) => {
    setClips(prev =>
      prev.map(clip => {
        if (clip.id === clipId) {
          const updatedClip = { ...clip, inTime, outTime };
          // Update duration if both times are provided
          if (inTime !== undefined && outTime !== undefined) {
            updatedClip.duration = parseFloat((outTime - inTime).toFixed(3));
          }
          return updatedClip;
        }
        return clip;
      })
    );
  };

  // Handle title change (Quick Render explicit field)
  const handleTitleChange = (clipId: string, titleText: string | undefined, titlePosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | undefined, titleFont?: string, titleColor?: string) => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, titleText, titlePosition, titleFont, titleColor } : clip
      )
    );
  };

  // Handle volume change (Quick Render explicit field)
  const handleVolumeChange = (clipId: string, volume: number) => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, volume } : clip
      )
    );
  };

  // Handle transition change (Quick Render explicit field)
  const handleTransitionChange = (clipId: string, transitionOut: 'none' | 'fade' | 'fadeSlow' | 'fadeFast' | 'wipeLeft' | 'wipeRight' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown') => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, transitionOut } : clip
      )
    );
  };

  // Handle motion effect change (Ken Burns style zoom/pan)
  const handleEffectChange = (clipId: string, effect: 'none' | 'zoomIn' | 'zoomOut' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown' | 'zoomInSlow' | 'zoomOutSlow') => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, effect: effect === 'none' ? undefined : effect } : clip
      )
    );
  };

  // Handle image settings change (Quick Render explicit field)
  const handleImageSettingsChange = (clipId: string, imageMode: 'fullframe' | 'overlay', imagePosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | undefined, imageDuration: number, imageScale: number) => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, imageMode, imagePosition, imageDuration, imageScale } : clip
      )
    );
  };

  // Handle captions change (Quick Render explicit field)
  const handleCaptionsChange = (clipId: string, showCaptions: boolean, captionStyle: 'default' | 'outline' | 'box' | 'minimal', captionFont?: string, captionBackground?: string, captionFontColor?: string, captionStrokeColor?: string) => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, showCaptions, captionStyle, captionFont, captionBackground, captionFontColor, captionStrokeColor } : clip
      )
    );
  };

  // Handle editing transcript/reference text for a clip (used for captions)
  const handleReferenceTextChange = (clipId: string, referenceText: string) => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, referenceText } : clip
      )
    );
  };

  // Handle editing transcript segment on a source file (persists to file's analysisData)
  // This allows corrections made in Select Sections to apply to the full video
  const handleTranscriptEdit = (fileId: string, segmentIndex: number, newText: string) => {
    setFiles(prev =>
      prev.map(file => {
        if (file.id !== fileId) return file;

        // Deep clone the file's analysisData to avoid mutation
        const updatedAnalysisData = file.analysisData ? { ...file.analysisData } : { transcript: { segments: [] } };

        if (updatedAnalysisData.transcript?.segments && updatedAnalysisData.transcript.segments[segmentIndex]) {
          // Clone segments array
          const updatedSegments = [...updatedAnalysisData.transcript.segments];
          updatedSegments[segmentIndex] = {
            ...updatedSegments[segmentIndex],
            text: newText,
          };
          updatedAnalysisData.transcript = {
            ...updatedAnalysisData.transcript,
            segments: updatedSegments,
          };
        }

        console.log(`📝 Updated transcript segment ${segmentIndex} for file "${file.name}": "${newText.substring(0, 50)}..."`);
        return { ...file, analysisData: updatedAnalysisData };
      })
    );
  };

  // Handle reassigning speaker on a transcript segment (persists to file's analysisData)
  const handleSpeakerEdit = (fileId: string, segmentIndex: number, newSpeaker: string) => {
    setFiles(prev =>
      prev.map(file => {
        if (file.id !== fileId) return file;

        const updatedAnalysisData = file.analysisData ? { ...file.analysisData } : { transcript: { segments: [] } };

        if (updatedAnalysisData.transcript?.segments && updatedAnalysisData.transcript.segments[segmentIndex]) {
          const updatedSegments = [...updatedAnalysisData.transcript.segments];
          updatedSegments[segmentIndex] = {
            ...updatedSegments[segmentIndex],
            speaker: newSpeaker,
          };
          updatedAnalysisData.transcript = {
            ...updatedAnalysisData.transcript,
            segments: updatedSegments,
          };
        }

        console.log(`🔄 Reassigned speaker on segment ${segmentIndex} for file "${file.name}" to ${newSpeaker}`);
        console.log(`🔄 New segments:`, updatedAnalysisData.transcript.segments.map((s: any) => s.speaker));
        return { ...file, analysisData: updatedAnalysisData };
      })
    );
  };

  // Debug: Log when files state changes
  React.useEffect(() => {
    console.log('🗂️ VideoEnhancerNLE files state changed, file count:', files.length);
  }, [files]);

  // Handle changing the layer type (main/background) for clips
  const handleLayerTypeChange = (clipId: string, layerType: 'main' | 'background') => {
    setClips(prev =>
      prev.map(clip =>
        clip.id === clipId ? { ...clip, layerType } : clip
      )
    );
  };

  // Handle stacking a clip on top of another (multi-track overlay)
  const handleStackClip = (draggedClipId: string, targetClipId: string) => {
    setClips(prev => {
      const draggedClip = prev.find(c => c.id === draggedClipId);
      const targetClip = prev.find(c => c.id === targetClipId);
      if (!draggedClip || !targetClip) return prev;

      // Prevent circular stacking
      if (targetClip.stackedOn === draggedClipId) return prev;

      // Find the highest track number to determine the new track
      const targetTrack = targetClip.track || 0;
      const existingOverlays = prev.filter(c => c.stackedOn === targetClipId);
      const maxOverlayTrack = existingOverlays.reduce((max, c) => Math.max(max, c.track || 0), targetTrack);
      const newTrack = maxOverlayTrack + 1;

      return prev.map(clip => {
        if (clip.id === draggedClipId) {
          // Get target clip's timing info
          const targetInTime = targetClip.inTime || 0;
          const targetOutTime = targetClip.outTime || targetClip.duration;

          return {
            ...clip,
            isOverlay: true,
            track: newTrack,
            stackedOn: targetClipId,
            overlayPosition: 'center' as const,
            overlayScale: 1.0,
            volume: 0, // Mute overlay audio by default
            // Sync timing with target clip
            inTime: clip.inTime !== undefined ? clip.inTime : 0,
            outTime: clip.outTime !== undefined ? clip.outTime : clip.duration,
          };
        }
        return clip;
      });
    });

    toast({
      title: "Clip stacked",
      description: "Clip has been stacked as an overlay. Adjust position and scale in the overlay settings.",
    });
  };

  // Handle unstacking a clip (move back to main track)
  const handleUnstackClip = (clipId: string) => {
    setClips(prev =>
      prev.map(clip => {
        if (clip.id === clipId) {
          return {
            ...clip,
            isOverlay: false,
            track: 0,
            stackedOn: undefined,
            overlayPosition: undefined,
            overlayScale: undefined,
            volume: 1.0, // Restore audio
          };
        }
        return clip;
      })
    );

    toast({
      title: "Clip unstacked",
      description: "Clip has been moved back to the main track.",
    });
  };

  // Handle overlay settings change (position and scale)
  const handleOverlaySettingsChange = (clipId: string, position: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight', scale: number) => {
    setClips(prev =>
      prev.map(clip => {
        if (clip.id === clipId) {
          return {
            ...clip,
            overlayPosition: position,
            overlayScale: position === 'center' ? 1.0 : scale,
          };
        }
        return clip;
      })
    );
  };

  // Handle inserting video segment from transcript
  const handleInsertText = (text: string, file?: ProjectFile, startTime?: number, endTime?: number, referenceText?: string) => {
    if (file && startTime !== undefined && endTime !== undefined) {
      const segmentDuration = endTime - startTime;
      const newClips: ProjectClip[] = [];
      
      // Check if this is an audio file with a linked video (green screen)
      if (file.fileType === 'audio' && file.linkedVideoId) {
        // Find the linked green screen video
        const linkedVideo = files.find(f => f.id === file.linkedVideoId);
        if (linkedVideo) {
          // Add the green screen video clip with the same time range
          const videoClip: ProjectClip = {
            id: Math.random().toString(36).substr(2, 9),
            name: `${linkedVideo.name} (${startTime.toFixed(1)}s-${endTime.toFixed(1)}s)`,
            fileType: 'video',
            thumbnailUrl: linkedVideo.thumbnailUrl || '',
            duration: segmentDuration,
            sourceFileId: linkedVideo.id,
            prompt: text, // The trim instruction
            referenceText: referenceText, // Store reference text separately for UI display
            analysisData: linkedVideo.analysisData,
            layerType: 'main',
            linkedPairId: file.linkedPairId,
            isLinked: true,
            position: clips.length,
            inTime: startTime,
            outTime: endTime,
          };
          newClips.push(videoClip);
          
          // Add the audio clip
          const audioClip: ProjectClip = {
            id: Math.random().toString(36).substr(2, 9),
            name: `${file.name} (${startTime.toFixed(1)}s-${endTime.toFixed(1)}s)`,
            fileType: 'audio',
            thumbnailUrl: file.thumbnailUrl || 'AUDIO_ICON',
            duration: segmentDuration,
            sourceFileId: file.id,
            prompt: text, // The trim instruction
            referenceText: referenceText,
            analysisData: file.analysisData,
            layerType: 'audio',
            linkedPairId: file.linkedPairId,
            isLinked: true,
            position: clips.length + 1,
            inTime: startTime,
            outTime: endTime,
          };
          newClips.push(audioClip);
        }
      } else {
        // Regular file (video or audio without linked video)
        const clip: ProjectClip = {
          id: Date.now().toString(),
          name: `${file.name} (${startTime.toFixed(1)}s-${endTime.toFixed(1)}s)`,
          fileType: file.fileType || 'video',
          thumbnailUrl: file.thumbnailUrl || '',
          duration: segmentDuration,
          sourceFileId: file.id,
          prompt: text, // Use the clean trim instruction without quotes or reference text
          referenceText: referenceText, // Store reference text separately for UI display
          analysisData: file.analysisData,
          inTime: startTime,
          outTime: endTime,
        };
        newClips.push(clip);
      }

      setClips(prev => [...prev, ...newClips]);
      
      const message = newClips.length > 1 
        ? "Green screen video and audio segments added to timeline"
        : "Video segment added to timeline";
      
      toast({
        title: message,
        description: `${segmentDuration.toFixed(1)}s clip: "${text.substring(0, 40)}${text.length > 40 ? '...' : ''}"`,
      });
    } else {
      // Fallback: create text overlay clip if no timing info
      const textClip: ProjectClip = {
        id: Date.now().toString(),
        name: `Text: ${text.substring(0, 30)}${text.length > 30 ? '...' : ''}`,
        fileType: 'text',
        thumbnailUrl: '',
        duration: 5, // Default 5 seconds for text
        sourceFileId: '', // Text clips don't have source files
        prompt: text,
      };

      setClips(prev => [...prev, textClip]);
      
      toast({
        title: "Text added to timeline",
        description: `"${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`,
      });
    }
  };

  // Generate master prompt from all clips - SIMPLIFIED VERSION
  const generateMasterPrompt = () => {
    // Separate clips by type - EXCLUDE luma matte clips from video sequence
    const videoClips = clips.filter(c =>
      c.fileType === 'video' &&
      c.layerType !== 'mask' &&
      !c.name?.includes('Luma Matte') &&
      !c.name?.includes('foreground_mask') &&
      !c.name?.includes('_mask')
    );
    const lumaMatteClips = clips.filter(c =>
      c.fileType === 'video' && (
        c.layerType === 'mask' ||
        c.name?.includes('Luma Matte') ||
        c.name?.includes('foreground_mask') ||
        c.name?.includes('_mask')
      )
    );
    const audioClips = clips.filter(c => c.fileType === 'audio');
    const imageClips = clips.filter(c => c.fileType === 'image');

    // Build natural language prompt like NSM validation sets
    // Key: clip-specific instructions go in assets_metadata.comment, not here
    // This prompt just handles overall arrangement and global instructions

    // Helper to simplify clip names
    const simplifyName = (name: string) => name.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

    // Build clip arrangement string (trim info is in assets_metadata.comment)
    const clipNames = videoClips.map(clip => `"${simplifyName(clip.name)}"`);

    let prompt = '';

    // Include script content if available - this tells the Lambda what the creative direction is
    if (scriptContent) {
      prompt = `SCRIPT/CREATIVE DIRECTION:\n${scriptContent}\n\nEdit the following clips to match this script. Use the transcript timings in each clip's comment to select the right portions. `;
    }

    // Video arrangement instruction - simple list
    if (clipNames.length > 0) {
      if (!scriptContent) {
        prompt = `Generate video that edits these ${clipNames.length} clips together in sequence: ${clipNames.join(', ')}. `;
      } else {
        prompt += `Available clips: ${clipNames.join(', ')}. `;
      }
    }

    // Check if all videos have green screen
    const allHaveGreenScreen = videoClips.length > 0 && videoClips.every(c => c.prompt?.includes('chromaKey'));
    if (allHaveGreenScreen) {
      prompt += 'Apply chromaKey to remove green background from all videos. ';
    }

    // Add audio instructions
    if (audioClips.length > 0) {
      audioClips.forEach((clip) => {
        const audioName = simplifyName(clip.name);
        prompt += `Add "${audioName}" as audio track. `;
      });
    }

    // Background images
    const backgroundImages = imageClips.filter(clip =>
      clip.layerType === 'background' || clip.prompt?.includes('background')
    );
    if (backgroundImages.length > 0) {
      const bgName = simplifyName(backgroundImages[0].name);
      prompt += `Use "${bgName}" as background. `;
    }

    // Luma matte info
    if (lumaMatteClips.length > 0) {
      lumaMatteClips.forEach((clip) => {
        const matteName = simplifyName(clip.name);
        prompt += `Apply luma matte "${matteName}" for transparency. `;
      });
    }

    return prompt.trim() || 'Create video from provided clips';
  };

  // Update editable master prompt when clips or script change
  useEffect(() => {
    if (!isEditingMasterPrompt) {
      setEditableMasterPrompt(generateMasterPrompt());
    }
  }, [clips, isEditingMasterPrompt, scriptContent]);

  // Original complex generation code (keeping for reference but unused)
  const generateMasterPromptOLD = () => {
    // Always collect ALL user prompts first (edited prompts from timeline)
    const allUserPrompts = clips
      .map((clip, index) => {
        if (!clip.prompt) return null;
        // Include layer type information for better context
        const layerInfo = clip.layerType && clip.layerType !== 'main' 
          ? ` [${clip.layerType} layer]` 
          : '';
        return { clip, prompt: clip.prompt, layerInfo, index };
      })
      .filter(Boolean);

    // Check if this is a green screen compositing workflow
    // DISABLED - Lambda should handle this automatically based on the assets
    const hasGreenScreen = false;
    
    if (hasGreenScreen && clips.length >= 2) {
      // Find ALL green screen clips
      const greenScreenClips = clips.filter(clip => 
        clip.name?.toLowerCase().includes('green screen') || 
        clip.prompt?.toLowerCase().includes('chroma key') ||
        clip.prompt?.toLowerCase().includes('green screen')
      );
      
      // Find corresponding audio clips
      const audioClips = clips.filter(clip => 
        clip.fileType === 'audio' || 
        (clip.name?.toLowerCase().includes('original audio') && clip.layerType === 'audio')
      );
      
      // Find background image
      const backgroundClip = clips.find(clip => 
        clip.fileType === 'image' || 
        (clip.layerType === 'background' && clip.fileType === 'image')
      );

      // Debug logging
      console.log('Green screen detection:', {
        greenScreenClips: greenScreenClips.map(c => c.name),
        audioClips: audioClips.map(c => c.name),
        backgroundClip: backgroundClip?.name
      });

      if (greenScreenClips.length > 0 && backgroundClip) {
        let description = `Create a Shotstack timeline with green screen chroma key composition.\n\n`;
        description += `CRITICAL SHOTSTACK TRACK ORDER (tracks array order determines layering):\n`;
        description += `- tracks[0] = TOP LAYER (Foreground): All green screen video clips with chromaKey enabled\n`;
        description += `- tracks[1] = BOTTOM LAYER (Background): Background image that shows through removed green areas\n`;
        description += `- tracks[2+] = AUDIO LAYERS: Audio tracks synced with video segments\n\n`;
        description += `IMPORTANT: The first track in the JSON array is rendered ON TOP. Background must be AFTER video tracks.\n\n`;
        
        description += `FOREGROUND VIDEO CLIPS (All on tracks[0] - the FIRST/TOP track):\n`;
        let currentStart = 0;
        // Only process actual video clips, not audio
        const actualVideoClips = greenScreenClips.filter(clip => 
          clip.fileType === 'video' && !clip.name?.toLowerCase().includes('audio')
        );
        
        actualVideoClips.forEach((clip, index) => {
          // Get the file to access original URL
          const file = files.find(f => f.id === clip.sourceFileId);
          
          // Use explicit in/out times if available, otherwise parse from prompt
          let trimStart = clip.inTime;
          let trimEnd = clip.outTime;
          
          if (trimStart === undefined || trimEnd === undefined) {
            // Fallback to parsing from prompt
            const userPrompt = allUserPrompts.find(p => p.clip.id === clip.id);
            const timeInstruction = userPrompt?.prompt || '';
            const timeMatch = timeInstruction.match(/from ([\d.]+)s to ([\d.]+)s/i);
            
            if (timeMatch) {
              trimStart = parseFloat(timeMatch[1]);
              trimEnd = parseFloat(timeMatch[2]);
            }
          }
          
          if (trimStart !== undefined && trimEnd !== undefined) {
            const duration = parseFloat((trimEnd - trimStart).toFixed(3));
            const roundedStart = parseFloat(currentStart.toFixed(3));
            const roundedTrimStart = parseFloat(trimStart.toFixed(3));
            
            description += `Clip ${index + 1}: "${clip.name}"\n`;
            description += `  - Position in timeline: start=${roundedStart}s\n`;
            description += `  - Source trim: trim=${roundedTrimStart} (take from ${roundedTrimStart}s of source)\n`;
            description += `  - Clip duration: length=${duration}s\n`;
            description += `  - Apply chromaKey to remove green\n`;
            
            currentStart += duration;
          }
        });
        
        description += `\nBACKGROUND IMAGE (tracks[1] - the SECOND/BOTTOM track):\n`;
        const totalDuration = currentStart > 0 ? parseFloat(currentStart.toFixed(3)) : 24; // Use calculated duration or default
        description += `- "${backgroundClip.name}" - full timeline duration (start=0, length=${totalDuration}s)\n\n`;
        
        description += `AUDIO: For each green screen video clip, add an audio track using the matching "(audio)" asset.\n`;
        description += `COPY the EXACT timing from each video clip to its audio:\n`;
        description += `  - If video has trim: 7.4, audio MUST have trim: 7.4\n`;
        description += `  - If video has start: 0, audio MUST have start: 0\n`;
        description += `  - If video has length: 2, audio MUST have length: 2\n`;
        description += `The audio tracks synchronize the sound with the trimmed video sections.\n\n`;
        
        // Only list explicit audio clips if they exist (non-linked audio)
        const explicitAudioClips = audioClips.filter(clip => !clip.isLinked);
        if (explicitAudioClips.length > 0) {
          description += `ADDITIONAL AUDIO TRACKS:\n`;
          currentStart = 0;
          explicitAudioClips.forEach((clip, index) => {
          // Use explicit in/out times if available, otherwise parse from prompt
          let trimStart = clip.inTime;
          let trimEnd = clip.outTime;
          
          if (trimStart === undefined || trimEnd === undefined) {
            // Fallback to parsing from prompt
            const userPrompt = allUserPrompts.find(p => p.clip.id === clip.id);
            const timeInstruction = userPrompt?.prompt || '';
            const timeMatch = timeInstruction.match(/from ([\d.]+)s to ([\d.]+)s/i);
            
            if (timeMatch) {
              trimStart = parseFloat(timeMatch[1]);
              trimEnd = parseFloat(timeMatch[2]);
            }
          }
          
          if (trimStart !== undefined && trimEnd !== undefined) {
            const duration = parseFloat((trimEnd - trimStart).toFixed(3));
            const roundedStart = parseFloat(currentStart.toFixed(3));
            const roundedTrimStart = parseFloat(trimStart.toFixed(3));
            
            description += `Audio ${index + 1}: "${clip.name}"\n`;
            description += `  - Position: start=${roundedStart}s, length=${duration}s\n`;
            description += `  - Source trim: trim=${roundedTrimStart} if needed\n`;
            
            currentStart += duration;
            }
          });
        }
        
        description += `\nIMPORTANT TRACK STRUCTURE:\n`;
        description += `- tracks[0]: ALL green screen video clips with chromaKey enabled\n`;
        description += `- tracks[1]: Background image\n`;
        description += `- tracks[2+]: Audio tracks - one for EACH green screen video\n`;
        description += `  * Use the ORIGINAL video URL (not the green screen URL) as type: "audio"\n`;
        description += `  * Match the trim and length values exactly from the corresponding video clip\n`;
        description += `- Each video clip needs chromaKey settings to remove green\n`;
        
        return description;
      }
    }
    
    // Check if this is an alpha mask compositing workflow
    // DISABLED - Lambda should handle this automatically based on the assets
    const hasAlphaMask = false;
    
    if (hasAlphaMask && clips.length >= 2) {
      // Find the components for alpha mask workflow
      const alphaMaskClip = clips.find(clip => 
        clip.name?.includes('Alpha Mask') || 
        clip.prompt?.toLowerCase().includes('alpha mask')
      );
      const originalVideoClip = clips.find(clip => 
        clip.fileType === 'video' && 
        !clip.name?.includes('Alpha Mask') && 
        !clip.prompt?.toLowerCase().includes('alpha mask')
      );
      const backgroundClip = clips.find(clip => 
        clip.fileType === 'image' || 
        clip.prompt?.toLowerCase().includes('background')
      );

      if (alphaMaskClip && originalVideoClip) {
        let description = `Composite the person from "${originalVideoClip.name}" over `;
        
        if (backgroundClip) {
          description += `the "${backgroundClip.name}" background `;
        } else {
          description += `a background `;
        }
        
        description += `by applying the "${alphaMaskClip.name}" for transparency. `;
        description += `Create a layered composition where the background is behind and the person appears in front with their background removed using the alpha mask.\n\n`;
        
        // Add ALL user customizations
        const allCustomEdits = allUserPrompts
          .map(p => `${p.clip.name}${p.layerInfo}: ${p.prompt}`)
          .join('\n');
        
        if (allCustomEdits) {
          description += `USER INSTRUCTIONS FOR ALL CLIPS:\n${allCustomEdits}\n`;
        }
        
        return description;
      }
    }
    
    // For all other workflows - separate background images from video sequence
    const backgroundImages = clips.filter(clip => {
      const file = files.find(f => f.id === clip.sourceFileId);
      return file?.fileType === 'image';
    });
    
    const videoAudioClips = clips.filter(clip => {
      const file = files.find(f => f.id === clip.sourceFileId);
      return file?.fileType !== 'image';
    });
    
    // Build natural language prompt like NSM validation sets
    // Key insight: use clip names and natural language, not structured data

    // Separate clips by type
    const videoClipsForPrompt = videoAudioClips.filter(c => {
      const file = files.find(f => f.id === c.sourceFileId);
      return file?.fileType === 'video';
    });
    const audioClipsForPrompt = videoAudioClips.filter(c => {
      const file = files.find(f => f.id === c.sourceFileId);
      return file?.fileType === 'audio';
    });

    // Build clip arrangement string (like "clip1 from :10 to :15, clip2 from :22 to :24")
    const clipArrangements: string[] = [];
    videoClipsForPrompt.forEach((clip) => {
      // Simplify clip name
      let clipName = clip.name.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

      // Check for trim points
      if (clip.inTime !== undefined && clip.outTime !== undefined) {
        const inSec = Math.round(clip.inTime);
        const outSec = Math.round(clip.outTime);
        clipArrangements.push(`"${clipName}" from :${inSec.toString().padStart(2, '0')} to :${outSec.toString().padStart(2, '0')}`);
      } else {
        clipArrangements.push(`"${clipName}"`);
      }
    });

    // Build the main prompt naturally
    let finalPrompt = '';

    // Video arrangement instruction
    if (clipArrangements.length > 0) {
      finalPrompt = `Arrange clips in sequence: ${clipArrangements.join(', ')}. `;
    }

    // Collect all text overlay instructions from clip prompts
    const textOverlays: string[] = [];
    const otherInstructions: string[] = [];

    allUserPrompts.forEach(({ clip, prompt }) => {
      if (!prompt || prompt.includes('chromaKey')) return;

      // Check if this is a text overlay instruction
      if (prompt.toLowerCase().includes('text overlay') ||
          prompt.toLowerCase().includes('title:') ||
          prompt.toLowerCase().includes('overlay:')) {
        textOverlays.push(prompt);
      } else if (!prompt.includes('Trim from')) {
        // Skip trim instructions (already in assets_metadata comments)
        // Simplify clip name for reference
        let clipName = clip.name.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();
        otherInstructions.push(`For "${clipName}": ${prompt}`);
      }
    });

    // Add text overlay instructions
    if (textOverlays.length > 0) {
      finalPrompt += textOverlays.join('. ') + '. ';
    }

    // Add other instructions
    if (otherInstructions.length > 0) {
      finalPrompt += otherInstructions.join('. ') + '. ';
    }

    // Add audio instructions
    if (audioClipsForPrompt.length > 0) {
      audioClipsForPrompt.forEach((clip) => {
        let audioName = clip.name.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();
        const volumeMatch = clip.prompt?.match(/volume[:\s]*([\d.]+)/i);
        const volume = volumeMatch ? parseFloat(volumeMatch[1]) : 1.0;

        if (clip.inTime !== undefined && clip.inTime > 0) {
          const startSec = Math.round(clip.inTime);
          finalPrompt += `Add "${audioName}" starting at :${startSec.toString().padStart(2, '0')}`;
        } else {
          finalPrompt += `Add "${audioName}" as audio track`;
        }
        if (volume !== 1.0) {
          finalPrompt += ` at volume ${volume}`;
        }
        finalPrompt += '. ';
      });
    }

    // Add background images instruction
    if (backgroundImages.length > 0) {
      const bgNames = backgroundImages.map(clip => {
        let name = clip.name.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();
        return `"${name}"`;
      }).join(', ');
      finalPrompt += `Use ${bgNames} as background. `;
    }

    return finalPrompt.trim() || 'Create video from provided clips';
  };

  // Handle Quick Preview - actual video playback of timeline clips at their trim points
  const handleQuickPreview = () => {
    if (clips.length === 0) return;

    // Filter to video/image clips (things we can visually preview)
    const videoClips = clips.filter(c =>
      c.fileType === 'video' &&
      c.layerType !== 'mask' &&
      !c.name?.includes('Luma Matte') &&
      !c.name?.includes('foreground_mask')
    );
    const imageClips = clips.filter(c => c.fileType === 'image' && c.layerType !== 'background');
    const audioClips = clips.filter(c => c.fileType === 'audio');

    const previewClips: Array<{
      id: string;
      type: 'video' | 'image' | 'audio';
      name: string;
      url: string;
      inTime: number;
      outTime: number;
      duration: number;
      thumbnailUrl?: string;
      effects: string[];
    }> = [];

    let totalDuration = 0;

    // Process video clips - these are the main preview content
    videoClips.forEach((clip, index) => {
      const file = files.find(f => f.id === clip.sourceFileId);
      const inTime = clip.inTime || 0;
      const outTime = clip.outTime || clip.duration || file?.duration || 5;
      const clipDuration = outTime - inTime;

      const effects: string[] = [];
      if (clip.transitionOut) effects.push(`Transition: ${clip.transitionOut}`);
      if (clip.volume !== undefined && clip.volume !== 1) effects.push(`Volume: ${Math.round(clip.volume * 100)}%`);
      if (clip.titleText) effects.push(`Title: "${clip.titleText}"`);

      let clipName = clip.name || `Clip ${index + 1}`;
      clipName = clipName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

      // Get the video URL from the file or clip (prefer proxy URL over presigned URLs)
      const videoUrl = resolveVideoUrl(file?.s3Url, file?.s3Key) || file?.url || clip.url || '';

      if (videoUrl) {
        previewClips.push({
          id: clip.id,
          type: 'video',
          name: clipName,
          url: videoUrl,
          inTime,
          outTime,
          duration: clipDuration,
          thumbnailUrl: clip.thumbnailUrl,
          effects,
        });
        totalDuration += clipDuration;
      }
    });

    // Process image clips
    imageClips.forEach((clip, index) => {
      const file = files.find(f => f.id === clip.sourceFileId);
      const imageDuration = clip.imageDuration || clip.duration || 5;

      const effects: string[] = [];
      if (clip.imageMode) effects.push(`Mode: ${clip.imageMode}`);
      if (clip.imagePosition) effects.push(`Position: ${clip.imagePosition}`);
      if (clip.imageScale) effects.push(`Scale: ${clip.imageScale}`);

      let clipName = clip.name || `Image ${index + 1}`;
      clipName = clipName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

      const imageUrl = resolveVideoUrl(file?.s3Url, file?.s3Key) || file?.url || clip.url || clip.thumbnailUrl || '';

      if (imageUrl) {
        previewClips.push({
          id: clip.id,
          type: 'image',
          name: clipName,
          url: imageUrl,
          inTime: 0,
          outTime: imageDuration,
          duration: imageDuration,
          thumbnailUrl: clip.thumbnailUrl || imageUrl,
          effects,
        });
        totalDuration += imageDuration;
      }
    });

    setQuickPreview({
      clips: previewClips,
      summary: {
        totalClips: previewClips.length,
        totalDuration: `${totalDuration.toFixed(1)}s`,
        videoClips: videoClips.length,
        imageClips: imageClips.length,
        audioTracks: audioClips.length,
      },
      totalDuration,
    });
    setShowQuickPreviewPanel(true);
  };

  // Quick preview playback is now handled internally by QuickPreviewPanel

  // Handle AI Edit Preview - shows timing suggestions before rendering
  const handlePreviewAI = async () => {
    if (clips.length === 0) return;

    setIsLoadingPreview(true);
    setShowPreviewPanel(true);
    setAiPreview(null);

    try {
      // Build assets metadata similar to handleRender
      const assetsMetadata: any[] = [];
      const videoClips = clips.filter(c =>
        c.fileType === 'video' &&
        c.layerType !== 'mask' &&
        !c.name?.includes('Luma Matte') &&
        !c.name?.includes('foreground_mask')
      );
      const audioClips = clips.filter(c => c.fileType === 'audio');
      const imageClips = clips.filter(c => c.fileType === 'image');

      // Process video clips
      videoClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (!file || (!file.s3Url && !file.s3Key)) return;

        const hasLumaMatte = file.linkedMaskId || file.greenScreenType === 'foreground-mask';
        const resolvedS3Url = resolveVideoUrl(file.s3Url, file.s3Key);
        const videoUrl = hasLumaMatte ? resolvedS3Url : (file.greenScreenUrl || resolvedS3Url);
        const rawDuration = clip.outTime || clip.duration || file.duration || 5;
        const clipDuration = rawDuration - (clip.inTime || 0);
        const minutes = Math.floor(clipDuration / 60);
        const seconds = Math.round(clipDuration % 60);
        const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        let clipName = clip.name || `clip_${index + 1}`;
        clipName = clipName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

        // Build comment with transcript and casting.heyjoe.io comments
        let comment = '';
        // Check for timed transcript segments first (better for AI timing decisions)
        // analysisResults is keyed by analysis_id, but we match by video_id === file.id (same as ProjectBin)
        const fileAnalysis = file?.id ? (() => {
          const match = Object.values(analysisResults).find((result: any) =>
            result.status === 'completed' &&
            (result.video_id === file.id || result.results?.video_id === file.id)
          );
          return match ? (match.results || match) : null;
        })() : null;
        const transcriptSegments = clip.analysisData?.transcript?.segments ||
                                   file?.analysisData?.transcript?.segments ||
                                   fileAnalysis?.transcript?.segments;
        const transcriptWords = clip.analysisData?.transcript?.words ||
                                file?.analysisData?.transcript?.words ||
                                fileAnalysis?.transcript?.words;

        if (transcriptSegments && transcriptSegments.length > 0) {
          // Include timed segments so AI can make intelligent cut decisions
          // Handle both formats: start/end (Whisper) and start_time/end_time (casting import)
          const segmentTexts = transcriptSegments.map((seg: any) => {
            const startTime = seg.start ?? seg.start_time ?? 0;
            const endTime = seg.end ?? seg.end_time ?? 0;
            const text = seg.text || seg.content || '';
            return `[${startTime.toFixed(1)}s-${endTime.toFixed(1)}s]: "${text}"`;
          }).join('\n');
          comment = `Transcript with timings:\n${segmentTexts}`;
        } else if (transcriptWords && transcriptWords.length > 0) {
          // Word-level timing - group into ~5 word chunks for readability
          // Handle both formats: start/end (Whisper) and start_time/end_time (casting import)
          const chunks: string[] = [];
          for (let i = 0; i < transcriptWords.length; i += 5) {
            const chunk = transcriptWords.slice(i, i + 5);
            const startTime = (chunk[0]?.start ?? chunk[0]?.start_time ?? 0).toFixed(1);
            const endTime = (chunk[chunk.length - 1]?.end ?? chunk[chunk.length - 1]?.end_time ?? 0).toFixed(1);
            const text = chunk.map((w: any) => w.word || w.text || '').join(' ');
            chunks.push(`[${startTime}s-${endTime}s]: "${text}"`);
          }
          comment = `Transcript with word timings:\n${chunks.join('\n')}`;
        } else {
          // Fallback to plain text - check multiple possible locations
          const transcriptText = clip.analysisData?.transcript?.text ||
                                 clip.analysisData?.transcript?.full_text ||
                                 file?.analysisData?.transcript?.text ||
                                 file?.analysisData?.transcript?.full_text ||
                                 fileAnalysis?.transcript?.text ||
                                 fileAnalysis?.transcript?.full_text ||
                                 clip.referenceText;
          if (transcriptText) {
            comment = `Transcript: ${transcriptText}`;
          }
        }

        // Debug: log what we found for transcript
        console.log(`📝 Clip ${clipName} transcript search:`, {
          clipAnalysisData: !!clip.analysisData?.transcript,
          fileAnalysisData: !!file?.analysisData?.transcript,
          fileId: file?.id,
          foundAnalysisMatch: !!fileAnalysis,
          hasTranscriptSegments: !!transcriptSegments,
          foundComment: !!comment
        });

        // Add casting comments if available (from imported casting.heyjoe.io data)
        if ((file as any).castingComments || (clip as any).castingComments) {
          const castingComments = (file as any).castingComments || (clip as any).castingComments;
          if (comment) comment += '\n\n';
          comment += `Casting Feedback: ${castingComments}`;
        }

        // Only send trim info if user has actually modified trim points
        // (not just the default 0 to full duration - let AI pick intelligent trims)
        const fullDuration = file.duration || clip.duration || 5;
        const userModifiedTrim = (clip.inTime !== undefined && clip.inTime > 0) ||
                                  (clip.outTime !== undefined && clip.outTime < fullDuration - 0.1);
        if (userModifiedTrim && clip.inTime !== undefined && clip.outTime !== undefined) {
          if (comment) comment += '\n\n';
          comment += `USER TRIM: ${clip.inTime.toFixed(1)}s to ${clip.outTime.toFixed(1)}s (USE THESE EXACT VALUES)`;
        }

        const metadata: any = {
          url: videoUrl,
          name: clipName,
          duration: formattedDuration,
        };

        // Add explicit trim fields ONLY if user has actually modified trim points
        if (userModifiedTrim) {
          if (clip.inTime !== undefined && clip.inTime > 0) {
            metadata.trim_start = clip.inTime;
          }
          if (clip.outTime !== undefined) {
            metadata.trim_end = clip.outTime;
          }
        }

        if (comment) metadata.comment = comment;
        assetsMetadata.push(metadata);
      });

      // Process audio clips
      audioClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (!file || !file.s3Url) return;
        const sourceInTime = clip.inTime || 0;
        const sourceOutTime = clip.outTime || clip.duration || file.duration || 5;
        const audioDuration = sourceOutTime - sourceInTime;
        const minutes = Math.floor(audioDuration / 60);
        const seconds = Math.round(audioDuration % 60);
        let audioName = clip.name || `audio_${index + 1}`;
        audioName = audioName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

        // Convert proxy URLs to direct S3 URLs for Shotstack (which needs absolute URLs)
        let audioUrl = file.s3Url;
        if (audioUrl.startsWith('/api/s3/')) {
          // Convert /api/s3/processed/... to https://hey-joe-videos.s3.amazonaws.com/processed/...
          const s3Path = audioUrl.replace('/api/s3/', '');
          audioUrl = `https://hey-joe-videos.s3.amazonaws.com/${s3Path}`;
        }

        assetsMetadata.push({
          url: audioUrl,
          name: audioName,
          duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
        });
      });

      // Process image clips
      imageClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (!file || !file.s3Url) return;
        let imageName = clip.name || `image_${index + 1}`;
        imageName = imageName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

        assetsMetadata.push({
          url: file.s3Url,
          name: imageName,
          // Width and height are required by Lambda for images (must be strings)
          width: String(file.width || 1920),
          height: String(file.height || 1080),
        });
      });

      // Use creative brief or generated prompt
      let aiPrompt = creativeBrief.trim()
        ? creativeBrief
        : generateMasterPrompt();

      // If we have script analysis with scenes/edit points, include it in the prompt
      if (scriptAnalysis && scriptAnalysis.scenes && scriptAnalysis.scenes.length > 0) {
        const editPlanSection = `

=== PRE-ANALYZED EDIT PLAN ===
The following scenes have been analyzed from the script with specific clip selections and trim times.
IMPORTANT: Use these exact clips and trim times as specified. Do not re-interpret or change the clip selections.

${scriptAnalysis.scenes.map((scene, idx) => {
  const clipDetails = scene.suggestedClips.map(clip => {
    if (typeof clip === 'string') {
      return `  - Clip: "${clip}"`;
    } else {
      const trimInfo = clip.trim
        ? ` (trim: ${clip.trim.from}s to ${clip.trim.to}s)`
        : '';
      return `  - Clip: "${clip.assetName}"${trimInfo} - ${clip.reason}`;
    }
  }).join('\n');

  return `Scene ${idx + 1}: ${scene.description}
Script Line: "${scene.scriptLine}"
Duration: ${scene.estimatedDuration}s
Clips:
${clipDetails}`;
}).join('\n\n')}

=== END EDIT PLAN ===
`;
        aiPrompt = aiPrompt + editPlanSection;
        console.log('Preview: Including script analysis edit plan with', scriptAnalysis.scenes.length, 'scenes');
      }

      // Log what we're sending for debugging
      const previewOutputConfig = resolveOutputConfig(aspectRatio, files);
      const previewEffectiveRatio = aspectRatio === 'source'
        ? `${previewOutputConfig.output_width}:${previewOutputConfig.output_height}`
        : aspectRatio;
      const requestBody = {
        company_name: currentProject,
        users_text: aiPrompt,
        users_objective: `Create an enhanced video using ${clips.length} clips in ${previewEffectiveRatio} aspect ratio`,
        assets_metadata: assetsMetadata,
        aspect_ratio: previewEffectiveRatio,
        shotstack_resolution: previewOutputConfig.shotstack_resolution || 'hd',
        output_width: previewOutputConfig.output_width,
        output_height: previewOutputConfig.output_height,
      };
      console.log('🚀 AI Preview request:', {
        promptLength: aiPrompt?.length || 0,
        promptPreview: aiPrompt?.substring(0, 100) + '...',
        assetsCount: assetsMetadata.length,
        assets: assetsMetadata.map((a: any) => ({
          name: a.name,
          hasUrl: !!a.url,
          urlPreview: a.url?.substring(0, 50),
          hasDuration: !!a.duration,
          duration: a.duration,
          hasWidth: !!a.width,
          hasHeight: !!a.height,
        })),
      });

      const response = await fetch('/api/shotstack/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ AI Preview failed:', {
          status: response.status,
          error: errorData,
        });
        throw new Error(errorData.details || errorData.error || `Preview generation failed (${response.status})`);
      }

      const data = await response.json();
      if (data.success && data.shotstackJson) {
        // Apply AI edit directly to timeline (new simplified approach)
        applyAIEditToTimeline(data.shotstackJson, 'AI Edit Generated');
      } else {
        throw new Error('Invalid preview response');
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate AI edit',
        variant: 'destructive',
      });
      setShowPreviewPanel(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Handle render mode toggle - show warning when switching to AI Creative
  const handleRenderModeToggle = () => {
    if (renderMode === 'direct') {
      // Switching TO AI Creative - show warning
      setShowAICreativeWarning(true);
    } else {
      // Switching to Quick Render - no warning needed
      setRenderMode('direct');
    }
  };

  // Confirm switching to AI Creative mode
  const confirmAICreativeMode = () => {
    setRenderMode('lambda');
    setShowAICreativeWarning(false);
  };

  // Render from AI Preview - actual implementation
  const executeRenderFromPreview = async () => {
    if (!aiPreview?.shotstackJson) {
      toast({
        title: 'No Preview Available',
        description: 'Please generate a preview first',
        variant: 'destructive',
      });
      return;
    }

    // Create new render entry
    const renderId = `render-${Date.now()}`;
    const newRender: RenderedVideo = {
      id: renderId,
      name: `AI Edit - ${new Date().toLocaleTimeString()}`,
      status: 'rendering',
      thumbnailUrl: clips[0]?.thumbnailUrl,
      createdAt: new Date(),
      configuration: {
        clips: clips.map(c => ({ ...c })),
        aspectRatio,
        renderMode: 'lambda',
        masterPrompt: creativeBrief || generateMasterPrompt(),
      },
    };
    setRenderedVideos(prev => [newRender, ...prev]);

    // Get auth token for credit-protected endpoints
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

    // Build caption data if captions are enabled
    const captionData = finishingTouches?.addCaptions ? {
      enabled: true,
      style: finishingTouches.captionStyle || 'bottom-center',
      assets: files
        .filter(f => f.analysisData?.transcript?.segments?.length > 0 || f.analysisData?.transcript?.items?.length > 0)
        .map(f => ({
          url: f.url,
          words: f.analysisData?.transcript?.items || f.analysisData?.transcript?.segments || [],
        })),
    } : undefined;

    try {
      // Submit to Shotstack with the pre-generated JSON (with 2-minute timeout)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      // Resolve output dimensions for Match Source / custom aspect ratios
      const renderOutputConfig = resolveOutputConfig(aspectRatio, files);

      const renderResponse = await fetch('/api/shotstack/render', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          json: aiPreview.shotstackJson,
          captionData,
          outputSize: { width: renderOutputConfig.output_width, height: renderOutputConfig.output_height },
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const renderData = await renderResponse.json();

      if (!renderResponse.ok) {
        console.error('Render submission failed:', renderData);
        throw new Error(renderData.details || renderData.error || 'Failed to submit render');
      }

      // Update with Shotstack ID and start polling
      setRenderedVideos(prev => prev.map(v =>
        v.id === renderId
          ? { ...v, shotstackId: renderData.id, shotstackJson: aiPreview.shotstackJson }
          : v
      ));

      // Only close preview panel on successful render submission
      setShowPreviewPanel(false);

      // Start polling for render status
      pollRenderStatus(renderId, renderData.id);

      toast({
        title: 'Render Started',
        description: 'Your AI edit is being rendered. Check the renders list for progress.',
      });
    } catch (error: any) {
      const errorMessage = error.name === 'AbortError'
        ? 'Render submission timed out after 2 minutes'
        : error.message;
      setRenderedVideos(prev => prev.map(v =>
        v.id === renderId
          ? { ...v, status: 'failed', error: errorMessage }
          : v
      ));
      // Keep preview panel open on failure so user can retry
      toast({
        title: 'Render Failed',
        description: `${errorMessage}. Preview is still available - you can try again.`,
        variant: 'destructive',
      });
    }
  };

  // Render from AI Preview - wrapper with credit confirmation
  const handleRenderFromPreview = () => {
    if (!aiPreview?.shotstackJson) {
      toast({
        title: 'No Preview Available',
        description: 'Please generate a preview first',
        variant: 'destructive',
      });
      return;
    }

    requireCreditConfirmation(
      CREDIT_OPERATIONS.SHOTSTACK_RENDER,
      'Video Render',
      () => executeRenderFromPreview()
    );
  };

  // Update a video clip's trim in the AI Preview
  const updateAiPreviewClipTrim = (clipIndex: number, newTrimStart: number, newLength: number) => {
    if (!aiPreview?.shotstackJson) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];

    // Find video track (usually last track)
    const videoTrack = tracks.find((t: any) => t.clips?.some((c: any) => c.asset?.type === 'video'));
    if (!videoTrack) return;

    const videoClips = videoTrack.clips.filter((c: any) => c.asset?.type === 'video');
    if (clipIndex >= videoClips.length) return;

    // Update the clip
    const clip = videoClips[clipIndex];
    clip.asset.trim = newTrimStart;
    clip.length = newLength;

    // Recalculate timeline positions for all clips
    let currentStart = 0;
    for (const c of videoClips) {
      c.start = currentStart;
      currentStart += c.length;
    }

    // Update steps to reflect changes
    const newSteps = aiPreview.steps.map((step, idx) => {
      if (step.type !== 'video') return step;
      const videoIdx = aiPreview.steps.slice(0, idx + 1).filter(s => s.type === 'video').length - 1;
      if (videoIdx !== clipIndex) return step;

      return {
        ...step,
        timing: {
          start: videoClips[clipIndex].start,
          length: newLength,
          end: videoClips[clipIndex].start + newLength,
          trimStart: newTrimStart,
          trimEnd: newTrimStart + newLength,
        },
        timing_note: `In: ${newTrimStart.toFixed(1)}s → Out: ${(newTrimStart + newLength).toFixed(1)}s`,
        asset: { ...step.asset, trim: newTrimStart },
      };
    });

    const totalDuration = videoClips.reduce((acc: number, c: any) => acc + c.length, 0);

    setAiPreview({
      ...aiPreview,
      steps: newSteps,
      totalDuration,
      summary: { ...aiPreview.summary, totalDuration: `${totalDuration.toFixed(1)}s` },
      shotstackJson: updatedJson,
    });

    // Video seeking is handled internally by AIEditPreview component
  };

  // Reorder clips in the AI Preview
  const reorderAiPreviewClips = (fromIndex: number, toIndex: number) => {
    if (!aiPreview?.shotstackJson) return;
    if (fromIndex === toIndex) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];
    const videoTrack = tracks.find((t: any) => t.clips?.some((c: any) => c.asset?.type === 'video'));
    if (!videoTrack) return;

    const videoClips = videoTrack.clips.filter((c: any) => c.asset?.type === 'video');
    const [movedClip] = videoClips.splice(fromIndex, 1);
    videoClips.splice(toIndex, 0, movedClip);

    // Recalculate timeline positions
    let currentStart = 0;
    for (const c of videoClips) {
      c.start = currentStart;
      currentStart += c.length;
    }

    // Update the track with reordered clips
    videoTrack.clips = [
      ...tracks.find((t: any) => t.clips?.some((c: any) => c.asset?.type === 'text'))?.clips || [],
      ...videoClips,
    ].filter(Boolean);

    // Rebuild steps
    const videoSteps = aiPreview.steps.filter(s => s.type === 'video');
    const [movedStep] = videoSteps.splice(fromIndex, 1);
    videoSteps.splice(toIndex, 0, movedStep);

    // Update timing for reordered steps
    let stepStart = 0;
    const updatedVideoSteps = videoSteps.map(step => {
      const length = step.timing?.length || 5;
      const updated = {
        ...step,
        timing: { ...step.timing, start: stepStart, end: stepStart + length },
      };
      stepStart += length;
      return updated;
    });

    // Merge video steps back with non-video steps
    const newSteps = aiPreview.steps.map(step => {
      if (step.type !== 'video') return step;
      return updatedVideoSteps.shift() || step;
    });

    setAiPreview({
      ...aiPreview,
      steps: newSteps,
      shotstackJson: updatedJson,
    });

    // Current clip index is managed internally by AIEditPreview component
  };

  // Delete a clip from AI Preview
  const deleteAiPreviewClip = (clipIndex: number) => {
    if (!aiPreview?.shotstackJson) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];
    const videoTrack = tracks.find((t: any) => t.clips?.some((c: any) => c.asset?.type === 'video'));
    if (!videoTrack) return;

    const videoClips = videoTrack.clips.filter((c: any) => c.asset?.type === 'video');
    videoClips.splice(clipIndex, 1);

    // Recalculate timeline positions
    let currentStart = 0;
    for (const c of videoClips) {
      c.start = currentStart;
      currentStart += c.length;
    }

    // Update track
    const textClips = tracks.find((t: any) => t.clips?.some((c: any) => c.asset?.type === 'text'))?.clips || [];
    videoTrack.clips = videoClips;

    // Remove from steps
    let videoIdx = -1;
    const newSteps = aiPreview.steps.filter(step => {
      if (step.type === 'video') {
        videoIdx++;
        return videoIdx !== clipIndex;
      }
      return true;
    });

    const totalDuration = videoClips.reduce((acc: number, c: any) => acc + c.length, 0);

    setAiPreview({
      ...aiPreview,
      steps: newSteps,
      totalDuration,
      summary: {
        ...aiPreview.summary,
        totalDuration: `${totalDuration.toFixed(1)}s`,
        videoClips: aiPreview.summary.videoClips - 1,
        totalClips: aiPreview.summary.totalClips - 1,
      },
      shotstackJson: updatedJson,
    });

    // Current clip index adjustment is handled internally by AIEditPreview component
  };

  // Update text overlay in AI Preview
  const updateAiPreviewTextOverlay = (overlayIndex: number, newText: string) => {
    if (!aiPreview?.shotstackJson) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];

    let textIdx = -1;
    for (const track of tracks) {
      for (const clip of (track.clips || [])) {
        if (clip.asset?.type === 'text') {
          textIdx++;
          if (textIdx === overlayIndex) {
            clip.asset.text = newText;
            break;
          }
        }
      }
    }

    // Update steps
    let stepTextIdx = -1;
    const newSteps = aiPreview.steps.map(step => {
      if (step.type === 'text' || step.asset?.type === 'text') {
        stepTextIdx++;
        if (stepTextIdx === overlayIndex) {
          return { ...step, asset: { ...step.asset, text: newText } };
        }
      }
      return step;
    });

    setAiPreview({ ...aiPreview, steps: newSteps, shotstackJson: updatedJson });
  };

  // Update text overlay timing (start time and duration) in AI Preview
  const updateAiPreviewTextOverlayTiming = (overlayIndex: number, newStart: number, newLength: number) => {
    if (!aiPreview?.shotstackJson) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];

    let textIdx = -1;
    for (const track of tracks) {
      for (const clip of (track.clips || [])) {
        if (clip.asset?.type === 'text') {
          textIdx++;
          if (textIdx === overlayIndex) {
            clip.start = newStart;
            clip.length = newLength;
            break;
          }
        }
      }
    }

    // Update steps
    let stepTextIdx = -1;
    const newSteps = aiPreview.steps.map(step => {
      if (step.type === 'text' || step.asset?.type === 'text') {
        stepTextIdx++;
        if (stepTextIdx === overlayIndex) {
          return {
            ...step,
            timing: { ...step.timing, start: newStart, length: newLength, end: newStart + newLength },
          };
        }
      }
      return step;
    });

    setAiPreview({ ...aiPreview, steps: newSteps, shotstackJson: updatedJson });
  };

  // Delete text overlay from AI Preview
  const deleteAiPreviewTextOverlay = (overlayIndex: number) => {
    if (!aiPreview?.shotstackJson) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];

    let textIdx = -1;
    for (const track of tracks) {
      track.clips = (track.clips || []).filter((clip: any) => {
        if (clip.asset?.type === 'text') {
          textIdx++;
          return textIdx !== overlayIndex;
        }
        return true;
      });
    }

    // Remove empty tracks
    updatedJson.timeline.tracks = tracks.filter((t: any) => t.clips?.length > 0);

    // Update steps
    let stepTextIdx = -1;
    const newSteps = aiPreview.steps.filter(step => {
      if (step.type === 'text' || step.asset?.type === 'text') {
        stepTextIdx++;
        return stepTextIdx !== overlayIndex;
      }
      return true;
    });

    setAiPreview({
      ...aiPreview,
      steps: newSteps,
      summary: {
        ...aiPreview.summary,
        textOverlays: aiPreview.summary.textOverlays - 1,
        totalClips: aiPreview.summary.totalClips - 1,
      },
      shotstackJson: updatedJson,
    });
  };

  // Duplicate a clip in AI Preview (inserts copy after the original)
  const duplicateAiPreviewClip = (clipIndex: number) => {
    if (!aiPreview?.shotstackJson) return;

    const updatedJson = JSON.parse(JSON.stringify(aiPreview.shotstackJson));
    const tracks = updatedJson.timeline?.tracks || [];
    const videoTrack = tracks.find((t: any) => t.clips?.some((c: any) => c.asset?.type === 'video'));
    if (!videoTrack) return;

    const videoClips = videoTrack.clips.filter((c: any) => c.asset?.type === 'video');
    const originalClip = videoClips[clipIndex];
    if (!originalClip) return;

    // Create a deep copy of the clip
    const duplicatedClip = JSON.parse(JSON.stringify(originalClip));

    // Insert the duplicate after the original
    videoClips.splice(clipIndex + 1, 0, duplicatedClip);

    // Recalculate timeline positions
    let currentStart = 0;
    for (const c of videoClips) {
      c.start = currentStart;
      currentStart += c.length;
    }

    // Update the track with all clips
    const textClips = tracks.flatMap((t: any) =>
      (t.clips || []).filter((c: any) => c.asset?.type === 'text')
    );
    videoTrack.clips = videoClips;

    // Duplicate the step as well
    const videoSteps = aiPreview.steps.filter(s => s.type === 'video');
    const originalStep = videoSteps[clipIndex];
    if (originalStep) {
      const duplicatedStep = JSON.parse(JSON.stringify(originalStep));
      videoSteps.splice(clipIndex + 1, 0, duplicatedStep);
    }

    // Update timing for all video steps
    let stepStart = 0;
    const updatedVideoSteps = videoSteps.map(step => {
      const length = step.timing?.length || 5;
      const updated = {
        ...step,
        timing: { ...step.timing, start: stepStart, end: stepStart + length },
      };
      stepStart += length;
      return updated;
    });

    // Rebuild all steps, preserving non-video steps
    let videoStepIdx = 0;
    const newSteps = aiPreview.steps.flatMap(step => {
      if (step.type !== 'video') return [step];
      const currentStep = updatedVideoSteps[videoStepIdx];
      videoStepIdx++;
      // If this was the duplicated position, return both original and duplicate
      if (videoStepIdx === clipIndex + 1) {
        return [currentStep, updatedVideoSteps[videoStepIdx++]].filter(Boolean);
      }
      return currentStep ? [currentStep] : [];
    });

    const totalDuration = videoClips.reduce((acc: number, c: any) => acc + c.length, 0);

    setAiPreview({
      ...aiPreview,
      steps: newSteps,
      totalDuration,
      summary: {
        ...aiPreview.summary,
        totalDuration: `${totalDuration.toFixed(1)}s`,
        videoClips: aiPreview.summary.videoClips + 1,
        totalClips: aiPreview.summary.totalClips + 1,
      },
      shotstackJson: updatedJson,
    });

    toast({
      title: 'Clip Duplicated',
      description: 'You can now adjust the trim points for the duplicate',
    });
  };

  // Convert Shotstack JSON directly to timeline clips with text overlays embedded
  // This is the new simplified approach - AI output goes straight to timeline
  const convertShotstackToTimelineClips = (shotstackJson: any): ProjectClip[] => {
    const tracks = shotstackJson?.timeline?.tracks || [];

    // Extract video clips
    const videoClips: any[] = [];
    const textOverlays: Array<{ text: string; start: number; length: number; position: string; style: string }> = [];

    for (const track of tracks) {
      for (const clip of (track.clips || [])) {
        if (clip.asset?.type === 'video') {
          videoClips.push(clip);
        } else if (clip.asset?.type === 'text') {
          textOverlays.push({
            text: clip.asset.text || '',
            start: clip.start || 0,
            length: clip.length || 3,
            position: clip.position || 'center',
            style: clip.asset.font?.size >= 50 ? 'title' : 'subtitle',
          });
        }
      }
    }

    // Sort video clips by start time
    videoClips.sort((a, b) => (a.start || 0) - (b.start || 0));

    // Build cumulative timeline to map text overlays to clips
    let cumulativeStart = 0;
    const clipTimings: Array<{ start: number; end: number; index: number }> = [];
    videoClips.forEach((clip, idx) => {
      const clipLength = clip.length || 5;
      clipTimings.push({ start: cumulativeStart, end: cumulativeStart + clipLength, index: idx });
      cumulativeStart += clipLength;
    });

    // Convert to ProjectClip format
    const newClips: ProjectClip[] = videoClips.map((clip, index) => {
      const srcUrl = clip.asset?.src || '';

      // Find the matching source file
      const matchingFile = files.find(f =>
        f.s3Url === srcUrl ||
        (f.s3Key && srcUrl.includes(f.s3Key)) ||
        srcUrl.includes(f.name)
      );

      // Calculate in/out times from trim and length
      const trimStart = clip.asset?.trim || 0;
      const clipLength = clip.length || 5;

      // Map Shotstack transition to Quick Render format
      let transitionOut: ProjectClip['transitionOut'] = 'none';
      const transitionType = clip.transition?.out || clip.transition?.in || '';
      if (transitionType.includes('fade')) {
        transitionOut = transitionType.includes('Slow') ? 'fadeSlow' : transitionType.includes('Fast') ? 'fadeFast' : 'fade';
      } else if (transitionType.includes('wipeLeft')) transitionOut = 'wipeLeft';
      else if (transitionType.includes('wipeRight')) transitionOut = 'wipeRight';
      else if (transitionType.includes('slideLeft')) transitionOut = 'slideLeft';
      else if (transitionType.includes('slideRight')) transitionOut = 'slideRight';
      else if (transitionType.includes('slideUp')) transitionOut = 'slideUp';
      else if (transitionType.includes('slideDown')) transitionOut = 'slideDown';
      else if (transitionType) transitionOut = 'fade';

      // Find text overlay for this clip (based on timeline position)
      const timing = clipTimings[index];
      const overlayForClip = textOverlays.find(o =>
        o.start >= timing.start && o.start < timing.end
      );

      // Map Shotstack position to our titlePosition format
      let titlePosition: ProjectClip['titlePosition'] = undefined;
      if (overlayForClip) {
        const pos = overlayForClip.position || 'center';
        if (pos.includes('top') && pos.includes('left')) titlePosition = 'top-left';
        else if (pos.includes('top') && pos.includes('right')) titlePosition = 'top-right';
        else if (pos.includes('top')) titlePosition = 'top-center';
        else if (pos.includes('bottom') && pos.includes('left')) titlePosition = 'bottom-left';
        else if (pos.includes('bottom') && pos.includes('right')) titlePosition = 'bottom-right';
        else if (pos.includes('bottom')) titlePosition = 'bottom-center';
        else if (pos.includes('left')) titlePosition = 'center-left';
        else if (pos.includes('right')) titlePosition = 'center-right';
        else titlePosition = 'center';
      }

      return {
        id: `ai-clip-${Date.now()}-${index}`,
        sourceFileId: matchingFile?.id || `unknown-${index}`,
        position: index,
        prompt: '', // Quick Render mode doesn't use prompt
        duration: clipLength,
        name: matchingFile?.name || `Clip ${index + 1}`,
        thumbnailUrl: matchingFile?.thumbnailUrl,
        fileType: 'video' as const,
        layerType: 'main' as const,
        inTime: trimStart,
        outTime: trimStart + clipLength,
        transitionOut,
        // Text overlay embedded in clip
        titleText: overlayForClip?.text || undefined,
        titlePosition: overlayForClip ? titlePosition : undefined,
      };
    });

    return newClips;
  };

  // Apply AI-generated edit directly to timeline (simplified approach)
  const applyAIEditToTimeline = (shotstackJson: any, message?: string) => {
    const newClips = convertShotstackToTimelineClips(shotstackJson);

    if (newClips.length === 0) {
      toast({
        title: 'No clips generated',
        description: 'The AI edit plan has no video clips',
        variant: 'destructive',
      });
      return;
    }

    // Update timeline with AI-generated clips
    setClips(newClips);

    // Stay in current render mode (don't switch to Quick Render)
    // This lets users stay in AI Creative mode to regenerate if needed

    // Close any open panels
    setShowPreviewPanel(false);
    setShowScriptPanel(false);
    setAiPreview(null);

    const clipsWithText = newClips.filter(c => c.titleText).length;
    const totalClips = newClips.length;

    toast({
      title: message || 'AI Edit Applied',
      description: clipsWithText > 0
        ? `${totalClips} clips added to timeline (${clipsWithText} with text overlays). Adjust as needed and render.`
        : `${totalClips} clips added to timeline. Adjust as needed and render.`,
    });
  };
  // Wire up forward refs for hooks that depend on functions defined above
  applyAIEditToTimelineRef.current = applyAIEditToTimeline;

  // Convert AI Preview to Timeline clips and switch to Quick Render mode
  const handleEditInTimeline = () => {
    if (!aiPreview?.steps) {
      toast({
        title: 'No Preview Available',
        description: 'Please generate an AI preview first',
        variant: 'destructive',
      });
      return;
    }

    // Filter to video steps only
    const videoSteps = aiPreview.steps.filter(s => s.type === 'video' && s.asset?.src);

    if (videoSteps.length === 0) {
      toast({
        title: 'No Video Clips',
        description: 'The AI preview has no video clips to edit',
        variant: 'destructive',
      });
      return;
    }

    // Convert AI preview steps to ProjectClip format
    const newClips: ProjectClip[] = videoSteps.map((step, index) => {
      const srcUrl = step.asset?.src || '';

      // Find the matching source file
      const matchingFile = files.find(f =>
        f.s3Url === srcUrl ||
        (f.s3Key && srcUrl.includes(f.s3Key)) ||
        srcUrl.includes(f.name)
      );

      // Calculate in/out times from trim and length
      const trimStart = step.asset?.trim || 0;
      const clipLength = step.timing?.length || 5;

      // Map AI transition to Quick Render transitionOut format
      let transitionOut: ProjectClip['transitionOut'] = 'none';
      if (step.transition) {
        const transitionType = step.transition.out || step.transition.in || '';
        // Map common Shotstack transitions to our format
        if (transitionType.includes('fade')) {
          transitionOut = transitionType.includes('Slow') ? 'fadeSlow' : transitionType.includes('Fast') ? 'fadeFast' : 'fade';
        } else if (transitionType.includes('wipeLeft')) {
          transitionOut = 'wipeLeft';
        } else if (transitionType.includes('wipeRight')) {
          transitionOut = 'wipeRight';
        } else if (transitionType.includes('slideLeft')) {
          transitionOut = 'slideLeft';
        } else if (transitionType.includes('slideRight')) {
          transitionOut = 'slideRight';
        } else if (transitionType.includes('slideUp')) {
          transitionOut = 'slideUp';
        } else if (transitionType.includes('slideDown')) {
          transitionOut = 'slideDown';
        } else if (transitionType) {
          // Default to fade for any other transition
          transitionOut = 'fade';
        }
      }

      return {
        id: `ai-clip-${Date.now()}-${index}`,
        sourceFileId: matchingFile?.id || `unknown-${index}`,
        position: index,
        prompt: '', // Clear prompt since this is Quick Render mode
        duration: clipLength,
        name: matchingFile?.name || `Clip ${index + 1}`,
        thumbnailUrl: matchingFile?.thumbnailUrl,
        fileType: 'video' as const,
        layerType: 'main' as const,
        inTime: trimStart,
        outTime: trimStart + clipLength,
        transitionOut,
      };
    });

    // Video pause is handled by AIEditPreview component's onEditInTimeline callback

    // Extract text overlays from Shotstack JSON to preserve them
    const textOverlaysFromJson: Array<{text: string; start: number; length: number; position: string; style: string}> = [];
    if (aiPreview.shotstackJson?.timeline?.tracks) {
      for (const track of aiPreview.shotstackJson.timeline.tracks) {
        for (const clip of (track.clips || [])) {
          if (clip.asset?.type === 'text') {
            textOverlaysFromJson.push({
              text: clip.asset.text || '',
              start: clip.start || 0,
              length: clip.length || 3,
              position: clip.position || 'center',
              style: clip.asset.font?.size >= 50 ? 'title' : 'subtitle',
            });
          }
        }
      }
    }

    // Store text overlays for use in Quick Render mode
    if (textOverlaysFromJson.length > 0) {
      console.log('Preserving text overlays from AI edit:', textOverlaysFromJson);
      // Store in localStorage or state for Quick Render to use
      localStorage.setItem('aiTextOverlays', JSON.stringify(textOverlaysFromJson));
    }

    // Update clips with AI-generated timeline
    setClips(newClips);

    // Switch to Quick Render mode (direct)
    setRenderMode('direct');

    // Close preview panel
    setShowPreviewPanel(false);

    // Clear AI preview (we've converted it to clips now)
    setAiPreview(null);

    const overlayCount = textOverlaysFromJson.length;
    toast({
      title: 'Loaded into Timeline',
      description: overlayCount > 0
        ? `${newClips.length} video clips loaded. Note: ${overlayCount} text overlays were saved but won't appear in Quick Render mode. Use "Render This Edit" for full AI edit with overlays.`
        : `${newClips.length} clips from AI preview are now in your timeline. Adjust and render when ready.`,
    });
  };

  // Handle render with Shotstack
  const handleRender = async () => {
    if (clips.length === 0) return;
    
    // No need to block - we can queue multiple renders!
    // Each one gets its own ID and tracking
    // Use the edited master prompt if user has modified it, otherwise use generated
    const masterPrompt = isEditingMasterPrompt ? editableMasterPrompt : generateMasterPrompt();
    const totalDuration = clips.reduce((sum, clip) => sum + clip.duration, 0);

    // Show immediate feedback - add to queue
    // Use the first clip's thumbnail as placeholder
    const firstClipThumbnail = clips[0]?.thumbnailUrl && 
                              clips[0].thumbnailUrl !== 'AUDIO_ICON' && 
                              clips[0].thumbnailUrl !== 'SRT_ICON' 
                              ? clips[0].thumbnailUrl 
                              : undefined;
    
    const queuedVideo: RenderedVideo = {
      id: Math.random().toString(36).substr(2, 9),
      name: `Enhanced_${new Date().toISOString().split('T')[0]}_${Date.now()}`,
      thumbnailUrl: firstClipThumbnail,
      timestamp: new Date().toISOString(),
      duration: totalDuration,
      status: 'processing',
      progress: 0,
      configuration: {
        // Strip analysisData from clips - it's not needed for rendering and bloats the JSON
        clips: clips.map(({ analysisData, ...clipWithoutAnalysis }) => clipWithoutAnalysis),
        masterPrompt,
      },
    };

    setRenderedVideos(prev => {
      const updated = [...prev, queuedVideo];
      localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updated));
      return updated;
    });

    toast({
      title: "Processing request",
      description: "Your video has been added to the render queue",
    });

    try {
      // Prepare assets metadata for Shotstack - SIMPLIFIED VERSION
      const assetsMetadata: any[] = [];
      
      // Separate clips by type
      // IMPORTANT: Exclude luma matte clips from video - they're handled separately by the backend
      const videoClips = clips.filter(c =>
        c.fileType === 'video' &&
        c.layerType !== 'mask' &&
        !c.name?.includes('Luma Matte') &&
        !c.name?.includes('foreground_mask')
      );
      const lumaMatteClips = clips.filter(c =>
        c.layerType === 'mask' ||
        c.name?.includes('Luma Matte') ||
        c.name?.includes('foreground_mask')
      );
      const audioClips = clips.filter(c => c.fileType === 'audio');
      const imageClips = clips.filter(c => c.fileType === 'image');
      const otherClips = clips.filter(c => !['video', 'audio', 'image'].includes(c.fileType || '') && c.layerType !== 'mask');

      console.log(`📊 Clip breakdown: ${videoClips.length} video, ${lumaMatteClips.length} luma matte, ${audioClips.length} audio, ${imageClips.length} image`);
      
      let cumulativeVideoTime = 0;
      
      // Process video clips with sequential timing
      videoClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (!file || (!file.s3Url && !file.s3Key)) {
          throw new Error(`Missing S3 URL for clip: ${clip.name}`);
        }

        // For luma matte workflow, use ORIGINAL video (not green screen processed)
        // The luma matte handles transparency, so we don't need the green screen version
        const hasLumaMatte = file.linkedMaskId || file.greenScreenType === 'foreground-mask';
        const resolvedS3Url = resolveVideoUrl(file.s3Url, file.s3Key);
        const videoUrl = hasLumaMatte ? resolvedS3Url : (file.greenScreenUrl || resolvedS3Url);
        // Get duration from clip or file, fallback to 5 seconds if missing
        const rawDuration = clip.outTime || clip.duration || file.duration || 5;
        const clipDuration = rawDuration - (clip.inTime || 0);
        const timelineStart = cumulativeVideoTime;
        const timelineEnd = cumulativeVideoTime + clipDuration;

        // Format duration as M:SS (what NSM validation sets use)
        const minutes = Math.floor(clipDuration / 60);
        const seconds = Math.round(clipDuration % 60);
        const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        // Use actual clip name (NSM validation sets use descriptive names like "Take 1", "clip1")
        // Simplify name by removing timing info
        let clipName = clip.name || `clip_${index + 1}`;
        clipName = clipName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

        // Build comment with editing notes (trim points and text overlay)
        // NSM validation sets use format: "start at :02, end at :07. text overlay: "text" in white text"
        let comment = '';

        // Add transcript for AI Creative mode (Lambda uses this to understand content)
        // Include TIMED segments so AI can make intelligent clip timing decisions
        if (renderMode === 'lambda') {
          const file = files.find(f => f.id === clip.sourceFileId);
          // analysisResults is keyed by analysis_id, but we match by video_id === file.id (same as ProjectBin)
          const fileAnalysis = file?.id ? (() => {
            const match = Object.values(analysisResults).find((result: any) =>
              result.status === 'completed' &&
              (result.video_id === file.id || result.results?.video_id === file.id)
            );
            return match ? (match.results || match) : null;
          })() : null;
          const transcriptSegments = clip.analysisData?.transcript?.segments ||
                                     file?.analysisData?.transcript?.segments ||
                                     fileAnalysis?.transcript?.segments;
          const transcriptWords = clip.analysisData?.transcript?.words ||
                                  file?.analysisData?.transcript?.words ||
                                  fileAnalysis?.transcript?.words;

          // Debug: log what we found for transcript in render
          console.log(`📝 Render - Clip ${clipName} transcript search:`, {
            clipAnalysisData: !!clip.analysisData?.transcript,
            fileAnalysisData: !!file?.analysisData?.transcript,
            fileId: file?.id,
            foundAnalysisMatch: !!fileAnalysis,
            hasSegments: !!transcriptSegments,
            hasWords: !!transcriptWords
          });

          if (transcriptSegments && transcriptSegments.length > 0) {
            // Include timed segments so AI can make intelligent cut decisions
            // Handle both formats: start/end (Whisper) and start_time/end_time (casting import)
            const segmentTexts = transcriptSegments.map((seg: any) => {
              const startTime = seg.start ?? seg.start_time ?? 0;
              const endTime = seg.end ?? seg.end_time ?? 0;
              const text = seg.text || seg.content || '';
              return `[${startTime.toFixed(1)}s-${endTime.toFixed(1)}s]: "${text}"`;
            }).join('\n');
            comment = `Transcript with timings:\n${segmentTexts}`;
          } else if (transcriptWords && transcriptWords.length > 0) {
            // Word-level timing - group into ~5 word chunks for readability
            // Handle both formats: start/end (Whisper) and start_time/end_time (casting import)
            const chunks: string[] = [];
            for (let i = 0; i < transcriptWords.length; i += 5) {
              const chunk = transcriptWords.slice(i, i + 5);
              const startTime = (chunk[0]?.start ?? chunk[0]?.start_time ?? 0).toFixed(1);
              const endTime = (chunk[chunk.length - 1]?.end ?? chunk[chunk.length - 1]?.end_time ?? 0).toFixed(1);
              const text = chunk.map((w: any) => w.word || w.text || '').join(' ');
              chunks.push(`[${startTime}s-${endTime}s]: "${text}"`);
            }
            comment = `Transcript with word timings:\n${chunks.join('\n')}`;
          } else {
            // Fallback to plain text - check multiple possible locations
            const transcriptText = clip.analysisData?.transcript?.text ||
                                   clip.analysisData?.transcript?.full_text ||
                                   file?.analysisData?.transcript?.text ||
                                   file?.analysisData?.transcript?.full_text ||
                                   fileAnalysis?.transcript?.text ||
                                   fileAnalysis?.transcript?.full_text ||
                                   clip.referenceText;
            if (transcriptText) {
              comment = `Transcript: ${transcriptText}`;
            }
          }
        }

        // Only send USER TRIM if user has actually modified trim points
        // (not just the default 0 to full duration)
        const fullDuration = file.duration || clip.duration || 5;
        const userModifiedTrim = (clip.inTime !== undefined && clip.inTime > 0) ||
                                  (clip.outTime !== undefined && clip.outTime < fullDuration - 0.1);
        if (userModifiedTrim && clip.inTime !== undefined && clip.outTime !== undefined) {
          if (comment) comment += '\n\n';
          comment += `USER TRIM: ${clip.inTime.toFixed(1)}s to ${clip.outTime.toFixed(1)}s (USE THESE EXACT VALUES)`;
        }
        if (clip.prompt && !clip.prompt.includes('chromaKey')) {
          let promptText = clip.prompt.trim();
          // Remove volume settings (handled separately)
          promptText = promptText.replace(/\s*volume[:\s]*[\d.]+/gi, '').trim();

          if (promptText) {
            if (comment) comment += '\n\n';
            comment += `CLIP INSTRUCTION: ${promptText}`;
          }
        }

        const metadata: any = {
          url: videoUrl,
          name: clipName,
          duration: formattedDuration,
        };

        // Add explicit trim fields ONLY if user has actually modified trim points
        // (not just the default 0 to full duration - let AI pick intelligent trims)
        if (userModifiedTrim) {
          if (clip.inTime !== undefined && clip.inTime > 0) {
            metadata.trim_start = clip.inTime;
          }
          if (clip.outTime !== undefined) {
            metadata.trim_end = clip.outTime;
          }
        }

        // Add comment only if we have editing notes
        if (comment) {
          metadata.comment = comment;
        }

        assetsMetadata.push(metadata);
        cumulativeVideoTime = timelineEnd;
      });

      // Process audio clips as overlays
      audioClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (!file || !file.s3Url) {
          throw new Error(`Missing S3 URL for clip: ${clip.name}`);
        }

        const sourceInTime = clip.inTime || 0;
        const sourceOutTime = clip.outTime || clip.duration || file.duration || 5;
        const audioDuration = sourceOutTime - sourceInTime;

        // Parse volume from prompt (e.g., "volume:0.5")
        const volumeMatch = clip.prompt?.match(/volume[:\s]*([\d.]+)/i);
        const volume = volumeMatch ? parseFloat(volumeMatch[1]) : 1.0;

        // Format duration as M:SS
        const minutes = Math.floor(audioDuration / 60);
        const seconds = Math.round(audioDuration % 60);
        const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        // Use actual clip name
        let audioName = clip.name || `audio_${index + 1}`;
        audioName = audioName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

        // Build comment with editing notes
        let audioComment = '';
        if (sourceInTime > 0) {
          audioComment = `start at :${Math.round(sourceInTime).toString().padStart(2, '0')}`;
        }
        if (volume !== 1.0) {
          if (audioComment) audioComment += ', ';
          audioComment += `volume ${volume}`;
        }

        const metadata: any = {
          url: file.s3Url,
          name: audioName,
          duration: formattedDuration,
        };

        if (audioComment) {
          metadata.comment = audioComment;
        }

        assetsMetadata.push(metadata);
      });
      
      // Process image clips as overlays or backgrounds
      imageClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (!file || !file.s3Url) {
          throw new Error(`Missing S3 URL for clip: ${clip.name}`);
        }

        // Determine if this is a background image or overlay
        const isBackground = clip.layerType === 'background' || clip.prompt?.toLowerCase().includes('background');

        // Use actual clip name
        let imageName = clip.name || (isBackground ? `background_${index + 1}` : `image_${index + 1}`);
        imageName = imageName.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();

        const metadata: any = {
          url: file.s3Url,
          name: imageName,
          // Width and height are required by Lambda for images (must be strings)
          width: String(file.width || 1920),
          height: String(file.height || 1080),
        };

        // Add comment if there's a prompt
        if (clip.prompt) {
          metadata.comment = clip.prompt;
        }

        assetsMetadata.push(metadata);
      });

      // Process luma matte clips - send as separate metadata for backend post-processing
      // DON'T add to main assets_metadata array - Lambda LLM gets confused by luma_matte type
      // Instead, we'll pass these separately and let backend inject them after Lambda returns
      const lumaMatteMetadata: any[] = [];
      lumaMatteClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (file && file.s3Url) {
          const lumaDuration = clip.duration || file.duration || 5;
          const metadata: any = {
            url: file.s3Url,
            s3Url: file.s3Url,
            name: `${clip.name}_luma_${index + 1}`,
            type: 'video',
            asset_type: 'luma_matte',
            track_type: 'luma_matte',
            duration: String(lumaDuration),
            layerType: 'mask',
            linkedPairId: file.linkedPairId || clip.linkedPairId,
            linkedVideoId: file.linkedVideoId,
          };
          lumaMatteMetadata.push(metadata);
          console.log(`🎭 Luma matte will be injected by backend: ${clip.name}, linkedPairId=${metadata.linkedPairId}`);
        }
      });

      // Process other clips if any
      otherClips.forEach((clip, index) => {
        const file = files.find(f => f.id === clip.sourceFileId);
        if (file && file.s3Url) {
          const otherDuration = clip.duration || file.duration || 5;
          const metadata: any = {
            url: file.s3Url,
            s3Url: file.s3Url,
            name: `${clip.name}_other_${index + 1}`,
            type: file.fileType || 'unknown',  // Lambda expects 'type'
            asset_type: file.fileType || 'unknown',  // Keep for backend compatibility
            track_type: 'overlay',
            duration: String(otherDuration),  // Duration must be STRING
          };
          assetsMetadata.push(metadata);
        }
      });

      // Add aspect ratio context to the prompt
      const directOutputConfig = resolveOutputConfig(aspectRatio, files);
      const displayRatio = aspectRatio === 'source'
        ? `${directOutputConfig.output_width}x${directOutputConfig.output_height} (Match Source)`
        : aspectRatio;
      const aspectRatioContext = `Output format: ${displayRatio} ${
        aspectRatio === '16:9' ? '(Landscape/YouTube)' :
        aspectRatio === '9:16' ? '(Portrait/TikTok/Reels)' :
        aspectRatio === '1:1' ? '(Square/Instagram)' :
        aspectRatio === '4:5' ? '(Instagram Portrait)' :
        aspectRatio === 'source' ? '' : ''
      }`;
      
      // Add explicit instructions for green screen handling
      const hasGreenScreenVideo = clips.some(clip => 
        clip.prompt?.toLowerCase().includes('green screen') || 
        clip.hasGreenScreen
      );
      const hasBackgroundLayer = clips.some(clip => 
        clip.layerType === 'background' || 
        clip.prompt?.toLowerCase().includes('background')
      );
      
      let greenScreenInstructions = '';
      if (hasGreenScreenVideo && hasBackgroundLayer) {
        greenScreenInstructions = `\n\nIMPORTANT COMPOSITING INSTRUCTIONS:
- This project contains GREEN SCREEN videos that must have chromaKey effect applied
- Apply chromaKey with color #97eca9 (Replicate's green) to remove green backgrounds
- Place videos with chromaKey ABOVE background images/videos in the layer order
- Background images/videos should be on the BOTTOM layer (no chromaKey)
- Preserve all audio from original videos at full volume (1.0)`;
      }
      
      const enhancedPrompt = `${masterPrompt}${greenScreenInstructions}\n\n${aspectRatioContext}`;

      // Choose endpoint based on render mode
      const endpoint = renderMode === 'direct'
        ? '/api/shotstack/generate-direct'
        : '/api/shotstack/generate';

      console.log(`Using ${renderMode} render mode - endpoint: ${endpoint}`);

      // Build request body based on render mode
      let requestBody: any;

      if (renderMode === 'direct') {
        // Direct mode: send explicit clip data for deterministic JSON generation
        const clipData = clips.map(clip => {
          // Find file by id or _id (MongoDB uses _id)
          const file = files.find(f => f.id === clip.sourceFileId || (f as any)._id === clip.sourceFileId);

          if (!file) {
            console.warn(`Could not find file for clip ${clip.name}, sourceFileId: ${clip.sourceFileId}`);
            console.log('Available file IDs:', files.map(f => ({ id: f.id, _id: (f as any)._id })));
          }

          const hasLumaMatte = file?.linkedMaskId || file?.greenScreenType === 'foreground-mask' || clip.linkedMaskId;
          const resolvedFileUrl = resolveVideoUrl(file?.s3Url, file?.s3Key);
          const resolvedClipUrl = resolveVideoUrl(clip.s3Url, clip.s3Key);
          const videoUrl = hasLumaMatte ? (resolvedFileUrl || resolvedClipUrl) : (file?.greenScreenUrl || resolvedFileUrl || resolvedClipUrl);

          // Ensure we have a URL - use clip's stored URL as fallback
          const finalUrl = videoUrl || resolvedClipUrl || clip.url;
          if (!finalUrl && clip.fileType === 'video') {
            console.error(`Missing URL for clip ${clip.name}, sourceFileId: ${clip.sourceFileId}`);
          }

          return {
            id: clip.id,
            name: clip.name,
            fileType: clip.fileType,
            layerType: clip.layerType,
            duration: clip.duration || file?.duration || 10,
            inTime: clip.inTime,
            outTime: clip.outTime,
            volume: clip.volume ?? 1.0,
            url: finalUrl,
            s3Url: clip.s3Url || file?.s3Url,
            s3Key: clip.s3Key || file?.s3Key,
            // Explicit Quick Render fields
            titleText: clip.titleText,
            titlePosition: clip.titlePosition,
            titleFont: clip.titleFont,
            chromaKey: clip.chromaKey || (clip.prompt?.includes('chromaKey') ? {
              enabled: true,
              threshold: parseInt(clip.prompt.match(/threshold:(\d+)/)?.[1] || '100'),
              halo: parseInt(clip.prompt.match(/halo:(\d+)/)?.[1] || '30')
            } : undefined),
            // Transition
            transitionOut: clip.transitionOut,
            // Image settings (Quick Render)
            imageMode: clip.imageMode,
            imagePosition: clip.imagePosition,
            imageDuration: clip.imageDuration,
            imageScale: clip.imageScale,
            // Multi-track overlay properties
            isOverlay: clip.isOverlay,
            track: clip.track,
            stackedOn: clip.stackedOn,
            overlayPosition: clip.overlayPosition,
            overlayScale: clip.overlayScale,
            // Caption settings
            showCaptions: clip.showCaptions,
            captionStyle: clip.captionStyle,
            captionFont: clip.captionFont,
            captionBackground: clip.captionBackground,
            captionFontColor: clip.captionFontColor,
            captionStrokeColor: clip.captionStrokeColor,
            // Transcript words for caption timing
            // If clip has edited referenceText, create synthetic word timings from it
            // Otherwise, look up from analysisResults (same pattern as UI detection)
            transcriptWords: clip.showCaptions ? (() => {
              // PRIORITY: If user edited the transcript text (referenceText), use that instead
              // This allows correcting transcription errors before rendering captions
              if (clip.referenceText && clip.referenceText.trim()) {
                const inTime = clip.inTime || 0;
                const outTime = clip.outTime || clip.duration || 10;
                const duration = outTime - inTime;
                const words = clip.referenceText.trim().split(/\s+/);
                const wordDuration = duration / words.length;

                // Create synthetic word objects with evenly distributed timing
                return words.map((word, idx) => ({
                  word: word,
                  start_time: inTime + (idx * wordDuration),
                  end_time: inTime + ((idx + 1) * wordDuration),
                }));
              }

              // First check clip and file analysisData
              if (clip.analysisData?.transcript?.words?.length > 0) return clip.analysisData.transcript.words;
              if (file?.analysisData?.transcript?.words?.length > 0) return file.analysisData.transcript.words;
              if (clip.analysisData?.transcript?.segments?.length > 0) return clip.analysisData.transcript.segments;
              if (file?.analysisData?.transcript?.segments?.length > 0) return file.analysisData.transcript.segments;

              // Look up from analysisResults (same pattern as ClipItem)
              const fileAnalysis = file?.id ? (() => {
                const match = Object.values(analysisResults).find((result: any) =>
                  result.status === 'completed' &&
                  (result.video_id === file.id || result.results?.video_id === file.id)
                );
                return match ? ((match as any).results || match) : null;
              })() : null;

              if ((fileAnalysis as any)?.transcript?.words?.length > 0) return (fileAnalysis as any).transcript.words;
              if ((fileAnalysis as any)?.transcript?.segments?.length > 0) return (fileAnalysis as any).transcript.segments;

              return [];
            })() : undefined,
          };
        });

        const directRenderConfig = resolveOutputConfig(aspectRatio, files);
        const directEffectiveRatio = aspectRatio === 'source'
          ? `${directRenderConfig.output_width}:${directRenderConfig.output_height}`
          : aspectRatio;
        requestBody = {
          clips: clipData,
          aspect_ratio: directEffectiveRatio,
          resolution: directRenderConfig.shotstack_resolution || 'hd',
          output_width: directRenderConfig.output_width,
          output_height: directRenderConfig.output_height,
          luma_matte_metadata: lumaMatteMetadata,
          filter: exportFilter !== 'none' ? exportFilter : undefined,
          colorAdjustments: hasColorAdjustments(colorAdjustments) ? colorAdjustments : undefined,
          // Template settings for styling
          template: activeTemplate ? {
            colors: activeTemplate.settings?.colors,
            fonts: activeTemplate.settings?.fonts,
            titles: activeTemplate.settings?.titles,
            timeline: activeTemplate.settings?.timeline,
            logo: activeTemplate.settings?.logo,
            audio: activeTemplate.settings?.audio,
          } : undefined,
        };
      } else {
        // Lambda mode: use existing format with assets_metadata
        // Use creative brief if provided, otherwise fall back to auto-generated prompt
        let aiPrompt = creativeBrief.trim()
          ? creativeBrief
          : enhancedPrompt;

        // If we have script analysis with scenes/edit points, include it in the prompt
        // This tells the LLM exactly which clips to use and their trim times
        if (scriptAnalysis && scriptAnalysis.scenes && scriptAnalysis.scenes.length > 0) {
          const editPlanSection = `

=== PRE-ANALYZED EDIT PLAN ===
The following scenes have been analyzed from the script with specific clip selections and trim times.
IMPORTANT: Use these exact clips and trim times as specified. Do not re-interpret or change the clip selections.

${scriptAnalysis.scenes.map((scene, idx) => {
  const clipDetails = scene.suggestedClips.map(clip => {
    if (typeof clip === 'string') {
      return `  - Clip: "${clip}"`;
    } else {
      const trimInfo = clip.trim
        ? ` (trim: ${clip.trim.from}s to ${clip.trim.to}s)`
        : '';
      return `  - Clip: "${clip.assetName}"${trimInfo} - ${clip.reason}`;
    }
  }).join('\n');

  return `Scene ${idx + 1}: ${scene.description}
Script Line: "${scene.scriptLine}"
Duration: ${scene.estimatedDuration}s
Clips:
${clipDetails}`;
}).join('\n\n')}

=== END EDIT PLAN ===
`;
          aiPrompt = aiPrompt + editPlanSection;
          console.log('Including script analysis edit plan with', scriptAnalysis.scenes.length, 'scenes');
        }

        const lambdaOutputConfig = resolveOutputConfig(aspectRatio, files);
        const lambdaEffectiveRatio = aspectRatio === 'source'
          ? `${lambdaOutputConfig.output_width}:${lambdaOutputConfig.output_height}`
          : aspectRatio;
        requestBody = {
          company_name: currentProject,
          users_text: aiPrompt,
          users_objective: `Create an enhanced video using ${clips.length} clips in ${lambdaEffectiveRatio} aspect ratio`,
          assets_metadata: assetsMetadata,
          luma_matte_metadata: lumaMatteMetadata,
          aspect_ratio: lambdaEffectiveRatio,
          shotstack_resolution: lambdaOutputConfig.shotstack_resolution || 'hd',
          output_width: lambdaOutputConfig.output_width,
          output_height: lambdaOutputConfig.output_height,
          filter: exportFilter !== 'none' ? exportFilter : undefined,
          colorAdjustments: hasColorAdjustments(colorAdjustments) ? colorAdjustments : undefined,
          debug: false
        };

        console.log('AI Creative request with transcript-aware assets:', {
          prompt: aiPrompt.substring(0, 200) + '...',
          hasScriptAnalysis: !!(scriptAnalysis && scriptAnalysis.scenes),
          scenesCount: scriptAnalysis?.scenes?.length || 0,
          assetsWithTranscripts: assetsMetadata.filter((a: any) => a.comment?.includes('Transcript')).length,
          totalAssets: assetsMetadata.length
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to generate Shotstack JSON');
      }

      const result = await response.json();
      
      // Update the queued video with Shotstack response
      setRenderedVideos(prev => {
        const updated = prev.map(v => v.id === queuedVideo.id ? {
          ...v,
          status: result.renderStatus === 'submitted' ? 'rendering' : 'processing',
          renderId: result.renderId,
          renderStatus: result.renderStatus,
          shotstackJson: result.shotStackJson,
          progress: 10,
        } : v);
        localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updated));
        return updated;
      });

      if (result.renderStatus === 'submitted' && result.renderId) {
        toast({
          title: 'Video Rendering Started!',
          description: renderMode === 'direct'
            ? 'Quick Render: Shotstack is processing your video.'
            : 'AI Creative: Shotstack is rendering your enhanced video.',
        });
        
        // Start polling for render status
        pollRenderStatus(queuedVideo.id, result.renderId);
      }
    } catch (error) {
      console.error('Render failed:', error);
      
      // Update the queued video to failed status
      setRenderedVideos(prev => {
        const updated = prev.map(v => v.id === queuedVideo.id ? {
          ...v,
          status: 'failed',
          error: error.message,
        } : v);
        localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updated));
        return updated;
      });
      
      toast({
        title: 'Render Failed',
        description: error.message || 'An error occurred during processing',
        variant: 'destructive'
      });
    }
  };

  // Poll render status
  const pollRenderStatus = async (videoId: string, renderId: string) => {
    const startTime = Date.now();
    const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
    let consecutiveErrors = 0;
    const MAX_ERRORS = 10;

    const pollInterval = setInterval(async () => {
      // Timestamp-based timeout check (reliable even when browser throttles timers)
      if (Date.now() - startTime > TIMEOUT_MS) {
        clearInterval(pollInterval);
        console.log('Render timed out after 15 minutes (timestamp check)');

        setRenderedVideos(prev => {
          const updated = prev.map(v => v.id === videoId && v.status !== 'rendered' && v.status !== 'failed' ? {
            ...v,
            status: 'failed' as const,
            error: 'Render timed out after 15 minutes',
          } : v);
          localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updated));
          return updated;
        });

        toast({
          title: 'Render Timed Out',
          description: 'The video rendering took longer than expected and was cancelled.',
          variant: 'destructive'
        });
        return;
      }

      try {
        const response = await fetch(`/api/shotstack/render-status/${renderId}`);
        if (!response.ok) {
          throw new Error('Failed to check render status');
        }

        const statusData = await response.json();
        const status = statusData.status;
        const progress = statusData.progress || 0;
        consecutiveErrors = 0; // Reset on success

        // Update video with current status
        setRenderedVideos(prev =>
          prev.map(v => v.id === videoId ? {
            ...v,
            renderStatus: status,
            progress: progress,
            url: statusData.videoUrl,
          } : v)
        );

        if (status === 'done') {
          clearInterval(pollInterval);

          // Check if this video has finishing touches that need auto-processing
          const currentVideo = renderedVideos.find(v => v.id === videoId);
          const needsAudioEnhancement = currentVideo?.finishingTouches?.enhanceAudio;

          setRenderedVideos(prev =>
            prev.map(v => v.id === videoId ? {
              ...v,
              status: 'rendered',
              url: statusData.videoUrl,
              progress: 100,
              pipelineStage: needsAudioEnhancement ? 'enhancing-audio' : 'complete',
            } : v)
          );

          // Save to localStorage
          const updatedVideos = renderedVideos.map(v =>
            v.id === videoId ? { ...v, status: 'rendered', url: statusData.videoUrl, progress: 100 } : v
          );
          localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updatedVideos));

          // Auto-trigger audio enhancement if finishing touches requested it
          if (needsAudioEnhancement && statusData.videoUrl) {
            toast({
              title: 'Video rendered — enhancing audio...',
              description: 'Automatically starting audio enhancement as requested.',
            });
            // Call handleEnhanceAudio directly (skip credit confirmation since user opted in during plan)
            const renderedVideo: RenderedVideo = {
              ...currentVideo!,
              status: 'rendered',
              url: statusData.videoUrl,
              videoUrl: statusData.videoUrl,
            };
            audioEnhancement.handleEnhanceAudio(renderedVideo);
          } else {
            toast({
              title: 'Video Ready!',
              description: 'Your enhanced video has been rendered successfully.',
            });
          }
        } else if (status === 'failed') {
          clearInterval(pollInterval);

          // Get detailed error information
          const errorMessage = statusData.errorDetails?.error || statusData.errorDetails?.message || statusData.error || 'Render failed';
          const errorDetails = statusData.errorDetails;

          setRenderedVideos(prev =>
            prev.map(v => v.id === videoId ? {
              ...v,
              status: 'failed',
              error: errorMessage,
              errorDetails: errorDetails,
            } : v)
          );

          console.log('Render failed with details:', errorDetails);

          toast({
            title: 'Render Failed',
            description: errorMessage,
            variant: 'destructive'
          });
        }
      } catch (error) {
        consecutiveErrors++;
        console.error(`Error polling render status (${consecutiveErrors}/${MAX_ERRORS}):`, error);

        if (consecutiveErrors >= MAX_ERRORS) {
          clearInterval(pollInterval);

          setRenderedVideos(prev => {
            const updated = prev.map(v => v.id === videoId && v.status !== 'rendered' && v.status !== 'failed' ? {
              ...v,
              status: 'failed' as const,
              error: 'Lost connection to render service after multiple retries',
            } : v);
            localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updated));
            return updated;
          });

          toast({
            title: 'Render Failed',
            description: 'Lost connection to the render service. Please try again.',
            variant: 'destructive'
          });
        }
      }
    }, 5000); // Poll every 5 seconds
  };
  // Wire up forward ref for plan mode hook
  pollRenderStatusRef.current = pollRenderStatus;

  // Resume polling for any in-progress renders on mount
  useEffect(() => {
    const inProgressRenders = renderedVideos.filter(v =>
      (v.status === 'rendering' || v.status === 'processing') && v.renderId
    );

    if (inProgressRenders.length > 0) {
      console.log(`🔄 Resuming polling for ${inProgressRenders.length} in-progress render(s)`);
      inProgressRenders.forEach(video => {
        if (video.renderId) {
          pollRenderStatus(video.id, video.renderId);
        }
      });
    }
  }, []); // Only run on mount

  // Download JSON configuration
  const downloadJson = (video: RenderedVideo) => {
    const jsonStr = JSON.stringify(video.configuration, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${video.name}_configuration.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "JSON downloaded",
      description: `Configuration saved as ${video.name}_configuration.json`,
    });
  };

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <main
        className="mx-auto"
        style={{
          maxWidth: 1440,
          padding: '0 16px 96px',
          background: 'var(--hj-bg)',
          color: 'var(--hj-ink)',
          minHeight: 'calc(100vh - 68px)',
        }}
      >
        <div
          className="mx-auto"
          style={{ maxWidth: 540, padding: '120px 16px 0' }}
        >
          <div
            className="text-center"
            style={{
              padding: '40px 32px',
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div
              className="mx-auto flex items-center justify-center"
              style={{
                width: 64,
                height: 64,
                borderRadius: 999,
                background: 'var(--hj-blue-tint)',
                color: 'var(--hj-yellow-ink)',
              }}
            >
              <FolderOpen className="w-7 h-7" />
            </div>
            <h2
              className="font-hj-display m-0"
              style={{ marginTop: 16, fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}
            >
              Sign in to keep editing.
            </h2>
            <p
              className="m-0"
              style={{ marginTop: 12, color: 'var(--hj-ink-3)', fontSize: 14, lineHeight: 1.55 }}
            >
              AI Creative needs your account to reach cloud storage, transcripts, and renders.
            </p>
            <button
              type="button"
              onClick={() => setShowLoginModal(true)}
              className="inline-flex items-center justify-center gap-2"
              style={{
                marginTop: 22,
                padding: '11px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              Sign in
            </button>
          </div>
        </div>
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            setShowLoginModal(false);
            setIsAuthenticated(true);
          }}
        />
      </main>
    );
  }

  // --- Shared JSX elements to eliminate sidebar/collapsed/mobile duplication ---

  const scriptPanelElement = renderMode === 'lambda' && (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-medium flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          Script
          {scriptContent && <Check className="h-3 w-3 text-muted-foreground" />}
          {scriptAnalysis && <Check className="h-3 w-3 text-muted-foreground" />}
        </h3>
      </div>
      <ScriptDropZone
        onScriptLoaded={handleScriptLoaded}
        onAnalyze={handleAnalyzeScript}
        scriptContent={scriptContent}
        scriptFileName={scriptFileName}
        isAnalyzing={isAnalyzingScript}
        assetsCount={files.length}
      />
    </Card>
  );

  const planModeChatElement = renderMode === 'lambda' && planModeActive && (
    <PlanModeChat
      conversation={planConversation}
      questions={planQuestions}
      editPlan={editPlan}
      isProcessing={isPlanProcessing}
      isPlanComplete={editPlan !== null}
      isRendering={isPlanRendering}
      scriptContent={scriptContent}
      scriptFileName={scriptFileName}
      finishingTouches={finishingTouches}
      files={files}
      onSendMessage={handlePlanMessage}
      onQuestionSelect={handlePlanQuestionSelect}
      onApproveAndRender={handleApprovePlan}
      onRefinePlan={handleRefinePlan}
      onScriptUpload={handleScriptLoaded}
      onFinishingTouchesChange={setFinishingTouches}
      aspectRatio={aspectRatio}
      onAspectRatioChange={setAspectRatio}
      onClose={handleClosePlanMode}
    />
  );

  const scriptAnalysisElement = renderMode === 'lambda' && scriptAnalysis && showScriptPanel && !planModeActive && (
    <ScriptAnalysisChat
      analysis={scriptAnalysis}
      answers={scriptAnswers}
      onAnswerChange={handleScriptAnswerChange}
      onGeneratePlan={handleGenerateScriptPlan}
      isGenerating={isGeneratingScriptPlan}
      onClose={() => setShowScriptPanel(false)}
      conversation={scriptConversation}
      onSendMessage={handleScriptRefinement}
      isRefining={isRefiningScript}
    />
  );

  const timelineElement = (
    <div data-tour="timeline">
      <TimelineSequence
        clips={clips}
        files={files}
        analysisResults={analysisResults}
        onClipDrop={handleClipDrop}
        onClipRemove={handleClipRemove}
        onClipReorder={handleClipReorder}
        onPromptChange={handlePromptChange}
        onTimeChange={handleTimeChange}
        onPreviewClip={handlePreviewClip}
        onTitleChange={handleTitleChange}
        onVolumeChange={handleVolumeChange}
        onTransitionChange={handleTransitionChange}
        onEffectChange={handleEffectChange}
        onImageSettingsChange={handleImageSettingsChange}
        onCaptionsChange={handleCaptionsChange}
        onReferenceTextChange={handleReferenceTextChange}
        onLayerTypeChange={handleLayerTypeChange}
        onStackClip={handleStackClip}
        onUnstackClip={handleUnstackClip}
        onOverlaySettingsChange={handleOverlaySettingsChange}
        renderMode={renderMode}
      />
    </div>
  );

  const handleApplyTemplate = (template: VideoTemplate) => {
    setActiveTemplate(template);
    toast({
      title: 'Template Applied',
      description: `"${template.name}" settings will be used for rendering.`,
    });
  };

  const templatesPanelElement = (compact?: boolean) => showTemplatesPanel && (
    <Card className={compact ? "p-3" : "p-4"}>
      <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-3'}`}>
        <h3 className={`${compact ? 'text-xs' : 'text-sm'} font-medium flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
          <Palette className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
          Templates
        </h3>
        <Button
          variant="ghost"
          size="sm"
          className={compact ? "h-5 w-5 p-0" : "h-6 w-6 p-0"}
          onClick={() => setShowTemplatesPanel(false)}
        >
          <X className={compact ? "h-3 w-3" : "h-4 w-4"} />
        </Button>
      </div>
      <TemplatesTab
        onApplyTemplate={handleApplyTemplate}
        currentProjectSettings={activeTemplate?.settings}
        projectFiles={files}
      />
    </Card>
  );

  return (
    <div
      className="min-h-screen"
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
    >
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        disableOverlayClose
        disableCloseOnEsc
        spotlightClicks={false}
        scrollToFirstStep
        scrollOffset={100}
        callback={handleTourCallback}
        styles={{
          options: {
            primaryColor: 'hsl(var(--primary))',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: '8px',
          },
          buttonNext: {
            borderRadius: '6px',
          },
          buttonBack: {
            borderRadius: '6px',
          },
          buttonSkip: {
            borderRadius: '6px',
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Got it!',
          next: 'Next',
          skip: 'Skip tour',
        }}
      />

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Breadcrumb tying the editor back to the rest of the app */}
        <div className="flex items-center gap-2.5" style={{ fontSize: 13 }}>
          <a
            href="/"
            className="inline-flex items-center gap-1.5"
            style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}
          >
            <span aria-hidden style={{ fontSize: 11 }}>‹</span>
            Home
          </a>
          <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
          <span style={{ color: 'var(--hj-ink)' }}>AI Creative</span>
        </div>

        {/* Compact Header */}
        <ProjectHeader
          projects={projects}
          currentProject={currentProject}
          activeTemplate={activeTemplate}
          showTemplatesPanel={showTemplatesPanel}
          isAuthenticated={isAuthenticated}
          onProjectChange={handleProjectChange}
          onCreateProject={handleCreateProject}
          onRenameProject={handleRenameProject}
          onDeleteProject={handleDeleteProject}
          onImport={() => {
            if (isAuthenticated) {
              setShowImporter(true);
            } else {
              setShowLoginModal(true);
            }
          }}
          onToggleTemplates={() => setShowTemplatesPanel(!showTemplatesPanel)}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
        />

        {/* Desktop: Side Panel Layout */}
        <div className="hidden lg:flex gap-4">
          {/* Left Sidebar - Assets */}
          {!sidebarCollapsed && (
            <div className="w-72 flex-shrink-0 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Assets</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={toggleSidebar}
                    >
                      <LayoutList className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>1-column layout</TooltipContent>
                </Tooltip>
              </div>

              {/* Project Bin - Compact mode */}
              <div data-tour="project-bin">
                <ProjectBin
                  files={files}
                  onFileSelect={handleFileSelect}
                  selectedFiles={selectedFiles}
                  onDragStart={handleDragStart}
                  onAddToTimeline={renderMode === 'direct' ? (file) => handleClipDrop(file.id) : undefined}
                  onAddToTimelineWithOptions={renderMode === 'direct' ? (file, options) => handleClipDropWithOptions(file.id, options) : undefined}
                  onAnalyze={handleAnalyze}
                  onRemoveBackground={handleRemoveBackground}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  analysisResults={analysisResults}
                  onInsertText={renderMode === 'direct' ? handleInsertText : undefined}
                  onFileRemove={handleFileRemove}
                  onTranscriptEdit={handleTranscriptEdit}
                  onSpeakerEdit={handleSpeakerEdit}
                  onOpenAIGenerator={() => setShowAIGenerator(true)}
                  compact
                />
              </div>

              {/* Templates Panel - Sidebar */}
              {templatesPanelElement(true)}

              {/* Script, Plan Mode, Script Analysis - Sidebar */}
              {scriptPanelElement}
              {planModeChatElement}
              {scriptAnalysisElement}
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* When sidebar collapsed, show Project Bin inline */}
            {sidebarCollapsed && (
              <>
                {/* Toggle back to sidebar */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Assets</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={toggleSidebar}
                      >
                        <Columns2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>2-column layout</TooltipContent>
                  </Tooltip>
                </div>
                {/* Project Bin - Full width inline */}
                <div data-tour="project-bin">
                  <ProjectBin
                    files={files}
                    onFileSelect={handleFileSelect}
                    selectedFiles={selectedFiles}
                    onDragStart={handleDragStart}
                    onAddToTimeline={renderMode === 'direct' ? (file) => handleClipDrop(file.id) : undefined}
                    onAddToTimelineWithOptions={renderMode === 'direct' ? (file, options) => handleClipDropWithOptions(file.id, options) : undefined}
                    onAnalyze={handleAnalyze}
                    onRemoveBackground={handleRemoveBackground}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    analysisResults={analysisResults}
                    onInsertText={renderMode === 'direct' ? handleInsertText : undefined}
                    onFileRemove={handleFileRemove}
                    onTranscriptEdit={handleTranscriptEdit}
                  onSpeakerEdit={handleSpeakerEdit}
                    onOpenAIGenerator={() => setShowAIGenerator(true)}
                  />
                </div>
                {/* Templates, Script, Plan Mode, Script Analysis - Inline when sidebar collapsed */}
                {templatesPanelElement()}
                {scriptPanelElement}
                {planModeChatElement}
                {scriptAnalysisElement}
              </>
            )}

            {/* Simple Upload Dropzone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              data-tour="media-layers"
            >
              <input {...getInputProps()} />
              <div className="flex items-center justify-center gap-2">
                <Upload className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {isDragActive ? 'Drop files here...' : 'Upload videos, images, or audio'}
                </span>
              </div>
            </div>

            {/* Render Mode Toggle - Above Timeline */}
            <div className={`flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50 ${planModeActive ? 'hidden sm:flex' : ''}`}>
              <div className="flex items-center gap-3">
                <div data-tour="render-mode" className={`flex items-center gap-2 text-sm ${planModeActive ? 'hidden' : ''}`}>
                  <span className={`px-3 py-1.5 rounded-md transition-all ${renderMode === 'direct' ? 'font-semibold text-gray-900 bg-white border border-gray-300 shadow-sm' : 'text-foreground/70'}`}>
                    Quick Render
                  </span>
                  <Switch
                    checked={renderMode === 'lambda'}
                    onCheckedChange={handleRenderModeToggle}
                  />
                  <span className={`px-3 py-1.5 rounded-md transition-all ${renderMode === 'lambda' ? 'font-semibold text-gray-900 bg-white border border-gray-300 shadow-sm' : 'text-foreground/70'}`}>
                    AI Creative
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p><strong>Quick Render:</strong> You control cuts, titles, volume, transitions.</p>
                      <p className="mt-1"><strong>AI Creative:</strong> AI makes editing decisions based on your brief.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  {renderMode === 'direct'
                    ? '• Manual control over all settings'
                    : '• AI reads transcripts & makes creative decisions'
                  }
                </span>
              </div>
              {/* Aspect ratio & Start AI Edit for AI Creative */}
              {renderMode === 'lambda' && (
                <div className="flex items-center gap-2">
                  <Select value={aspectRatio} onValueChange={setAspectRatio}>
                    <SelectTrigger className="h-8 w-[160px] border-border bg-secondary/50 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-border bg-popover">
                      <SelectItem value="source" className="text-xs">Match Source</SelectItem>
                      <SelectItem value="16:9" className="text-xs">16:9 (Landscape)</SelectItem>
                      <SelectItem value="9:16" className="text-xs">9:16 (Portrait/TikTok)</SelectItem>
                      <SelectItem value="1:1" className="text-xs">1:1 (Square)</SelectItem>
                      <SelectItem value="4:5" className="text-xs">4:5 (Instagram)</SelectItem>
                    </SelectContent>
                  </Select>
                  {files.length > 0 && !planModeActive && (
                    <Button
                      size="sm"
                      onClick={handleStartPlanMode}
                      disabled={isPlanProcessing}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Start AI Edit
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Timeline/Sequence - Desktop (Only shown in Quick Render mode) */}
            {renderMode === 'direct' && timelineElement}

            {/* Render Section - Inline in 2-column mode */}
            {!sidebarCollapsed && clips.length > 0 && (
              <div className="p-4 border border-border rounded-lg bg-muted/20">
                <RenderControlsPanel
                  showMasterPrompt={showMasterPrompt}
                  setShowMasterPrompt={setShowMasterPrompt}
                  isEditingMasterPrompt={isEditingMasterPrompt}
                  setIsEditingMasterPrompt={setIsEditingMasterPrompt}
                  editableMasterPrompt={editableMasterPrompt}
                  setEditableMasterPrompt={setEditableMasterPrompt}
                  generateMasterPrompt={generateMasterPrompt}
                  onMasterPromptSaved={() => toast({ title: "Master prompt saved", description: "Your custom prompt will be used for rendering" })}
                  aspectRatio={aspectRatio}
                  setAspectRatio={setAspectRatio}
                  renderMode={renderMode}
                  onRenderModeToggle={handleRenderModeToggle}
                  activeTemplate={activeTemplate}
                  setActiveTemplate={setActiveTemplate}
                  onShowTemplates={() => setShowTemplatesPanel(true)}
                  exportFilter={exportFilter}
                  setExportFilter={setExportFilter}
                  colorAdjustments={colorAdjustments}
                  setColorAdjustments={setColorAdjustments}
                  editPlan={editPlan}
                  isPlanApproved={isPlanApproved}
                  planModeActive={planModeActive}
                  isPlanProcessing={isPlanProcessing}
                  onStartPlanMode={handleStartPlanMode}
                  scriptFileName={scriptFileName}
                  filesCount={files.length}
                  clipsCount={clips.length}
                  isLoadingPreview={isLoadingPreview}
                  onPreviewAI={handlePreviewAI}
                  onQuickPreview={handleQuickPreview}
                  onRender={handleRender}
                  compact
                />
              </div>
            )}

            {/* AI Edit Preview Section */}
            {showPreviewPanel && (
              <AIEditPreview
                aiPreview={aiPreview}
                isLoadingPreview={isLoadingPreview}
                files={files}
                clips={clips}
                onClose={() => {
                  setShowPreviewPanel(false);
                  setAiPreview(null);
                }}
                onEditInTimeline={handleEditInTimeline}
                onRenderFromPreview={handleRenderFromPreview}
                onReorderClips={reorderAiPreviewClips}
                onDuplicateClip={duplicateAiPreviewClip}
                onDeleteClip={deleteAiPreviewClip}
                onUpdateClipTrim={updateAiPreviewClipTrim}
                onUpdateTextOverlay={updateAiPreviewTextOverlay}
                onUpdateTextOverlayTiming={updateAiPreviewTextOverlayTiming}
                onDeleteTextOverlay={deleteAiPreviewTextOverlay}
              />
            )}

            {/* Inline Quick Preview Section */}
            {showQuickPreviewPanel && quickPreview && (
              <QuickPreviewPanel
                quickPreview={quickPreview}
                onClose={() => {
                  setShowQuickPreviewPanel(false);
                  setQuickPreview(null);
                }}
                onRender={() => {
                  setShowQuickPreviewPanel(false);
                  handleRender();
                }}
              />
            )}

            {/* Rendered Videos Section - Inside main content area */}
            {(renderedVideos.length > 0 || savedRenders.length > 0) && (
              <div className="mt-6">
              <RenderHistory
                renderedVideos={renderedVideos}
                savedRenders={savedRenders}
                youtubeConnected={youtubeConnected}
                onViewVideo={(video) => setSelectedVideoModal(video)}
                onViewJson={(video) => setSelectedJsonView(video)}
                onDownload={(url) => window.open(url, '_blank')}
                onSchedule={(video) => {
                  setScheduleVideoData({
                    videoUrl: video.url!,
                    thumbnailUrl: video.thumbnailUrl,
                    renderId: video.renderId,
                  });
                  setShowScheduleModal(true);
                }}
                onYoutubePublish={(video) => openYoutubePublishModal(video)}
                onReimport={(video) => handleReimportRenderedVideo(video)}
                onSaveToLibrary={(video) => handleSaveToLibrary(video)}
                onDeleteFromHistory={(videoId) => handleDeleteRenderedVideo(videoId)}
                onDeleteFromLibrary={(videoId) => handleDeleteFromLibrary(videoId)}
                onRemoveFailedRender={(videoId) => {
                  const updated = renderedVideos.filter(v => v.id !== videoId);
                  setRenderedVideos(updated);
                  localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(updated));
                }}
                onViewErrorDetails={(video) => {
                  const errorInfo = video.errorDetails || { error: video.error || 'Unknown error' };
                  alert(`Render Error Details:

Error: ${errorInfo.error || 'Unknown'}
Message: ${errorInfo.message || 'No additional details'}

Full Details:
${JSON.stringify(errorInfo.details || {}, null, 2)}`);
                }}
                onEnhanceAudio={(video, mode) => audioEnhancement.executeEnhanceAudio(video, mode)}
                onRemoveBackground={(video) => bgReplacement.executeRemoveBackground(video)}
                onIteratePlan={(video) => loadPlanFromRender(video)}
              />
              </div>
            )}

          </div>
        </div>

        {/* Mobile/Tablet: Vertical Stack Layout */}
        <div className="lg:hidden space-y-4">
          {/* Simple Upload Dropzone - Mobile */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            data-tour="media-layers"
          >
            <input {...getInputProps()} />
            <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">Upload Media</p>
            <p className="text-xs text-muted-foreground mt-1">
              {isDragActive ? 'Drop files here...' : 'Videos, images, or audio files'}
            </p>
          </div>

          {/* Project Bin - Mobile */}
          <div data-tour="project-bin">
            <ProjectBin
              files={files}
              onFileSelect={handleFileSelect}
              selectedFiles={selectedFiles}
              onDragStart={handleDragStart}
              onAddToTimeline={renderMode === 'direct' ? (file) => handleClipDrop(file.id) : undefined}
              onAnalyze={handleAnalyze}
              onRemoveBackground={handleRemoveBackground}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              analysisResults={analysisResults}
              onInsertText={renderMode === 'direct' ? handleInsertText : undefined}
              onFileRemove={handleFileRemove}
              onTranscriptEdit={handleTranscriptEdit}
              onSpeakerEdit={handleSpeakerEdit}
              onOpenAIGenerator={() => setShowAIGenerator(true)}
            />
          </div>

          {/* Templates Panel - Mobile */}
          {templatesPanelElement()}

          {/* Timeline/Sequence - Mobile (Only shown in Quick Render mode) */}
          {renderMode === 'direct' && timelineElement}
        </div>

        {/* Master Prompt and Render Section - shown full width when sidebar collapsed (1-column) or on mobile */}
        {clips.length > 0 && (
          <div className={`p-4 border border-border rounded-lg bg-muted/20 ${!sidebarCollapsed ? 'hidden' : ''}`}>
            <RenderControlsPanel
              showMasterPrompt={showMasterPrompt}
              setShowMasterPrompt={setShowMasterPrompt}
              isEditingMasterPrompt={isEditingMasterPrompt}
              setIsEditingMasterPrompt={setIsEditingMasterPrompt}
              editableMasterPrompt={editableMasterPrompt}
              setEditableMasterPrompt={setEditableMasterPrompt}
              generateMasterPrompt={generateMasterPrompt}
              onMasterPromptSaved={() => toast({ title: "Master prompt saved", description: "Your custom prompt will be used for rendering" })}
              aspectRatio={aspectRatio}
              setAspectRatio={setAspectRatio}
              renderMode={renderMode}
              onRenderModeToggle={handleRenderModeToggle}
              activeTemplate={activeTemplate}
              setActiveTemplate={setActiveTemplate}
              onShowTemplates={() => setShowTemplatesPanel(true)}
              exportFilter={exportFilter}
              setExportFilter={setExportFilter}
              colorAdjustments={colorAdjustments}
              setColorAdjustments={setColorAdjustments}
              editPlan={editPlan}
              isPlanApproved={isPlanApproved}
              planModeActive={planModeActive}
              isPlanProcessing={isPlanProcessing}
              onStartPlanMode={handleStartPlanMode}
              scriptFileName={scriptFileName}
              filesCount={files.length}
              clipsCount={clips.length}
              isLoadingPreview={isLoadingPreview}
              onPreviewAI={handlePreviewAI}
              onQuickPreview={handleQuickPreview}
              onRender={handleRender}
            />
          </div>
        )}

      </div>

      {/* Modals */}
      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />
      
      {/* Video Importer */}
      <CastingVideoImporter
        open={showImporter}
        onOpenChange={(open) => {
          setShowImporter(open);
          // If importer was closed, re-check auth status
          if (!open) {
            const hasToken = document.cookie.includes('token=');
            setIsAuthenticated(hasToken);
          }
        }}
        onVideosImported={handleVideosImported}
      />

      {/* AI Asset Generator Modal */}
      <AIAssetGenerator
        isOpen={showAIGenerator}
        onClose={() => setShowAIGenerator(false)}
        onGenerate={handleAIGenerate}
        timelineDuration={clips.reduce((sum, clip) => sum + clip.duration, 0) || undefined}
      />

      {/* Welcome Dialog - Choose Render Mode */}
      <AlertDialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Welcome to Video Enhancer</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 pt-2">
              <p className="text-foreground">Choose your editing mode:</p>
              <div className="space-y-3">
                <div className="border rounded-lg p-4 bg-muted/30">
                  <h3 className="font-semibold text-sm mb-1">Quick Render</h3>
                  <p className="text-xs text-foreground/70">
                    You control everything: cuts, titles, volume, transitions, effects.
                    Fast and reliable.
                  </p>
                </div>
                <div className="border rounded-lg p-4 bg-purple-500/10 border-purple-500/30">
                  <h3 className="font-semibold text-sm mb-1 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    AI Creative
                  </h3>
                  <p className="text-xs text-foreground/70">
                    Let AI make editing decisions based on your creative brief and video transcripts.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                You can switch between modes anytime using the toggle above the timeline.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:flex-1"
              onClick={() => {
                setRenderMode('direct');
                localStorage.setItem('hasSeenWelcomeDialog', 'true');
                setShowWelcomeDialog(false);
                // Start tour if user hasn't completed it
                if (!localStorage.getItem('videoEnhancerTourCompleted')) {
                  setTimeout(() => setRunTour(true), 500); // Small delay for dialog to close
                }
              }}
            >
              Start with Quick Render
            </Button>
            <Button
              className="w-full sm:flex-1 bg-purple-600 hover:bg-purple-700"
              onClick={() => {
                setRenderMode('lambda');
                localStorage.setItem('hasSeenWelcomeDialog', 'true');
                setShowWelcomeDialog(false);
                // Start tour if user hasn't completed it
                if (!localStorage.getItem('videoEnhancerTourCompleted')) {
                  setTimeout(() => setRunTour(true), 500); // Small delay for dialog to close
                }
              }}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Start with AI Creative
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Creative Mode Warning Dialog */}
      <AlertDialog open={showAICreativeWarning} onOpenChange={setShowAICreativeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              AI Creative Mode - Beta
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                AI Creative mode is currently in development and results may be unpredictable.
              </p>
              <p>
                For total control over your video edits, choose <strong>Quick Render</strong> mode instead.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on Quick Render</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAICreativeMode}>
              Continue to AI Creative
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Choice Dialog */}
      <Dialog open={showImportChoice} onOpenChange={setShowImportChoice}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Videos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You're importing videos from:
            </p>
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{pendingImportData?.projectName}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {pendingImportData?.videos.length} video{pendingImportData?.videos.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <p className="text-sm">
              Would you like to:
            </p>
            
            <div className="space-y-3">
              <Button
                variant="default"
                className="w-full justify-start"
                onClick={() => processImport(true)}
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Create new project "{pendingImportData?.projectName}"
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => processImport(false)}
              >
                <Import className="w-4 h-4 mr-2" />
                Add to current project "{currentProject}"
              </Button>
            </div>
            
            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowImportChoice(false);
                  setPendingImportData(null);
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove BG Output Type Dialog (for rendered videos) */}
      <Dialog open={bgReplacement.outputTypeDialogOpen} onOpenChange={bgReplacement.setOutputTypeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Background</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose how to remove the background from your video:
            </p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="renderedOutputType"
                  value="foreground-mask"
                  checked={bgReplacement.selectedOutputType === 'foreground-mask'}
                  onChange={() => bgReplacement.setSelectedOutputType('foreground-mask')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-sm">⚪ Foreground Mask (Recommended)</div>
                  <p className="text-xs text-muted-foreground">
                    Black & white mask for Shotstack compositing. Best edge quality, no color artifacts.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="renderedOutputType"
                  value="green-screen"
                  checked={bgReplacement.selectedOutputType === 'green-screen'}
                  onChange={() => bgReplacement.setSelectedOutputType('green-screen')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-sm">🟢 Green Screen</div>
                  <p className="text-xs text-muted-foreground">
                    Replace background with green for chromaKey removal. Best for standard compositing.
                  </p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => bgReplacement.setOutputTypeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => bgReplacement.confirmRemoveBackground()}>
              Remove Background
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Background Replacement Wizard */}
      <BackgroundReplacementWizard
        open={bgReplacement.wizardOpen}
        step={bgReplacement.wizardStep}
        video={bgReplacement.targetVideo}
        maskUrl={bgReplacement.maskUrl}
        projectFiles={files}
        onClose={bgReplacement.closeWizard}
        onKeepGreenScreen={bgReplacement.keepGreenScreen}
        onSelectBackground={() => bgReplacement.setWizardStep('select-background')}
        onComposite={(url, type) => bgReplacement.executeCompositeBackground(url, type)}
        onGenerateAsset={(type, prompt, options) => handleAIGenerate(type, prompt, options)}
        generatingJob={aiGeneratingJob}
      />

      {/* JSON View Dialog */}
      <Dialog open={!!selectedJsonView} onOpenChange={() => setSelectedJsonView(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Configuration JSON - {selectedJsonView?.name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh]">
            <pre className="text-xs p-4 bg-muted rounded-lg">
              {selectedJsonView && JSON.stringify(selectedJsonView.configuration, null, 2)}
            </pre>
          </ScrollArea>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSelectedJsonView(null)}>
              Close
            </Button>
            <Button onClick={() => selectedJsonView && downloadJson(selectedJsonView)}>
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clip Preview Modal */}
      <Dialog open={!!previewClip} onOpenChange={() => setPreviewClip(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle>
              Clip Preview - {previewClip?.file.name}
              {previewClip?.clip.inTime !== undefined && previewClip?.clip.outTime !== undefined && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({previewClip.clip.inTime}s - {previewClip.clip.outTime}s)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {previewClip && previewClip.file.s3Url && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {/* Show a note if this is a green screen clip */}
                {previewClip.file.hasGreenScreen && (
                  <div className="bg-yellow-900/20 border border-yellow-700/50 px-3 py-2 mb-2 rounded">
                    <p className="text-xs text-yellow-600">
                      Note: Previewing original video with audio. The actual timeline will use the green screen version.
                    </p>
                  </div>
                )}
                <video
                  ref={(video) => {
                    if (video && previewClip.clip.inTime !== undefined) {
                      video.currentTime = previewClip.clip.inTime;
                      // Set up event listener to loop between in and out points
                      const handleTimeUpdate = () => {
                        if (previewClip.clip.outTime !== undefined && video.currentTime >= previewClip.clip.outTime) {
                          video.currentTime = previewClip.clip.inTime || 0;
                        }
                      };
                      video.addEventListener('timeupdate', handleTimeUpdate);
                      // Clean up on unmount
                      return () => video.removeEventListener('timeupdate', handleTimeUpdate);
                    }
                  }}
                  // Always use original s3Url to ensure audio playback
                  // Green screen versions don't have audio
                  src={resolveVideoUrl(previewClip.file.s3Url, previewClip.file.s3Key) || previewClip.file.s3Url}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              </div>
            )}
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <p><strong>In Time:</strong> {previewClip?.clip.inTime ?? 'Start'}s</p>
              <p><strong>Out Time:</strong> {previewClip?.clip.outTime ?? 'End'}s</p>
              <p><strong>Clip Duration:</strong> {
                previewClip?.clip.inTime !== undefined && previewClip?.clip.outTime !== undefined
                  ? `${(previewClip.clip.outTime - previewClip.clip.inTime).toFixed(1)}s`
                  : 'Full clip'
              }</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Playback Modal */}
      <Dialog open={!!selectedVideoModal} onOpenChange={() => setSelectedVideoModal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle>Video Playback - {selectedVideoModal?.name}</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {selectedVideoModal && selectedVideoModal.url && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  src={selectedVideoModal.url}
                  controls
                  autoPlay
                  className="w-full h-full"
                  poster={selectedVideoModal.thumbnailUrl}
                />
              </div>
            )}
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <p><strong>Duration:</strong> {Math.round(selectedVideoModal?.duration || 0)}s</p>
              <p><strong>Clips:</strong> {selectedVideoModal?.configuration.clips.length || 0}</p>
              <p><strong>Rendered:</strong> {selectedVideoModal ? new Date(selectedVideoModal.timestamp).toLocaleString() : ''}</p>
              <p><strong>Master Prompt:</strong> {selectedVideoModal?.configuration.masterPrompt}</p>
            </div>
            <div className="flex justify-between items-center mt-6">
              <Button variant="outline" onClick={() => setSelectedVideoModal(null)}>
                Close
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => selectedVideoModal && setSelectedJsonView(selectedVideoModal)}>
                  View JSON
                </Button>
                {selectedVideoModal?.url && (
                  <Button onClick={() => selectedVideoModal.url && window.open(selectedVideoModal.url, '_blank')}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Video
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove BG Options Dialog */}
      <Dialog open={showRemoveBgModal} onOpenChange={setShowRemoveBgModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Background</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Choose how to remove the background from your video:
              </p>
              
              {/* Output Type Selection */}
              <div className="mt-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="outputType"
                    value="foreground-mask"
                    checked={selectedOutputType === 'foreground-mask'}
                    onChange={(e) => setSelectedOutputType(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">⚪ Foreground Mask (Recommended)</div>
                    <p className="text-xs text-muted-foreground">
                      Black & white mask for Shotstack compositing. Best edge quality, no color artifacts.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="outputType"
                    value="green-screen"
                    checked={selectedOutputType === 'green-screen'}
                    onChange={(e) => setSelectedOutputType(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">🟢 Green Screen</div>
                    <p className="text-xs text-muted-foreground">
                      Replace background with green for chromaKey removal. Best for standard compositing.
                    </p>
                  </div>
                </label>
              </div>
              
              <p className="text-sm text-muted-foreground mt-4">
                <strong>Processing {pendingRemoveBgFiles.length} video{pendingRemoveBgFiles.length !== 1 ? 's' : ''}</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {(() => {
                  const totalDuration = pendingRemoveBgFiles.reduce((sum, fileId) => {
                    const file = files.find(f => f.id === fileId);
                    // If duration is missing (imported videos), estimate 30 seconds per video
                    return sum + (file?.duration || 30);
                  }, 0);
                  const estimatedMinutes = Math.max(1, Math.ceil(totalDuration / 30)); // Rough estimate: ~30 seconds of video per minute of processing
                  
                  // Check if any files are missing duration info
                  const missingDuration = pendingRemoveBgFiles.some(fileId => {
                    const file = files.find(f => f.id === fileId);
                    return !file?.duration || file.duration === 0;
                  });
                  
                  if (missingDuration) {
                    return `Estimated processing time: ${estimatedMinutes}-${estimatedMinutes * 2} minutes (duration info unavailable for imported videos)`;
                  }
                  return `Total video duration: ${Math.round(totalDuration)}s. Estimated processing time: ${estimatedMinutes}-${estimatedMinutes * 2} minutes`;
                })()}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowRemoveBgModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowRemoveBgModal(false);
                processRemoveBackground(selectedOutputType);
              }}
            >
              Remove Background
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Background Selector Dialog */}
      <Dialog open={showBackgroundSelector} onOpenChange={setShowBackgroundSelector}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Background for Compositing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Alpha mask generated for: <strong>{selectedOriginalFile?.name}</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                Choose a background to composite with your video. Audio will be preserved from the original video.
              </p>
            </div>
            
            <div>
              <Label htmlFor="background-select">Background Options</Label>
              <Select value={selectedBackground} onValueChange={setSelectedBackground}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a background..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&h=1080&fit=crop">
                    Office Space (Modern)
                  </SelectItem>
                  <SelectItem value="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop">
                    Mountain Landscape
                  </SelectItem>
                  <SelectItem value="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&h=1080&fit=crop">
                    Forest Background
                  </SelectItem>
                  <SelectItem value="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&h=1080&fit=crop">
                    Beach Scene
                  </SelectItem>
                  <SelectItem value="https://images.unsplash.com/photo-1514905552197-0610a4d8fd73?w=1920&h=1080&fit=crop">
                    City Skyline
                  </SelectItem>
                  <SelectItem value="#FFFFFF">
                    Solid White
                  </SelectItem>
                  <SelectItem value="#000000">
                    Solid Black
                  </SelectItem>
                  <SelectItem value="#00FF00">
                    Chroma Green
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {selectedBackground && (
              <div className="p-3 border rounded-lg">
                <p className="text-sm font-medium">Preview:</p>
                {selectedBackground.startsWith('#') ? (
                  <div 
                    className="w-full h-24 rounded mt-2" 
                    style={{ backgroundColor: selectedBackground }}
                  />
                ) : (
                  <img 
                    src={selectedBackground} 
                    alt="Selected background" 
                    className="w-full h-24 object-cover rounded mt-2"
                  />
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowBackgroundSelector(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompositeBackground}
              disabled={!selectedBackground}
            >
              Composite Video
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Post Modal */}
      {scheduleVideoData && (
        <SchedulePostModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setScheduleVideoData(null);
          }}
          onSchedule={async (data) => {
            const response = await fetch('/api/scheduled-posts', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(data),
            });
            if (!response.ok) {
              throw new Error('Failed to schedule post');
            }
            toast({
              title: 'Post Scheduled',
              description: 'Your video has been scheduled for posting.',
            });
          }}
          videoUrl={scheduleVideoData.videoUrl}
          thumbnailUrl={scheduleVideoData.thumbnailUrl}
          renderId={scheduleVideoData.renderId}
        />
      )}

      {/* YouTube Publish Modal */}
      <Dialog open={showYoutubePublishModal} onOpenChange={(open) => {
        if (!open) {
          setShowYoutubePublishModal(false);
          setYoutubePublishData(null);
          setYoutubeUploadResult(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-red-600" />
              Publish to YouTube
            </DialogTitle>
          </DialogHeader>

          {youtubeUploadResult ? (
            // Success state
            <div className="space-y-4 text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Upload Complete!</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Your video has been uploaded to YouTube.
                </p>
              </div>
              <Button
                className="w-full"
                onClick={() => window.open(youtubeUploadResult.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View on YouTube
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowYoutubePublishModal(false);
                  setYoutubePublishData(null);
                  setYoutubeUploadResult(null);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            // Upload form
            <div className="space-y-4">
              {/* Video Preview */}
              {youtubePublishData && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <div className="w-24 h-14 bg-black rounded overflow-hidden flex-shrink-0">
                    {youtubePublishData.thumbnailUrl ? (
                      <img
                        src={youtubePublishData.thumbnailUrl}
                        alt="Video thumbnail"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-white/70" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{youtubePublishData.name}</p>
                    <p className="text-xs text-muted-foreground">Ready to upload</p>
                  </div>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="youtube-title">Title *</Label>
                <Input
                  id="youtube-title"
                  value={youtubePublishForm.title}
                  onChange={(e) => setYoutubePublishForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Video title..."
                  maxLength={100}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="youtube-description">Description</Label>
                <Textarea
                  id="youtube-description"
                  value={youtubePublishForm.description}
                  onChange={(e) => setYoutubePublishForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your video..."
                  rows={3}
                />
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label htmlFor="youtube-tags">Tags (comma-separated)</Label>
                <Input
                  id="youtube-tags"
                  value={youtubePublishForm.tags}
                  onChange={(e) => setYoutubePublishForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="casting, video, demo..."
                />
              </div>

              {/* Privacy */}
              <div className="space-y-2">
                <Label htmlFor="youtube-privacy">Privacy</Label>
                <Select
                  value={youtubePublishForm.privacy}
                  onValueChange={(value) => setYoutubePublishForm(prev => ({ ...prev, privacy: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private - Only you can view</SelectItem>
                    <SelectItem value="unlisted">Unlisted - Anyone with link can view</SelectItem>
                    <SelectItem value="public">Public - Everyone can view</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowYoutubePublishModal(false);
                    setYoutubePublishData(null);
                  }}
                  disabled={youtubeUploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={uploadToYoutube}
                  disabled={youtubeUploading || !youtubePublishForm.title.trim()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {youtubeUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Youtube className="w-4 h-4 mr-2" />
                      Upload to YouTube
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credit Confirmation Dialog */}
      <CreditConfirmDialog
        open={creditConfirmOpen}
        onOpenChange={setCreditConfirmOpen}
        operationType={creditConfirmOperation}
        operationName={creditConfirmOperationName}
        onConfirm={() => {
          if (creditConfirmCallback) {
            creditConfirmCallback();
          }
          // Refresh balance after operation
          fetchBalance();
        }}
        onCancel={() => {
          setCreditConfirmCallback(null);
        }}
      />

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        open={showCreditPurchaseModal}
        onOpenChange={setShowCreditPurchaseModal}
      />

      {/* Subscription Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        trigger={upgradeModalTrigger}
      />

    </div>
  );
};
